import {t, useLanguage} from '../i18n';
import RegionalServiceAreasPicker from '../components/RegionalServiceAreasPicker';
import BusinessRegionSettings from "../components/BusinessRegionSettings";
import "./PublicProfileEditor.css";
import {usePendingEditor} from "../hooks/useEditorNavigation";
// src/sections/GeneralSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { loopBusinessNow, loopBusinessUpdateDoc } from "../utils/businessNativeSafeWrites";
import { db as defaultDb } from "../firebase";
import PanamaLocationPickerModal, { readUserLocation, buildLocationLabel } from "../components/PanamaLocationPickerModal";
import PanamaServiceAreasPicker, { normalizeServiceAreasAdvanced } from "../components/PanamaServiceAreasPicker";
import SmartLogoFrame from "../components/SmartLogoFrame";
import whatsappContactIcon from "../assets/contact-icons/whatsapp.svg";
import phoneContactIcon from "../assets/contact-icons/phone.svg";
import instagramContactIcon from "../assets/contact-icons/instagram.svg";
import websiteContactIcon from "../assets/contact-icons/website.svg";
import { PhoneInput } from "../components/PhoneKit";
import { IMAGE_UPLOAD_ACCEPT, prepareImageFileForUpload } from "../utils/imageUploadUtils";
import { generateUniqueBusinessSlug, reserveBusinessSlug, slugifyBusinessName } from "../utils/businessSlug";
import { LOOP_PHONE_COUNTRIES, cleanPhoneNationalInput, formatPhoneDisplay, joinPhoneDisplay, parsePhoneValue } from "../utils/phoneUtils";
import "../styles/loopLayers.css";

const PHONE_COUNTRIES = LOOP_PHONE_COUNTRIES.map((item) => ({
  code: item.dialCode,
  country: item.countryCode,
  label: item.label,
  placeholder: item.placeholder,
}));

const BUSINESS_SUBTYPE_OPTIONS = {
  events: [
    { value: "infantiles_animacion", label: "Infantiles / animación" },
    { value: "fotografia_eventos", label: "Fotografía & eventos" },
  ],
  beauty: [
    { value: "belleza", label: "Belleza general" },
    { value: "barberia", label: "Barbería" },
    { value: "unas", label: "Uñas / manicure" },
    { value: "maquillaje", label: "Maquillaje" },
    { value: "pestanas_cejas", label: "Pestañas y cejas" },
  ],
  wellness: [
    { value: "spa", label: "Spa" },
    { value: "psicologia", label: "Psicología" },
    { value: "masajes", label: "Masajes" },
    { value: "terapias", label: "Terapias" },
  ],
  fitness: [
    { value: "entrenamiento_personal", label: "Entrenamiento personal" },
    { value: "clases_grupales", label: "Clases grupales" },
  ],
  others: [
    { value: "otro", label: "Otro / por definir" },
    { value: "servicios_generales", label: "Servicios generales" },
  ],
};

function normalizeBusinessTypeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getSelectedBusinessTypeMeta(selectedTypeId, businessTypes, businessDoc) {
  const selected = (businessTypes || []).find((t) => String(t.id) === String(selectedTypeId));
  const raw = selected?.name || selected?.label || selected?.id || businessDoc?.type || businessDoc?.category || businessDoc?.businessType || "";
  const normalized = normalizeBusinessTypeText(raw);
  if (normalized.includes("evento") || normalized.includes("event")) return { group: "events", label: "Eventos" };
  if (normalized.includes("belleza") || normalized.includes("barber") || normalized.includes("beauty") || normalized.includes("uñas") || normalized.includes("unas")) return { group: "beauty", label: "Belleza y barberías" };
  if (normalized.includes("bienestar") || normalized.includes("spa") || normalized.includes("wellness") || normalized.includes("salud")) return { group: "wellness", label: "Bienestar y spa" };
  if (normalized.includes("fitness") || normalized.includes("entren") || normalized.includes("gym") || normalized.includes("deporte")) return { group: "fitness", label: "Fitness" };
  return { group: "others", label: "Otros" };
}

function getSubtypeLabel(group) {
  if (group === "events") return "Subtipo de evento";
  if (group === "beauty") return "Subtipo de belleza";
  if (group === "wellness") return "Subtipo de bienestar";
  if (group === "fitness") return "Subtipo de fitness";
  return "Subtipo";
}

function subtypeLabelFromValue(value, group) {
  const found = (BUSINESS_SUBTYPE_OPTIONS[group] || []).find((x) => String(x.value) === String(value));
  return found?.label || value || "Sin subtipo";
}

function splitPhoneValue(value) {
  const parsed = parsePhoneValue(value, { defaultCountry: "PA" });
  return { code: parsed.dialCode || "+507", local: parsed.nationalFormatted || "" };
}

function CountryFlag({ country, className = "" }) {
  return <span className={`gen-country-badge ${className}`.trim()} aria-hidden="true">{String(country || "PA").toUpperCase()}</span>;
}

function PhoneInputWithCountry({ value = "", onChange = () => {}, placeholder = "6000-0000" }) {
  const initial = useMemo(() => splitPhoneValue(value), []);
  const [code, setCode] = useState(initial.code);
  const [local, setLocal] = useState(initial.local);
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    const parsed = splitPhoneValue(value);
    setCode(parsed.code);
    setLocal(parsed.local);
  }, [value]);

  useEffect(() => {
    const cleanLocal = String(local || "").trim();
    onChange(cleanLocal ? joinPhoneDisplay(code, cleanLocal) : "");
  }, [code, local]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  const currentCountry = PHONE_COUNTRIES.find((c) => c.code === code) || PHONE_COUNTRIES[0];

  return (
    <div className="gen-phone-combo gen-phone-combo-pro" ref={pickerRef}>
      <button type="button" className="gen-phone-country-button" onClick={() => setOpen((v) => !v)}>
        <CountryFlag country={currentCountry.country} className="gen-phone-flag" />
        <strong>{currentCountry.country} {currentCountry.code}</strong>
        <span className="gen-phone-chevron" aria-hidden="true">⌄</span>
      </button>
      {open ? (
        <div className="gen-phone-country-menu" role="listbox">
          {PHONE_COUNTRIES.map((c) => (
            <button key={`${c.country}-${c.code}`} type="button" className={c.code === code ? "active" : ""} onClick={() => { setCode(c.code); setOpen(false); }}>
              <CountryFlag country={c.country} className="gen-phone-menu-flag" />
              <div><strong>{c.label}</strong><em>{c.country} {c.code}</em></div>
            </button>
          ))}
        </div>
      ) : null}
      <input className="gen-phone-local" value={local} onChange={(e) => setLocal(cleanPhoneNationalInput(e.target.value, currentCountry.country))} placeholder={currentCountry.placeholder || placeholder} inputMode="tel" autoComplete="tel" />
    </div>
  );
}


function normalizePublicInstagram(value = "") {
  const clean = String(value || "")
    .trim()
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, "")
    .replace(/^instagram\.com\//i, "")
    .replace(/^@+/, "")
    .split(/[/?#]/)[0]
    .replace(/[^a-zA-Z0-9._]/g, "")
    .slice(0, 30);
  return clean;
}

function instagramUrlFromHandle(handle = "") {
  const clean = normalizePublicInstagram(handle);
  return clean ? `https://instagram.com/${clean}` : "";
}

function normalizePublicWebsite(value = "") {
  const raw = String(value || "").trim().replace(/\s+/g, "");
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^www\./i.test(raw)) return `https://${raw}`;
  if (/^[a-z0-9][a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(raw)) return `https://${raw}`;
  return raw;
}

function displayWebsiteValue(value = "") {
  return String(value || "").trim().replace(/^https?:\/\//i, "");
}

function makePhonePatch(prefix, value = "") {
  const parsed = parsePhoneValue(value, { defaultCountry: "PA" });
  const hasValue = Boolean(String(value || "").trim());
  const display = hasValue && parsed.isValid ? parsed.display : "";
  const e164 = hasValue && parsed.isValid ? parsed.e164 : "";
  const base = {
    [`${prefix}`]: prefix === "whatsapp" ? e164 : display,
    [`${prefix}Display`]: display,
    [`${prefix}E164`]: e164,
    [`${prefix}Normalized`]: e164,
    [`${prefix}CountryCode`]: e164 ? parsed.countryCode : "",
    [`${prefix}DialCode`]: e164 ? parsed.dialCode : "",
    [`${prefix}National`]: e164 ? parsed.nationalNumber : "",
  };
  return { parsed, hasValue, display, e164, patch: base };
}

function extractLatLngFromGoogleMapsUrl(raw) {
  const text = String(raw || "").trim();
  if (!text) return null;
  let m = text.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)(?:,|\/|\?)/);
  if (m) return { lat: m[1], lng: m[2] };
  m = text.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (m) return { lat: m[1], lng: m[2] };
  m = text.match(/[?&]ll=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (m) return { lat: m[1], lng: m[2] };
  m = text.match(/[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (m) return { lat: m[1], lng: m[2] };
  return null;
}

function buildGoogleMapsSearchUrl(labelOrLoc) {
  if (!labelOrLoc) return "";
  if (typeof labelOrLoc === "string") {
    const raw = labelOrLoc.trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(raw)}`;
  }
  const lat = labelOrLoc.lat || labelOrLoc.latitude;
  const lng = labelOrLoc.lng || labelOrLoc.longitude;
  if (lat && lng) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  const label = buildLocationLabel(labelOrLoc);
  return label ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}` : "";
}

function extractMapSrc(linkOrAddress, fallbackAddress = "") {
  const raw = String(linkOrAddress || "").trim();
  const address = String(fallbackAddress || "").trim();
  if (raw.includes("<iframe")) {
    const match = raw.match(/src=["']([^"']+)["']/i);
    if (match && match[1]) return match[1];
  }
  if (raw.includes("/embed?")) return raw;
  const coords = extractLatLngFromGoogleMapsUrl(raw);
  if (coords) return `https://www.google.com/maps?q=${coords.lat},${coords.lng}&z=17&output=embed`;
  const query = address || raw;
  return query ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : "";
}

function getPublicReadiness({ businessDoc, logoPreview, name, selectedTypeId }) {
  const hasName = Boolean(String(name || businessDoc?.name || "").trim());
  const hasType = Boolean(selectedTypeId || businessDoc?.type || businessDoc?.businessType || businessDoc?.category);
  const hasLogo = Boolean(logoPreview || businessDoc?.logoUrl || businessDoc?.logo);
  const missing = [];
  if (!hasName) missing.push("nombre del negocio");
  if (!hasType) missing.push("categoría principal");
  if (!hasLogo) missing.push("logo");
  return { ready: missing.length === 0, missing };
}

function Icon({ name }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.25,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  const paths = {
    whatsapp: (
      <>
        <path d="M20.4 11.7a8.3 8.3 0 0 1-12.25 7.3L4 20l1.1-4a8.3 8.3 0 1 1 15.3-4.3Z" />
        <path d="M8.9 8.9c.25-.55.48-.58.82-.58h.6c.2 0 .46.05.7.55.24.5.84 1.95.92 2.1.08.15.12.33.02.53-.1.2-.17.32-.34.5-.17.18-.36.4-.52.54-.17.16-.35.33-.15.66.2.33.88 1.45 1.9 2.35 1.3 1.15 2.4 1.5 2.75 1.66.35.16.56.14.76-.08.2-.22.88-1.02 1.12-1.38.24-.36.47-.3.8-.18.33.12 2.08.98 2.43 1.16.35.18.58.27.67.42.08.15.08.85-.2 1.67" />
      </>
    ),
    phone: <path d="M22 16.9v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.45 19.45 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.63 2.6a2 2 0 0 1-.45 2.11L8 9.72a16 16 0 0 0 6.28 6.28l1.29-1.29a2 2 0 0 1 2.11-.45c.83.3 1.7.51 2.6.63A2 2 0 0 1 22 16.9Z" />,
    instagram: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="3.7" />
        <circle cx="17.2" cy="6.8" r=".75" fill="currentColor" stroke="none" />
      </>
    ),
    web: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3a14 14 0 0 1 0 18" />
        <path d="M12 3a14 14 0 0 0 0 18" />
      </>
    ),
    map: (
      <>
        <path d="M12 21s7-4.7 7-11a7 7 0 0 0-14 0c0 6.3 7 11 7 11Z" />
        <circle cx="12" cy="10" r="2.4" />
      </>
    ),
    edit: (
      <>
        <path d="M4 20h4.5L19.2 9.3a2.1 2.1 0 0 0-3-3L5.5 17 4 20Z" />
        <path d="m14.8 7.7 1.5 1.5" />
      </>
    ),
    copy: (
      <>
        <path d="M10 13.5a3.2 3.2 0 0 0 4.5 0l3-3a3.2 3.2 0 0 0-4.5-4.5l-1.1 1.1" />
        <path d="M14 10.5a3.2 3.2 0 0 0-4.5 0l-3 3a3.2 3.2 0 0 0 4.5 4.5l1.1-1.1" />
      </>
    ),
    share: (
      <>
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <path d="m8.6 10.5 6.8-4" />
        <path d="m8.6 13.5 6.8 4" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
  };

  return <span className={`gen-v3-icon gen-v3-icon-${name}`}><svg {...common}>{paths[name] || paths.web}</svg></span>;
}

function ContactIcon({ type }) {
  const labelMap = {
    whatsapp: "WhatsApp",
    phone: "Teléfono",
    instagram: "Instagram",
    web: "Sitio web",
  };

  const assetMap = {
    whatsapp: whatsappContactIcon,
    phone: phoneContactIcon,
    instagram: instagramContactIcon,
    web: websiteContactIcon,
  };

  return (
    <span className={`gen-contact-icon gen-contact-icon-${type}`} aria-label={labelMap[type] || t("Contacto")}>
      <img src={assetMap[type] || websiteContactIcon} alt="" aria-hidden="true" draggable={false} />
    </span>
  );
}

function EmptyDash() { return <span className="gen-muted-dash">{t("No configurado")}</span>; }

function VisibilitySwitch({ visible, onClick }) {
  return (
    <button type="button" className={`gen-v3-visibility ${visible ? "on" : "off"}`} onClick={onClick}>
      <span className="track"><i /></span>
      <strong>{visible ? t("Visible") : t("Oculta")}</strong>
    </button>
  );
}

function Modal({ title, subtitle, children, onClose, footer, status, compact=false }) {
  const modalNode = (
    <div className={`gen-v3-modal gen-v3-modal-workspace ${compact ? "pp-confirmation" : ""}`} onMouseDown={onClose}>
      <div
        className="gen-v3-sheet gen-v3-sheet-workspace"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="gen-v3-sheet-head">
          <div><h3>{title}</h3>{subtitle ? <p>{subtitle}</p> : null}</div>
          <button type="button" onClick={onClose} aria-label={t("Cerrar")}>×</button>
        </div>
        <div className="gen-v3-sheet-body">{status && <p role="status" className="business-editor-status">{status}</p>}{children}</div>
        {footer ? <div className="gen-v3-sheet-footer">{footer}</div> : null}
      </div>
    </div>
  );

  if (typeof document !== "undefined" && document.body) {
    return createPortal(modalNode, document.body);
  }

  return modalNode;
}

function Field({ label, children, hint }) {
  return <label className="gen-v3-field"><span>{label}</span>{children}{hint ? <em>{hint}</em> : null}</label>;
}


function logoPreviewInitial(name = "") {
  const clean = String(name || "Loop").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
  return (parts[0] || "LP").slice(0, 2).toUpperCase();
}

function LogoBgPreview({ src, name = "", bgColor = "#f7efe6", className = "", rounded = 26 }) {
  const cleanBg = String(bgColor || "#f7efe6").trim() || "#f7efe6";
  return (
    <div
      className={`gen-logo-bg-preview ${className}`}
      style={{ "--gen-logo-bg": cleanBg, backgroundColor: cleanBg, borderRadius: rounded }}
    >
      {src ? (
        <img src={src} alt={name || "Logo"} draggable={false} />
      ) : (
        <span>{logoPreviewInitial(name)}</span>
      )}
    </div>
  );
}

export default function GeneralSection({
  businessDoc = {},
  businessId = null,
  businessTypes = [],
  nameRef,
  addressRef,
  mapsRef,
  phoneRef,
  whatsappRef,
  instagramRef,
  websiteRef,
  hoursRef,
  eventCategoryRef,
  serviceModeRef,
  serviceAreasRef,
  quienesRef,
  logoPreview,
  onDiscardLogo = () => {},
  onOpenGallery,
  openCropper = () => {},
  uploadPct = 0,
  onSaveGeneral = async () => false,
  onTypeChange = () => {},
  visible: visibleProp,
  onVisibilityChange = () => {},
}) {
 useLanguage();
  const initialType = businessDoc?.type || businessDoc?.businessType || businessDoc?.category || "";
  const [selectedTypeId, setSelectedTypeId] = useState(() => {
    const byId = businessTypes.find((t) => String(t.id) === String(initialType));
    if (byId) return String(byId.id);
    const byName = businessTypes.find((t) => String(t.name) === String(initialType));
    return byName ? String(byName.id) : String(initialType || "");
  });
  const [visible, setVisible] = useState(() => {
    if (typeof visibleProp !== "undefined") return !!visibleProp;
    if (typeof businessDoc.visible !== "undefined") return !!businessDoc.visible;
    if (typeof businessDoc.hidden !== "undefined") return !businessDoc.hidden;
    return true;
  });

  const [logoBgColor, setLogoBgColor] = useState(() => businessDoc?.logoBackgroundColor || businessDoc?.brandBackgroundColor || businessDoc?.mediaBackgroundColor || "");
  const [logoUploadStatus, setLogoUploadStatus] = useState("");
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [mapExpanded,setMapExpanded]=useState(false);
  const [previewExpanded,setPreviewExpanded]=useState(false);
  const logoDraftRef=useRef(null);

  const [generalSaving,setGeneralSaving]=useState(false);
  const [visibilityBusy,setVisibilityBusy]=useState(false);
  const [visibilityConfirm,setVisibilityConfirm]=useState(false);
  usePendingEditor({busy:visibilityBusy,title:"Visibilidad del perfil"});
  const [generalStatus,setGeneralStatus]=useState("");
  const modalDraftRef=useRef(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactStatus, setContactStatus] = useState("");
  const [showLocationModal, setShowLocationModal] = useState(false);
  const fileInputRef = useRef(null);

  const [name, setName] = useState(businessDoc?.name || "");
  const [subTypeValue, setSubTypeValue] = useState(businessDoc?.businessSubType || businessDoc?.subType || businessDoc?.serviceSubcategory || businessDoc?.eventCategory || "");
  const [slugValue, setSlugValue] = useState(businessDoc?.slug || businessDoc?.publicSlug || "");
  const [slugStatus, setSlugStatus] = useState("");
  const [phone, setPhone] = useState(businessDoc?.phone || businessDoc?.phoneDisplay || businessDoc?.phoneNumber || businessDoc?.publicPhone || "");
  const [whatsapp, setWhatsapp] = useState(businessDoc?.whatsappDisplay || businessDoc?.whatsapp || businessDoc?.whatsApp || businessDoc?.publicWhatsapp || "");
  const [instagram, setInstagram] = useState(businessDoc?.instagram || businessDoc?.instagramHandle || businessDoc?.publicInstagram || "");
  const [website, setWebsite] = useState(businessDoc?.website || businessDoc?.websiteUrl || businessDoc?.publicWebsite || "");
  const [presentation, setPresentation] = useState(businessDoc?.quienesSomos || businessDoc?.about || businessDoc?.description || "");
  const [address, setAddress] = useState(businessDoc?.address || "");
  const [mapsValue, setMapsValue] = useState(businessDoc?.googleMapsLink || businessDoc?.mapsLink || businessDoc?.googleMapsUrl || businessDoc?.mapUrl || "");
  const [serviceMode, setServiceMode] = useState(businessDoc?.serviceMode || "hybrid");
  const [selectedServiceAreasAdvanced, setSelectedServiceAreasAdvanced] = useState(() => {
    const advanced = Array.isArray(businessDoc?.serviceAreasAdvanced)
      ? businessDoc.serviceAreasAdvanced
      : Array.isArray(businessDoc?.deliveryAreas)
        ? businessDoc.deliveryAreas
        : [];
    if (advanced.length) return normalizeServiceAreasAdvanced(advanced);
    const raw = Array.isArray(businessDoc?.serviceAreas)
      ? businessDoc.serviceAreas
      : String(businessDoc?.serviceArea || businessDoc?.coverageArea || "").split(",");
    return normalizeServiceAreasAdvanced(raw.map((label) => ({ label, extraFee: 0 })));
  });

  const selectedTypeMeta = useMemo(() => getSelectedBusinessTypeMeta(selectedTypeId, businessTypes, businessDoc), [selectedTypeId, businessTypes, businessDoc]);
  const subtypeOptions = BUSINESS_SUBTYPE_OPTIONS[selectedTypeMeta.group] || BUSINESS_SUBTYPE_OPTIONS.others;
  const typeLabel = (businessTypes || []).find((t) => String(t.id) === String(selectedTypeId))?.name || selectedTypeMeta.label || selectedTypeId || "Negocio";
  const subtypeLabel = subtypeLabelFromValue(subTypeValue, selectedTypeMeta.group);
  const showPhysicalLocation = serviceMode === "onsite" || serviceMode === "hybrid";
  const showServiceAreas = serviceMode === "delivery" || serviceMode === "hybrid";
  const currentLogoSource = logoPreview || businessDoc?.logoUrl || businessDoc?.logo || businessDoc?.logoImage || businessDoc?.menuImage || businessDoc?.mainPhotoUrl || "";
  const cleanSlug = slugValue || slugifyBusinessName(name || businessDoc?.name || "tu-negocio");
  const publicUrl = `loopreservas.com/business/${cleanSlug}`;
  const mapSrc = useMemo(() => extractMapSrc(mapsValue, address), [mapsValue, address]);
  const userLastLoc = readUserLocation();
  const resolvedLogoBgColor = useMemo(() => {
    return String(
      logoBgColor ||
      businessDoc?.logoBackgroundColor ||
      businessDoc?.brandBackgroundColor ||
      businessDoc?.mediaBackgroundColor ||
      "#f7efe6"
    ).trim() || "#f7efe6";
  }, [logoBgColor, businessDoc?.logoBackgroundColor, businessDoc?.brandBackgroundColor, businessDoc?.mediaBackgroundColor]);

  const phoneDisplayValue = useMemo(() => formatPhoneDisplay(phone, "PA"), [phone]);
  const whatsappDisplayValue = useMemo(() => formatPhoneDisplay(whatsapp, "PA"), [whatsapp]);
  const instagramPreviewHandle = useMemo(() => normalizePublicInstagram(instagram), [instagram]);
  const websitePreviewUrl = useMemo(() => normalizePublicWebsite(website), [website]);

  // LOOP GENERAL CONTACT PUBLIC POLISH V4
  // Public contact summary actions use configured channel values directly.
  // Missing channels keep the editor as the action; no verification workflow exists here.
  const phonePublicE164 = useMemo(() => makePhonePatch("phone", phone).e164, [phone]);
  const whatsappPublicE164 = useMemo(() => makePhonePatch("whatsapp", whatsapp).e164, [whatsapp]);
  const phonePublicHref = useMemo(() => phonePublicE164 ? `tel:${phonePublicE164}` : "", [phonePublicE164]);
  const whatsappPublicHref = useMemo(() => {
    const digits = String(whatsappPublicE164 || "").replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }, [whatsappPublicE164]);
  const instagramPublicHref = useMemo(() => instagramPreviewHandle ? instagramUrlFromHandle(instagramPreviewHandle) : "", [instagramPreviewHandle]);
  const websitePublicHref = useMemo(() => /^https?:\/\//i.test(websitePreviewUrl || "") ? websitePreviewUrl : "", [websitePreviewUrl]);

  const activatePublicContactChannel = (href = "") => {
    if (!href) {
      setActiveModal("contact");
      return;
    }
    try {
      if (/^tel:/i.test(href)) {
        window.location.href = href;
        return;
      }
      window.open(href, "_blank", "noopener,noreferrer");
    } catch (_) {
      setActiveModal("contact");
    }
  };


  useEffect(() => {
    if (activeModal === "contact") setContactStatus("");
  }, [activeModal]);


  const syncRefs = () => {
    try { if (nameRef?.current) nameRef.current.value = name || ""; } catch (_) {}
    try { if (eventCategoryRef?.current) eventCategoryRef.current.value = subTypeValue || ""; } catch (_) {}
    try { if (phoneRef?.current) phoneRef.current.value = phone || ""; } catch (_) {}
    try { if (whatsappRef?.current) whatsappRef.current.value = whatsapp || ""; } catch (_) {}
    try { if (instagramRef?.current) instagramRef.current.value = instagram || ""; } catch (_) {}
    try { if (websiteRef?.current) websiteRef.current.value = website || ""; } catch (_) {}
    try { if (quienesRef?.current) quienesRef.current.value = presentation || ""; } catch (_) {}
    try { if (addressRef?.current) addressRef.current.value = address || ""; } catch (_) {}
    try { if (mapsRef?.current) mapsRef.current.value = mapsValue || ""; } catch (_) {}
    try { if (serviceModeRef?.current) serviceModeRef.current.value = serviceMode || "hybrid"; } catch (_) {}
    try {
      if (serviceAreasRef?.current) {
        const labels = selectedServiceAreasAdvanced.map((a) => String(a?.label || "").trim()).filter(Boolean);
        serviceAreasRef.current.value = labels.join(", ");
        serviceAreasRef.current.dataset.serviceAreasAdvanced = JSON.stringify(selectedServiceAreasAdvanced);
      }
    } catch (_) {}
    try { if (hoursRef?.current) hoursRef.current.value = businessDoc?.hours || businessDoc?.horarios || businessDoc?.businessHours || ""; } catch (_) {}
  };

  useEffect(() => { syncRefs(); }, [name, subTypeValue, phone, whatsapp, instagram, website, presentation, address, mapsValue, serviceMode, selectedServiceAreasAdvanced, businessDoc?.hours]);

  useEffect(() => {
    if(activeModal) return;
    setName(businessDoc?.name || "");
    setPhone(businessDoc?.phone || businessDoc?.phoneDisplay || businessDoc?.phoneNumber || businessDoc?.publicPhone || "");
    setWhatsapp(businessDoc?.whatsappDisplay || businessDoc?.whatsapp || businessDoc?.whatsApp || businessDoc?.publicWhatsapp || "");
    setInstagram(businessDoc?.instagram || businessDoc?.instagramHandle || businessDoc?.publicInstagram || "");
    setWebsite(businessDoc?.website || businessDoc?.websiteUrl || businessDoc?.publicWebsite || "");
    setPresentation(businessDoc?.quienesSomos || businessDoc?.about || businessDoc?.description || "");
    setAddress(businessDoc?.address || "");
    setMapsValue(businessDoc?.googleMapsLink || businessDoc?.mapsLink || businessDoc?.googleMapsUrl || businessDoc?.mapUrl || "");
    setServiceMode(businessDoc?.serviceMode || "hybrid");
    setSubTypeValue(businessDoc?.businessSubType || businessDoc?.subType || businessDoc?.serviceSubcategory || businessDoc?.eventCategory || "");
    setSlugValue(businessDoc?.slug || businessDoc?.publicSlug || "");
    setLogoBgColor(businessDoc?.logoBackgroundColor || businessDoc?.brandBackgroundColor || businessDoc?.mediaBackgroundColor || "");
    if (typeof visibleProp !== "undefined") setVisible(!!visibleProp);
    else if (typeof businessDoc.visible !== "undefined") setVisible(!!businessDoc.visible);
    else if (typeof businessDoc.hidden !== "undefined") setVisible(!businessDoc.hidden);
  }, [businessDoc, visibleProp, Boolean(activeModal)]);

  useEffect(() => {
    const initial = businessDoc?.type || businessDoc?.businessType || businessDoc?.category || "";
    const byId = businessTypes.find((t) => String(t.id) === String(initial));
    if (byId) setSelectedTypeId(String(byId.id));
    else {
      const byName = businessTypes.find((t) => String(t.name) === String(initial));
      setSelectedTypeId(byName ? String(byName.id) : String(initial || ""));
    }
  }, [businessDoc?.type, businessDoc?.businessType, businessDoc?.category, businessTypes]);

  useEffect(() => {
    const exists = subtypeOptions.some((opt) => String(opt.value) === String(subTypeValue));
    if (!exists && subTypeValue) setSubTypeValue("");
  }, [selectedTypeMeta.group]);

  const getWritableBusinessId = () => {
    if (businessId) return businessId;
    if (businessDoc?.id) return businessDoc.id;
    if (businessDoc?.businessId) return businessDoc.businessId;
    try {
      const path = window.location.pathname || "";
      const patterns = [/\/business\/([^/]+)\/edit/i, /\/businesses\/([^/]+)\/edit/i, /\/business\/([^/]+)\/settings/i, /\/businesses\/([^/]+)\/settings/i];
      for (const pattern of patterns) {
        const match = path.match(pattern);
        if (match && match[1]) return decodeURIComponent(match[1]);
      }
      const params = new URLSearchParams(window.location.search || "");
      return params.get("businessId") || params.get("id") || null;
    } catch (_) { return null; }
  };

  useEffect(()=>{if(activeModal)modalDraftRef.current={slugValue,selectedTypeId,subTypeValue,name,phone,whatsapp,instagram,website,presentation,address,mapsValue,serviceMode,selectedServiceAreasAdvanced};},[Boolean(activeModal)]);
  const closeGeneralModal=()=>{if(generalSaving || contactSaving)return;const old=modalDraftRef.current;if(old){setSlugValue(old.slugValue);setSelectedTypeId(old.selectedTypeId);setSubTypeValue(old.subTypeValue);setName(old.name);setPhone(old.phone);setWhatsapp(old.whatsapp);setInstagram(old.instagram);setWebsite(old.website);setPresentation(old.presentation);setAddress(old.address);setMapsValue(old.mapsValue);setServiceMode(old.serviceMode);setSelectedServiceAreasAdvanced(old.selectedServiceAreasAdvanced);}setActiveModal(null);};
  usePendingEditor({pending:Boolean(activeModal || showLogoModal),busy:generalSaving||contactSaving,title:"Perfil público",discard:()=>showLogoModal ? closeLogoEditor() : closeGeneralModal()});
  const saveGeneralModal = async () => {
    if (generalSaving) return;
    const phoneInfo=makePhonePatch('phone',phone), whatsappInfo=makePhonePatch('whatsapp',whatsapp);
    if ((String(phone||'').trim() && !phoneInfo.parsed.isValid) || (String(whatsapp||'').trim() && !whatsappInfo.parsed.isValid)) {
      setGeneralStatus('Revisa el teléfono y WhatsApp antes de guardar.');setActiveModal('contact');return;
    }
    if (!String(name||'').trim()) {setGeneralStatus('Escribe el nombre del negocio.');setActiveModal('identity');return;}
    const cleanInstagram=normalizePublicInstagram(instagram), cleanInstagramUrl=instagramUrlFromHandle(cleanInstagram), cleanWebsite=normalizePublicWebsite(website);
      const publicContactPatch = {
        ...phoneInfo.patch,
        contactPhone: phoneInfo.display,
        publicPhone: phoneInfo.display,
        ...whatsappInfo.patch,
        whatsApp: whatsappInfo.e164,
        whatsappPhone: whatsappInfo.e164,
        publicWhatsapp: whatsappInfo.e164,
        publicWhatsappDisplay: whatsappInfo.display,
        instagram: cleanInstagram,
        instagramHandle: cleanInstagram,
        publicInstagram: cleanInstagram,
        instagramUrl: cleanInstagramUrl,
        publicInstagramUrl: cleanInstagramUrl,
        website: cleanWebsite,
        websiteUrl: cleanWebsite,
        publicWebsite: cleanWebsite,
        publicWebsiteUrl: cleanWebsite,
        "socialLinks.instagram": cleanInstagramUrl,
        "socialLinks.website": cleanWebsite,
        "publicContact.phone": phoneInfo.display,
        "publicContact.phoneE164": phoneInfo.e164,
        "publicContact.whatsapp": whatsappInfo.e164,
        "publicContact.whatsappDisplay": whatsappInfo.display,
        "publicContact.instagram": cleanInstagram,
        "publicContact.instagramUrl": cleanInstagramUrl,
        "publicContact.website": cleanWebsite,
        "contact.phone": phoneInfo.display,
        "contact.phoneE164": phoneInfo.e164,
        "contact.whatsapp": whatsappInfo.e164,
        "contact.whatsappDisplay": whatsappInfo.display,
        "contact.instagram": cleanInstagram,
        "contact.instagramUrl": cleanInstagramUrl,
        "contact.website": cleanWebsite,
      };
    setGeneralSaving(true);setGeneralStatus('');
    try {
      syncRefs();
      const saved=await onSaveGeneral({type:typeLabel,publicContactPatch});
      if(saved===false || saved?.ok===false){setGeneralStatus('No se pudo guardar. Tus cambios siguen aquí.');return;}
      if(slugValue !== (businessDoc?.slug || businessDoc?.publicSlug || '')) {
        const slugSaved=await savePublicSlugNow();
        if(!slugSaved){setGeneralStatus('Los datos del perfil se guardaron, pero falta actualizar el enlace. Revisa Identidad e intenta nuevamente.');setActiveModal('identity');return;}
      }
      setActiveModal(null);setGeneralStatus('Cambios guardados.');
    } catch (_) {setGeneralStatus('No se pudo guardar. Tus cambios siguen aquí.');}
    finally {setGeneralSaving(false);}
  };

  const persistSimplePatch = async (patch) => {
    const writableBusinessId = getWritableBusinessId();
    if (!writableBusinessId) return false;
    try { await loopBusinessUpdateDoc(defaultDb, `businesses/${writableBusinessId}`, { ...patch, updatedAt: loopBusinessNow() }); return true; } catch (err) { console.error("[GeneralSection] patch failed", err); setGeneralStatus("No se pudo guardar el cambio. Intenta nuevamente."); return false; }
  };

  const toggleVisibility = async () => {
    if(visibilityBusy)return;
    const next = !visible;
    if (next) {
      const readiness = getPublicReadiness({ businessDoc, logoPreview, name, selectedTypeId });
      if (!readiness.ready) {
        setGeneralStatus(`Para publicar, completa: ${readiness.missing.join(", ")}.`);
        return;
      }
    }
    setVisibilityBusy(true);setGeneralStatus("");
    try { const result=await onVisibilityChange(next);if(result===false)throw new Error("visibility_not_saved");setVisible(next);setVisibilityConfirm(false);setGeneralStatus(next?"Perfil publicado.":"Perfil oculto."); } catch (e) { console.error(e);setGeneralStatus("No se pudo cambiar la visibilidad. Intenta nuevamente."); } finally {setVisibilityBusy(false);}
  };

  const handleFileChange = async (e) => {
    const f = e.target?.files?.[0];
    try { if (e?.target) e.target.value = null; } catch (_) {}
    if (!f) return;
    const prepared = await prepareImageFileForUpload(f, { purpose: "logo", namePrefix: "loop-logo", maxMb: 18, setStatus: setLogoUploadStatus });
    if (!prepared.ok) {
      alert(prepared.message || "No pudimos cargar ese logo. Usa JPG, PNG, WEBP o HEIC/HEIF.");
      return;
    }
    setLogoUploadStatus(prepared.converted ? "HEIC convertido a JPG. Abriendo recorte..." : "Abriendo recorte...");
    try {
      openCropper(prepared.file, "logo", { aspect: 16 / 9, cropperZIndex: 90000, outputWidth: 1440, fitMode: "contain", backgroundColor: resolvedLogoBgColor, sourceFormat: prepared.converted ? "heic-converted-jpeg" : "standard-image" });
    } finally {
      setTimeout(() => setLogoUploadStatus(""), 900);
    }
  };

  const adjustCurrentLogo = async () => {
    if (!currentLogoSource) return;
    try {
      const response = await fetch(currentLogoSource, { mode: "cors" });
      if (!response.ok) throw new Error(`Could not fetch logo: ${response.status}`);
      const blob = await response.blob();
      const ext = blob.type && blob.type.includes("png") ? "png" : blob.type && blob.type.includes("webp") ? "webp" : "jpg";
      const file = new File([blob], `loop-logo-current.${ext}`, { type: blob.type || "image/jpeg" });
      openCropper(file, "logo", { aspect: 16 / 9, cropperZIndex: 90000, outputWidth: 1440, fitMode: "contain", backgroundColor: resolvedLogoBgColor });
    } catch (err) {
      console.warn("[GeneralSection] Could not reframe current logo", err);
      fileInputRef.current?.click();
    }
  };

  useEffect(()=>{if(showLogoModal)logoDraftRef.current=logoBgColor;},[showLogoModal]);
  const closeLogoEditor=()=>{if(generalSaving)return;setLogoBgColor(logoDraftRef.current || "");onDiscardLogo();setShowLogoModal(false);};
  const saveLogoEditor=async()=>{if(generalSaving)return;setGeneralSaving(true);try{const saved=await onSaveGeneral({logoBackgroundColor:resolvedLogoBgColor});if(saved===false||saved?.ok===false){setGeneralStatus("No se pudo guardar el logo. Intenta nuevamente.");return;}setShowLogoModal(false);setGeneralStatus("Logo guardado.");}catch(_){setGeneralStatus("No se pudo guardar el logo. Intenta nuevamente.");}finally{setGeneralSaving(false);}};
  const saveLogoBackgroundColor = async (nextColor) => {
    const clean = String(nextColor || "#f7efe6").trim();
    setLogoBgColor(clean);

  };

  const suggestSlugFromName = async () => {
    const writableBusinessId = getWritableBusinessId();
    const sourceName = String(name || businessDoc?.name || "negocio").trim();
    try {
      setSlugStatus("Generando URL...");
      const next = await generateUniqueBusinessSlug(defaultDb, sourceName, writableBusinessId);
      setSlugValue(next);
      setSlugStatus("URL sugerida lista. Presiona guardar para aplicarla.");
    } catch (_) {
      const next = slugifyBusinessName(sourceName);
      setSlugValue(next);
      setSlugStatus("URL sugerida lista. Presiona guardar para aplicarla.");
    }
  };

  const savePublicSlugNow = async () => {
    const writableBusinessId = getWritableBusinessId();
    if (!writableBusinessId) { setSlugStatus("Guarda el negocio antes de editar la URL pública."); return; }
    const clean = slugifyBusinessName(slugValue || name || businessDoc?.name || "negocio");
    setSlugValue(clean);
    setSlugStatus("Validando disponibilidad...");
    try {
      const savedSlug = await reserveBusinessSlug(defaultDb, clean, writableBusinessId, { businessName: name || businessDoc?.name || clean });
      setSlugValue(savedSlug);
      setSlugStatus(`URL guardada: /business/${savedSlug}`);
      return true;
    } catch (err) {
      console.error("[GeneralSection] Error saving public slug", err);
      const code = String(err?.code || "");
      if (code.includes("already-exists")) setSlugStatus("Esa URL ya está en uso. Prueba otra variación.");
      else if (code.includes("permission-denied")) setSlugStatus("No tienes permisos para cambiar esta URL pública.");
      else setSlugStatus("No se pudo guardar la URL pública.");
      return false;
    }
  };

  const persistServiceModeNow = async (nextMode) => {
    setServiceMode(nextMode);

  };

  const handleServiceAreasAdvancedChange = async (nextAreas) => {
    const normalized = businessDoc.countryCode && businessDoc.countryCode !== "PA" ? nextAreas.map(area=>({...area,country:businessDoc.countryCode})) : normalizeServiceAreasAdvanced(nextAreas);
    setSelectedServiceAreasAdvanced(normalized);
    const labels = normalized.map((area) => String(area?.label || "").trim()).filter(Boolean);

  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(`https://${publicUrl}`); } catch (_) {}
  };

  const shareUrl = async () => {
    const url = `https://${publicUrl}`;
    try {
      if (navigator.share) await navigator.share({ title: name || "Loop", url });
      else await navigator.clipboard.writeText(url);
    } catch (_) {}
  };


  const savePublicContactNow = async () => {
    setContactStatus("");

    const phoneInfo = makePhonePatch("phone", phone);
    const whatsappInfo = makePhonePatch("whatsapp", whatsapp);
    const hasPhone = Boolean(String(phone || "").trim());
    const hasWhatsapp = Boolean(String(whatsapp || "").trim());

    if (hasPhone && !phoneInfo.parsed.isValid) {
      setContactStatus("Revisa el teléfono. El número no coincide con el país seleccionado.");
      return;
    }
    if (hasWhatsapp && !whatsappInfo.parsed.isValid) {
      setContactStatus("Revisa el WhatsApp. El número no coincide con el país seleccionado.");
      return;
    }

    const cleanInstagram = normalizePublicInstagram(instagram);
    const cleanInstagramUrl = instagramUrlFromHandle(cleanInstagram);
    const cleanWebsite = normalizePublicWebsite(website);

    setContactSaving(true);
    try {
      const patch = {
        ...phoneInfo.patch,
        contactPhone: phoneInfo.display,
        publicPhone: phoneInfo.display,
        ...whatsappInfo.patch,
        whatsApp: whatsappInfo.e164,
        whatsappPhone: whatsappInfo.e164,
        publicWhatsapp: whatsappInfo.e164,
        publicWhatsappDisplay: whatsappInfo.display,
        instagram: cleanInstagram,
        instagramHandle: cleanInstagram,
        publicInstagram: cleanInstagram,
        instagramUrl: cleanInstagramUrl,
        publicInstagramUrl: cleanInstagramUrl,
        website: cleanWebsite,
        websiteUrl: cleanWebsite,
        publicWebsite: cleanWebsite,
        publicWebsiteUrl: cleanWebsite,
        "socialLinks.instagram": cleanInstagramUrl,
        "socialLinks.website": cleanWebsite,
        "publicContact.phone": phoneInfo.display,
        "publicContact.phoneE164": phoneInfo.e164,
        "publicContact.whatsapp": whatsappInfo.e164,
        "publicContact.whatsappDisplay": whatsappInfo.display,
        "publicContact.instagram": cleanInstagram,
        "publicContact.instagramUrl": cleanInstagramUrl,
        "publicContact.website": cleanWebsite,
        "contact.phone": phoneInfo.display,
        "contact.phoneE164": phoneInfo.e164,
        "contact.whatsapp": whatsappInfo.e164,
        "contact.whatsappDisplay": whatsappInfo.display,
        "contact.instagram": cleanInstagram,
        "contact.instagramUrl": cleanInstagramUrl,
        "contact.website": cleanWebsite,
      };

      if(!(await persistSimplePatch(patch))) throw new Error("contact_save_failed");
      setPhone(phoneInfo.display);
      setWhatsapp(whatsappInfo.display);
      setInstagram(cleanInstagram);
      setWebsite(cleanWebsite);
      try {
        window.__LOOP_GENERAL_CONTACT_LAST_SAVE__ = {
          businessId: getWritableBusinessId(),
          phone: phoneInfo.display,
          phoneE164: phoneInfo.e164,
          whatsapp: whatsappInfo.e164,
          whatsappDisplay: whatsappInfo.display,
          instagram: cleanInstagram,
          instagramUrl: cleanInstagramUrl,
          website: cleanWebsite,
          savedAt: new Date().toISOString(),
        };
      } catch (_) {}
      setContactStatus("Contacto guardado.");
      window.setTimeout(() => setActiveModal(null), 360);
    } catch (err) {
      console.error("[GeneralSection] contact save failed", err);
      setContactStatus("No se pudo guardar el contacto. Intenta nuevamente.");
    } finally {
      setContactSaving(false);
    }
  };

  return (
    <div className="gen-page gen-v3-page">
      {generalStatus && <p className="business-editor-status" role="status">{generalStatus}</p>}
      <style>{styles}</style>
      <BusinessRegionSettings key={businessId} business={businessDoc} businessId={businessId} />
      <input ref={nameRef} type="hidden" value={name || ""} readOnly />
      <input ref={eventCategoryRef} type="hidden" value={subTypeValue || ""} readOnly />
      <input ref={phoneRef} type="hidden" value={phone || ""} readOnly />
      <input ref={whatsappRef} type="hidden" value={whatsapp || ""} readOnly />
      <input ref={instagramRef} type="hidden" value={instagram || ""} readOnly />
      <input ref={websiteRef} type="hidden" value={website || ""} readOnly />
      <input ref={quienesRef} type="hidden" value={presentation || ""} readOnly />
      <input ref={addressRef} type="hidden" value={address || ""} readOnly />
      <input ref={mapsRef} type="hidden" value={mapsValue || ""} readOnly />
      <input ref={serviceModeRef} type="hidden" value={serviceMode || "hybrid"} readOnly />
      <input ref={serviceAreasRef} type="hidden" value={selectedServiceAreasAdvanced.map((a) => a.label).filter(Boolean).join(", ")} data-service-areas-advanced={JSON.stringify(selectedServiceAreasAdvanced)} readOnly />
      <input ref={hoursRef} type="hidden" value={businessDoc?.hours || businessDoc?.horarios || businessDoc?.businessHours || ""} readOnly />
      <input ref={fileInputRef} type="file" accept={IMAGE_UPLOAD_ACCEPT} hidden onChange={handleFileChange} />

      <div className="gen-v3-head">
        <div>
          <h1>General</h1>
          <p>{t("Gestiona la información principal de tu negocio y cómo te ven tus clientes en Loop.")}</p>
        </div>
      </div>

      <div className="pp-editor">
        <header className="pp-identity">
          <button type="button" className="pp-logo" onClick={()=>setShowLogoModal(true)} aria-label={t("Editar logo")}>
            {currentLogoSource ? <img src={currentLogoSource} alt=""/> : <span>{logoPreviewInitial(name)}</span>}<span className="pp-logo-edit"><Icon name="edit"/></span>
          </button>
          <div className="pp-identity-copy"><h2>{name || t("Nombre del negocio")}</h2><p>{[t(typeLabel),subTypeValue ? t(subtypeLabel) : ""].filter(Boolean).join(" · ")}</p><button type="button" className="pp-link" onClick={()=>setActiveModal("identity")}>{t("Editar perfil") + " "}<Icon name="edit"/></button></div>
          <button type="button" className="pp-button" onClick={()=>window.open(`/business/${cleanSlug}`,"_blank","noopener,noreferrer")}>{t("Vista previa")}<Icon name="eye"/></button>
        </header>
        <section className="pp-visibility" aria-label={t("Visibilidad del perfil")}>
          <div><span className={`pp-dot ${visible?"is-public":""}`}/><div><h3>{t("Visibilidad del perfil") + " "}<span>{visible ? t("Público") : t("Oculto")}</span></h3><p>{visible ? t("Tus clientes pueden encontrar tu perfil y consultar disponibilidad.") : t("Tu perfil no está disponible para nuevas reservas públicas.")}</p></div></div>
          <button type="button" className="pp-button" disabled={visibilityBusy} onClick={()=>{setGeneralStatus("");setVisibilityConfirm(true);}}>{visibilityBusy ? t("Guardando…") : visible ? "Ocultar perfil" : "Publicar perfil"}</button>
        </section>
        <div className="pp-booking-link"><Icon name="web"/><span title={publicUrl}>{publicUrl}</span><button type="button" className="pp-link" onClick={copyUrl}>{t("Copiar enlace")}</button><button type="button" className="pp-link" onClick={shareUrl}>{t("Compartir")}</button></div>
        <section className="pp-panel pp-description"><header><div><h3>{t("Presentación")}</h3><p>{t("Qué haces y qué te distingue.")}</p></div><button type="button" className="pp-link" onClick={()=>setActiveModal("presentation")}>{t("Editar presentación")}</button></header><p className="pp-description-text">{presentation || "Cuéntales a tus clientes qué pueden encontrar en tu negocio."}</p>{onOpenGallery && <button type="button" className="pp-link" onClick={onOpenGallery}>{t("Gestionar fotos del negocio →")}</button>}</section>
        <div className="pp-details">
          <section className="pp-panel"><header><div><h3>{t("Contacto público")}</h3><p>{t("Elige cómo pueden contactarte.")}</p></div></header><div className="pp-contact-list">
            {[
              {key:"whatsapp",label:"WhatsApp",value:whatsappDisplayValue},
              {key:"phone",label:"Teléfono",value:phoneDisplayValue},
              {key:"instagram",label:"Instagram",value:instagramPreviewHandle ? "@"+instagramPreviewHandle : ""},
              {key:"web",label:"Sitio web",value:websitePreviewUrl ? displayWebsiteValue(websitePreviewUrl) : ""},
            ].map(channel=><button key={channel.key} type="button" className="pp-contact-row" onClick={()=>{setContactStatus("");setActiveModal("contact");}} aria-label={t(channel.value?"Editar {label}":"Agregar {label}",{label:t(channel.label)})}><ContactIcon type={channel.key}/><span><strong>{t(channel.label)}</strong><span>{channel.value || t("Sin agregar")}</span></span><em>{channel.value?t("Editar"):t("Agregar")}</em></button>)}
          </div></section>
          <section className="pp-panel"><header><div><h3>{t("Ubicación y atención")}</h3><p>{serviceMode==="delivery" ? t("Atiendes a domicilio.") : serviceMode==="hybrid" ? t("Atiendes en tu local y a domicilio.") : t("Tus clientes visitan tu local.")}</p></div><button type="button" className="pp-link" onClick={()=>setActiveModal("location")}>{t("Editar ubicación")}</button></header>
          <p className="pp-address">{Array.from(new Map(String(address||"").split(",").map(x=>x.trim()).filter(Boolean).map(x=>[x.toLocaleLowerCase("es"),x])).values()).join(", ") || "Agrega la ubicación de tu negocio"}</p>
          {showPhysicalLocation && mapSrc ? <details className="pp-map" onToggle={event=>setMapExpanded(event.currentTarget.open)}><summary>{t("Ver mapa")}</summary>{mapExpanded && <iframe title={t("Mapa del negocio")} src={mapSrc} loading="lazy" referrerPolicy="no-referrer-when-downgrade"/>}</details> : null}
          {(mapsValue||address) && <button type="button" className="pp-link" onClick={()=>window.open(buildGoogleMapsSearchUrl(mapsValue||address),"_blank","noopener,noreferrer")}>{t("Abrir en Google Maps →")}</button>}
          {showServiceAreas && <p className="pp-coverage">{selectedServiceAreasAdvanced.length ? "Cobertura: "+selectedServiceAreasAdvanced.map(a=>a.label).filter(Boolean).join(" · ") : "Agrega las zonas donde atiendes a domicilio."}</p>}
          </section>
        </div>
      </div>
      {visibilityConfirm && <Modal compact title={visible?"¿Ocultar tu perfil?":"Publicar perfil"} subtitle={visible?"El negocio dejará de estar disponible para nuevas reservas desde su perfil público. Esta acción no cancela las reservas existentes.":"Tus clientes podrán encontrar el negocio y consultar su disponibilidad pública."} status={generalStatus} onClose={()=>{if(!visibilityBusy)setVisibilityConfirm(false);}} footer={<><button type="button" className="gen-v3-secondary-btn" disabled={visibilityBusy} onClick={()=>setVisibilityConfirm(false)}>{t("Cancelar")}</button><button type="button" className="gen-v3-primary-btn" disabled={visibilityBusy} onClick={toggleVisibility}>{visibilityBusy?t("Guardando…"):visible?"Ocultar perfil":"Publicar perfil"}</button></>}/>}

      {uploadPct > 0 ? <div className="gen-progress"><span style={{ width: `${uploadPct}%` }} /></div> : null}
      {logoUploadStatus ? <div className="gen-logo-upload-status">{logoUploadStatus}</div> : null}









{activeModal ? <Modal title={t("Editar perfil")} subtitle="Revisa cómo se verá tu negocio antes de guardar." status={generalStatus || contactStatus} onClose={closeGeneralModal} footer={<><span className="pp-save-hint">{t("Los cambios se aplican al guardar")}</span><button type="button" className="gen-v3-secondary-btn" onClick={closeGeneralModal} disabled={generalSaving}>{t("Cancelar")}</button><button type="button" className="gen-v3-primary-btn" onClick={saveGeneralModal} disabled={generalSaving}>{generalSaving ? t("Guardando…") : t("Guardar cambios")}</button></>}>
<div className="pp-unified-editor"><div className="pp-edit-main"><nav className="pp-edit-tabs" aria-label={t("Secciones del perfil")}>{[['identity','Identidad'],['presentation','Presentación'],['location','Ubicación y contacto']].map(([key,label])=><button key={key} type="button" aria-pressed={activeModal===key || (key==='location' && activeModal==='contact')} onClick={()=>setActiveModal(key)}>{label}</button>)}</nav>
<section hidden={activeModal!=='identity'}>
          <div className="gen-identity-studio gen-identity-studio-simple">
            <section className="gen-identity-form-card">
              <div className="gen-identity-section-head">
                <div>
                  <span>{t("Información")}</span>
                  <h4>{t("Datos principales")}</h4>
                </div>

              </div>

              <div className="gen-identity-fields">
                <Field label={t("Nombre del negocio")}>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Nombre de la empresa")} />
                </Field>

                <div className="gen-identity-two-fields">
                  <Field label={t("Tipo principal")}>
                    <select value={selectedTypeId} onChange={(e) => { setSelectedTypeId(e.target.value); setSubTypeValue(""); }}>
                      <option value="">{t("Otros")}</option>
                      {businessTypes.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                    </select>
                  </Field>

                  <Field label={getSubtypeLabel(selectedTypeMeta.group)}>
                    <select value={subTypeValue} onChange={(e) => setSubTypeValue(e.target.value)}>
                      <option value="">{t("Selecciona un subtipo")}</option>
                      {subtypeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              <div className="gen-identity-url-card">
                <div className="gen-identity-section-head compact">
                  <div>
                    <span>{t("URL pública")}</span>
                    <h4>{t("Link de reservas")}</h4>
                  </div>
                  <button type="button" onClick={copyUrl}>{t("Copiar")}</button>
                </div>

                <div className="gen-identity-url-preview">
                  <span>loopreservas.com/business/</span>
                  <strong>{cleanSlug}</strong>
                </div>

                <div className="gen-identity-url-actions">
                  <input value={slugValue} onChange={(e) => setSlugValue(slugifyBusinessName(e.target.value))} placeholder={t("tu-negocio")} />
                  <button type="button" onClick={suggestSlugFromName}>{t("Sugerir")}</button>
                  <small>{t("El enlace se actualizará al guardar los cambios.")}</small>
                </div>

                {slugStatus ? <p className="gen-identity-url-status">{slugStatus}</p> : null}
              </div>
            </section>
          </div>
        </section>
<section hidden={activeModal!=='presentation'}>
          <Field label={t("Quiénes somos")}><textarea value={presentation} onChange={(e) => setPresentation(e.target.value)} rows={7} placeholder={t("Ejemplo: Somos un spa especializado en brindar experiencias de bienestar y relajación.")} /></Field>
        </section>
<section hidden={!['location','contact'].includes(activeModal)}>
          <div className="gen-v3-mode-grid">
            {[{ value: "onsite", icon: "📍", title: "Local físico", desc: "Clientes visitan tu ubicación" }, { value: "delivery", icon: "🚗", title: "A domicilio", desc: "Tú visitas al cliente" }, { value: "hybrid", icon: "✨", title: "Híbrido", desc: "Local y domicilio" }].map((mode) => <button key={mode.value} type="button" className={`gen-v3-mode-card ${serviceMode === mode.value ? "active" : ""}`} onClick={() => persistServiceModeNow(mode.value)}><span>{mode.icon}</span><strong>{mode.title}</strong><em>{mode.desc}</em></button>)}
          </div>
          {showPhysicalLocation && businessDoc.countryCode && businessDoc.countryCode !== "PA" ? <Field label={t("Dirección del local")}><input value={address} onChange={e=>setAddress(e.target.value)} placeholder={t("Ciudad, calle y referencia del local")} /></Field> : showPhysicalLocation ? <div className="gen-v3-location-picker-row"><div><strong>{address || "Selecciona ubicación"}</strong><span>{address ? "Dirección pública del negocio" : "Usa el botón para elegir una ubicación"}</span></div><button type="button" className="gen-v3-secondary-btn" onClick={() => setShowLocationModal(true)}>{t("Cambiar ubicación")}</button></div> : null}
          {showPhysicalLocation ? <Field label={t("Link de Google Maps")} hint="Este link aparecerá como botón en el perfil público."><input value={mapsValue} onChange={(e) => setMapsValue(e.target.value)} placeholder={t("Pega aquí el link de Google Maps")} /></Field> : null}
          {showServiceAreas ? <div className="gen-v3-service-areas">{businessDoc.countryCode && businessDoc.countryCode !== "PA" ? <RegionalServiceAreasPicker value={selectedServiceAreasAdvanced} onChange={handleServiceAreasAdvancedChange} countryCode={businessDoc.countryCode}/> : <PanamaServiceAreasPicker value={selectedServiceAreasAdvanced} onChange={handleServiceAreasAdvancedChange} title={t("Zonas donde atiendes")} subtitle="Elige cobertura por provincia, distrito, corregimiento o zona." />}</div> : null}
        <h4 className="pp-contact-heading">{t("Canales de contacto")}</h4>
          <div className="gen-contact-studio gen-contact-studio-v5">
            <section className="gen-contact-editor-group gen-contact-editor-group-direct">
              <div className="gen-contact-editor-group-head">
                <span className="gen-contact-editor-group-icon"><Icon name="phone" /></span>
                <div>
                  <strong>{t("Contacto directo")}</strong>
                  <small>{t("Define cómo pueden llamarte o escribirte por WhatsApp.")}</small>
                </div>
              </div>

              <div className="gen-contact-phone-grid">
                <PhoneInput
                  label={t("Teléfono")}
                  value={phone}
                  onChange={(nextValue) => setPhone(nextValue)}
                  defaultCountry={businessDoc.countryCode || "PA"}
                  className="gen-contact-phone-kit"
                />
                <PhoneInput
                  label="WhatsApp"
                  value={whatsapp}
                  onChange={(nextValue) => setWhatsapp(nextValue)}
                  defaultCountry={businessDoc.countryCode || "PA"}
                  className="gen-contact-phone-kit"
                />
              </div>
            </section>

            <section className="gen-contact-editor-group gen-contact-editor-group-digital">
              <div className="gen-contact-editor-group-head">
                <span className="gen-contact-editor-group-icon"><Icon name="web" /></span>
                <div>
                  <strong>{t("Presencia digital")}</strong>
                  <small>{t("Agrega tus canales públicos para que tus clientes puedan encontrarte.")}</small>
                </div>
              </div>

              <div className="gen-contact-link-grid">
                <label className="gen-contact-pro-field">
                  <span>Instagram</span>
                  <div className="gen-contact-prefixed-input">
                    <b>@</b>
                    <input
                      value={instagram}
                      onChange={(e) => setInstagram(e.target.value)}
                      onBlur={() => setInstagram(normalizePublicInstagram(instagram))}
                      placeholder={t("tuempresa")}
                      autoComplete="off"
                    />
                  </div>
                </label>

                <label className="gen-contact-pro-field">
                  <span>{t("Sitio web")}</span>
                  <div className="gen-contact-prefixed-input is-web">
                    <b>↗</b>
                    <input
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      onBlur={() => setWebsite(normalizePublicWebsite(website))}
                      placeholder={t("https://tusitio.com")}
                      autoComplete="url"
                    />
                  </div>
                </label>
              </div>
            </section>

            {contactStatus ? <div className={`gen-contact-status ${contactStatus.includes("guardado") ? "is-ok" : "is-error"}`}>{contactStatus}</div> : null}
          </div>
        </section>
</div><button type="button" className="pp-preview-toggle" aria-expanded={previewExpanded} onClick={()=>setPreviewExpanded(open=>!open)}>{previewExpanded ? "Ocultar vista previa" : "Ver vista previa"}</button><aside className={`pp-live-preview ${previewExpanded ? "is-open" : ""}`}><span>{t("Vista previa")}</span><div className="pp-preview-card">{currentLogoSource ? <img src={currentLogoSource} alt={t("Logo del negocio")}/> : <div className="pp-preview-initial">{logoPreviewInitial(name)}</div>}<div><h3>{name || t("Tu negocio")}</h3><p>{[t(typeLabel),subTypeValue ? t(subtypeLabel) : ""].filter(Boolean).join(' · ')}</p><p>{address || 'Ubicación por completar'}</p></div></div><p className="pp-preview-description">{presentation || 'La presentación de tu negocio aparecerá aquí.'}</p><small>{t("Previsualización de tus cambios. La publicación se administra por separado.")}</small></aside></div></Modal> : null}
      {showLogoModal ? (
        <Modal status={generalStatus}
          title={t("Logo del negocio")}
          subtitle="Sube, ajusta y previsualiza cómo se verá tu marca en Loop."
          onClose={closeLogoEditor}
          footer={
            <>
              <button type="button" className="gen-v3-secondary-btn gen-logo-studio-footer-secondary" onClick={closeLogoEditor} disabled={generalSaving}>{t("Cancelar")}</button>
              <button type="button" className="gen-v3-primary-btn" onClick={saveLogoEditor} disabled={generalSaving}>{generalSaving ? t("Guardando…") : t("Guardar cambios")}</button>
            </>
          }
        >
          <div className="gen-logo-studio">
            <section
              className="gen-logo-studio-preview-panel"
              style={{ "--gen-logo-bg": resolvedLogoBgColor, "--smart-logo-bg": resolvedLogoBgColor, backgroundColor: resolvedLogoBgColor }}
            >
              <div className="gen-logo-studio-preview-top">
                <span>{t("Vista principal")}</span>
                <strong>{currentLogoSource ? "Logo cargado" : "Sin logo"}</strong>
              </div>

              <button
                type="button"
                className="gen-logo-studio-main-preview"
                style={{ "--gen-logo-bg": resolvedLogoBgColor, "--smart-logo-bg": resolvedLogoBgColor, backgroundColor: resolvedLogoBgColor }}
                onClick={() => currentLogoSource ? adjustCurrentLogo() : fileInputRef.current?.click()}
                aria-label={currentLogoSource ? "Ajustar logo actual" : "Subir logo"}
              >
                {currentLogoSource ? (
                  <LogoBgPreview
                    src={currentLogoSource}
                    name={name || "Logo"}
                    bgColor={resolvedLogoBgColor}
                    className="is-studio-main"
                    rounded={26}
                  />
                ) : (
                  <div className="gen-logo-studio-empty">
                    <b>+</b>
                    <strong>{t("Sube tu logo")}</strong>
                    <span>JPG, PNG, WEBP o HEIC</span>
                  </div>
                )}
              </button>

              <div className="gen-logo-studio-mini-row">
                <div className="gen-logo-mini-card">
                  <div className="gen-logo-mini-thumb" style={{ "--gen-logo-bg": resolvedLogoBgColor, backgroundColor: resolvedLogoBgColor }}>
                    {currentLogoSource ? (
                      <LogoBgPreview
                        src={currentLogoSource}
                        name={name || "Logo"}
                        bgColor={resolvedLogoBgColor}
                        className="is-mini"
                        rounded={14}
                      />
                    ) : <span>{String(name || "L").slice(0, 1).toUpperCase()}</span>}
                  </div>

                  <div>
                    <strong>{name || t("Tu negocio")}</strong>
                    <span>{t("Tarjeta del negocio")}</span>
                  </div>
                </div>

                <div className="gen-logo-mini-rail">
                  <div className="gen-logo-mini-dot" />
                  <div>
                    <strong>{t("Panel")}</strong>
                    <span>{t("Vista compacta")}</span>
                  </div>
                </div>
              </div>
            </section>

            <aside className="gen-logo-studio-controls">
              <div className="gen-logo-control-card gen-logo-control-card-primary">
                <span className="gen-logo-studio-kicker">{t("Imagen")}</span>
                <h4>{t("Tu imagen de marca")}</h4>
                <p>{t("Usa una imagen clara y deja que Loop la adapte a tarjetas, perfil público y panel de gestión.")}</p>

                <div className="gen-logo-control-actions">
                  <button
                    type="button"
                    className="gen-logo-primary-action"
                    onClick={() => {
                      requestAnimationFrame(() => fileInputRef.current?.click());
                    }}
                  >{t("Cambiar imagen")}</button>
                  <button
                    type="button"
                    className="gen-logo-secondary-action"
                    disabled={!currentLogoSource}
                    onClick={adjustCurrentLogo}
                  >{t("Ajustar encuadre")}</button>
                </div>

                {logoUploadStatus ? <div className="gen-logo-upload-status">{logoUploadStatus}</div> : null}
              </div>

              <details className="gen-logo-control-card pp-logo-background"><summary>{t("Fondo del logo · opcional")}</summary>
                <div className="gen-logo-color-head">
                  <div>
                    <span className="gen-logo-studio-kicker">{t("Fondo")}</span>
                    <h4>{t("Color del marco")}</h4>
                  </div>
                  <input type="color" value={resolvedLogoBgColor} onChange={(e) => saveLogoBackgroundColor(e.target.value)} aria-label={t("Elegir color de fondo del logo")} />
                </div>

                <div className="gen-logo-studio-swatches">
                  {[
                    ["#f7efe6", "Crema"],
                    ["#fff8ef", "Marfil"],
                    ["#ffffff", "Blanco"],
                    ["#f3f4f6", "Gris"],
                    ["#e8f3f1", "Verde suave"],
                    ["#fbe4dc", "Rosa suave"],
                    ["#111827", "Oscuro"],
                  ].map(([color, label]) => (
                    <button
                      key={color}
                      type="button"
                      aria-label={`Usar ${label}`}
                      className={color.toLowerCase() === String(resolvedLogoBgColor).toLowerCase() ? "active" : ""}
                      style={{ backgroundColor: color }}
                      onClick={() => saveLogoBackgroundColor(color)}
                    >
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </details>

              <div className="gen-logo-studio-tip">
                <strong>{t("Recomendación")}</strong>
                <span>{t("Para fotos verticales o de personas, usa “Ajustar encuadre” para centrar el sujeto antes de guardar.")}</span>
              </div>
            </aside>
          </div>
        </Modal>
      ) : null}

      {showLocationModal ? (
        typeof document !== "undefined" && document.body ? createPortal(
          <div className="gen-location-picker-portal-layer">
            <PanamaLocationPickerModal
              open={showLocationModal}
              onClose={() => setShowLocationModal(false)}
              title={t("Ubicación del local")}
              subtitle="Busca o cambia provincia, distrito, corregimiento y zona sin borrar la ubicación anterior."
              initialLabel={String(address || "").trim() || buildLocationLabel(userLastLoc)}
              initialValue={address ? null : userLastLoc}
              requireAllLevels={false}
              allowPartialSelection={true}
              confirmLabel="Usar ubicación"
              onConfirm={(loc) => {
                const label = buildLocationLabel(loc);
                const nextMaps = buildGoogleMapsSearchUrl(loc) || buildGoogleMapsSearchUrl(label);
                setAddress(label);
                if (nextMaps) setMapsValue(nextMaps);
                setShowLocationModal(false);
              }}
            />
          </div>,
          document.body
        ) : (
          <PanamaLocationPickerModal
            open={showLocationModal}
            onClose={() => setShowLocationModal(false)}
            title={t("Ubicación del local")}
            subtitle="Busca o cambia provincia, distrito, corregimiento y zona sin borrar la ubicación anterior."
            initialLabel={String(address || "").trim() || buildLocationLabel(userLastLoc)}
            initialValue={address ? null : userLastLoc}
            requireAllLevels={false}
            allowPartialSelection={true}
            confirmLabel="Usar ubicación"
            onConfirm={(loc) => {
              const label = buildLocationLabel(loc);
              const nextMaps = buildGoogleMapsSearchUrl(loc) || buildGoogleMapsSearchUrl(label);
              setAddress(label);
              if (nextMaps) setMapsValue(nextMaps);
              setShowLocationModal(false);
            }}
          />
        )
      ) : null}
    </div>
  );
}

const styles = `
  .gen-v3-page{display:grid;gap:18px;width:100%;max-width:100%;}
  .gen-v3-head{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin:2px 4px 4px;}
  .gen-v3-head h1{margin:0;color:#0f172a;font-size:clamp(26px,2.1vw,34px);line-height:1;font-weight:950;letter-spacing:-.06em;}
  .gen-v3-head p{margin:8px 0 0;color:#475569;font-size:14px;font-weight:750;line-height:1.4;}
  .gen-v3-hero-card,.gen-v3-card{border:1px solid rgba(226,232,240,.94);background:rgba(255,255,255,.94);box-shadow:0 18px 54px rgba(15,23,42,.06);border-radius:28px;}
  .gen-v3-hero-card{display:grid;grid-template-columns:180px minmax(0,1fr) 330px;gap:28px;align-items:center;padding:28px 30px;min-height:250px;}
  .gen-v3-logo-wrap{position:relative;width:180px;height:180px;border-radius:34px;background:var(--gen-logo-bg,#f7efe6);box-shadow:0 18px 48px rgba(15,23,42,.08);}
  .gen-v3-logo-canvas{width:100%;height:100%;border:0;border-radius:34px;background:var(--gen-logo-bg,#f7efe6);padding:0;overflow:hidden;cursor:pointer;display:grid;place-items:center;color:#00675f;font-weight:950;}
  .gen-v3-logo-canvas .smart-logo-frame{width:100%!important;height:100%!important;border:0!important;box-shadow:none!important;background:var(--gen-logo-bg,#f7efe6)!important;}
  .gen-v3-logo-canvas .smart-logo-inner{padding:0!important;}
  .gen-v3-logo-canvas .smart-logo-wash,.gen-v3-logo-canvas .smart-logo-blur{display:none!important;}
  .gen-v3-floating-edit{position:absolute;right:-8px;bottom:16px;width:46px;height:46px;border-radius:999px;border:1px solid rgba(226,232,240,.9);background:#fff;color:#00675f;display:grid;place-items:center;box-shadow:0 12px 28px rgba(15,23,42,.16);cursor:pointer;}
  .gen-v3-hero-info{min-width:0;display:grid;align-content:center;justify-items:start;}
  .gen-v3-hero-info h2{margin:0;color:#0f172a;font-size:34px;line-height:.98;font-weight:950;letter-spacing:-.07em;}
  .gen-v3-hero-info p{margin:7px 0 0;color:#475569;font-size:18px;font-weight:850;}
  .gen-v3-pill{margin-top:9px;display:inline-flex;height:28px;align-items:center;border-radius:999px;background:rgba(0,103,95,.09);color:#00675f;padding:0 12px;font-size:13px;font-weight:900;}
  .gen-v3-url-line{margin-top:17px;display:flex;align-items:center;gap:9px;max-width:100%;color:#00675f;}
  .gen-v3-url-line strong{font-size:16px;font-weight:950;overflow-wrap:anywhere;}
  .gen-v3-url-line button{border:0;background:transparent;color:#00675f;font-weight:950;cursor:pointer;font-size:18px;}
  .gen-v3-hero-actions{display:flex;gap:10px;margin-top:16px;flex-wrap:wrap;}
  .gen-v3-hero-actions button,.gen-v3-secondary-btn{height:44px;border-radius:14px;border:1px solid rgba(226,232,240,.95);background:#fff;color:#0f172a;padding:0 16px;font-size:14px;font-weight:900;display:inline-flex;align-items:center;gap:8px;cursor:pointer;box-shadow:0 6px 18px rgba(15,23,42,.035);}
  .gen-v3-hero-actions button:hover,.gen-v3-secondary-btn:hover{border-color:rgba(0,103,95,.25);color:#00675f;}
  .gen-v3-public-card{min-height:166px;border:1px solid rgba(0,103,95,.12);background:linear-gradient(135deg,rgba(0,103,95,.065),rgba(255,255,255,.9));border-radius:24px;padding:26px 24px;display:grid;align-content:center;gap:14px;}
  .gen-v3-public-card div{display:flex;align-items:center;gap:11px;color:#0f172a;}
  .gen-v3-public-card strong{font-size:16px;font-weight:950;}
  .gen-v3-public-card p{margin:0;color:#475569;font-size:14px;font-weight:750;line-height:1.45;}
  .gen-v3-public-card button,.gen-v3-primary-btn{height:44px;border:0;border-radius:14px;background:linear-gradient(135deg,#005f57,#007467);color:#fff;padding:0 18px;font-size:14px;font-weight:950;box-shadow:0 14px 30px rgba(0,103,95,.18);cursor:pointer;}
  .gen-v3-two-col{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:18px;}
  .gen-v3-card{padding:26px 28px;}
  .gen-v3-card-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:22px;}
  .gen-v3-card-head h3{margin:0;color:#0f172a;font-size:19px;font-weight:950;letter-spacing:-.035em;}
  .gen-v3-card-head p{margin:5px 0 0;color:#64748b;font-size:13px;font-weight:750;line-height:1.35;}
  .gen-v3-info-list{display:grid;gap:0;margin-bottom:22px;}
  .gen-v3-info-list>div{display:grid;grid-template-columns:42px 140px minmax(0,1fr) 24px;align-items:center;min-height:56px;border-bottom:1px solid rgba(226,232,240,.72);gap:10px;}
  .gen-v3-info-list>div:last-child{border-bottom:0;}
  .gen-v3-info-list strong{color:#0f172a;font-size:14px;font-weight:900;}
  .gen-v3-info-list span{color:#475569;font-size:14px;font-weight:750;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .gen-v3-info-list button{border:0;background:transparent;color:#64748b;font-size:24px;line-height:1;cursor:pointer;}
  .gen-v3-icon{width:30px;height:30px;border-radius:999px;display:inline-grid;place-items:center;background:rgba(0,103,95,.09);color:#00675f;font-weight:950;font-style:normal;flex:0 0 auto;}
  .gen-v3-icon-whatsapp{background:#dcfce7;color:#16a34a;}.gen-v3-icon-instagram{background:linear-gradient(135deg,#feda75,#d62976,#4f5bd5);color:#fff;}.gen-v3-icon-phone,.gen-v3-icon-web,.gen-v3-icon-map{background:rgba(0,103,95,.09);color:#00675f;}
  .gen-muted-dash{color:#94a3b8!important;font-style:normal;}
  .gen-v3-location-top{display:flex;gap:14px;align-items:flex-start;margin-bottom:16px;}
  .gen-v3-location-top strong{display:block;color:#0f172a;font-size:15px;font-weight:950;line-height:1.25;}.gen-v3-location-top span{display:block;margin-top:4px;color:#64748b;font-size:13px;font-weight:750;}
  .gen-v3-map-preview{height:158px;border-radius:18px;overflow:hidden;background:linear-gradient(135deg,#e8f3f1,#f8fafc);display:grid;place-items:center;margin-bottom:18px;color:#00675f;}
  .gen-v3-map-preview iframe{width:100%;height:100%;border:0;filter:saturate(.82) contrast(.96);}
  .gen-v3-map-preview div{display:grid;place-items:center;gap:6px;color:#00675f;font-weight:900;}
  .gen-v3-row-actions{display:flex;gap:10px;flex-wrap:wrap;}
  .gen-v3-presentation-summary{position:relative;overflow:hidden;}
  .gen-v3-presentation-box{min-height:108px;border:1px solid rgba(226,232,240,.78);border-radius:18px;background:linear-gradient(135deg,#ffffff,#fbfaf7);padding:18px 240px 18px 18px;position:relative;display:flex;align-items:center;}
  .gen-v3-presentation-box p{margin:0;color:#0f172a;font-size:15px;font-weight:720;line-height:1.65;white-space:pre-wrap;}
  .gen-v3-spa-art{position:absolute;right:28px;bottom:8px;width:180px;height:92px;opacity:.9;}
  .gen-v3-spa-art:before{content:"";position:absolute;right:44px;bottom:0;width:50px;height:86px;border-radius:28px 28px 12px 12px;background:#e6c9a3;}.gen-v3-spa-art span{position:absolute;right:95px;bottom:0;width:44px;height:44px;border-radius:999px;background:#e8f3e6;}.gen-v3-spa-art i{position:absolute;right:5px;bottom:0;width:78px;height:36px;border-radius:999px;background:#f0e6d7;}.gen-v3-spa-art b{position:absolute;right:120px;bottom:0;width:50px;height:28px;border-radius:999px;background:#d6c1a1;}
  .gen-v3-presentation-summary>.gen-v3-secondary-btn{margin-top:14px;}
  .gen-progress{height:10px;border-radius:999px;background:#e2e8f0;overflow:hidden}.gen-progress span{display:block;height:100%;background:linear-gradient(135deg,#00675f,#269690)}
  .gen-logo-upload-status{color:#00675f;font-size:12px;font-weight:900;margin:0 4px;}
  .gen-v3-modal{position:fixed;inset:0;z-index:var(--loop-z-modal-backdrop,4000);background:rgba(15,23,42,.44);backdrop-filter:blur(10px);display:grid;place-items:center;padding:20px;}
  .gen-v3-sheet{width:min(760px,100%);max-height:min(88vh,900px);overflow:auto;background:#fff;border:1px solid rgba(226,232,240,.88);border-radius:30px;box-shadow:0 30px 90px rgba(15,23,42,.28);}
  .gen-v3-sheet-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;padding:24px 26px 18px;border-bottom:1px solid rgba(226,232,240,.72);}.gen-v3-sheet-head h3{margin:0;color:#0f172a;font-size:24px;font-weight:950;letter-spacing:-.05em;}.gen-v3-sheet-head p{margin:6px 0 0;color:#64748b;font-size:13px;font-weight:750;line-height:1.4;}.gen-v3-sheet-head>button{width:40px;height:40px;border:0;border-radius:999px;background:#f8fafc;color:#0f172a;font-size:26px;line-height:1;cursor:pointer;}
  .gen-v3-sheet-body{padding:24px 26px;display:grid;gap:16px;}.gen-v3-sheet-footer{padding:16px 26px 24px;border-top:1px solid rgba(226,232,240,.72);display:flex;justify-content:flex-end;gap:10px;}
  .gen-v3-modal-grid{display:grid;grid-template-columns:210px minmax(0,1fr);gap:18px;align-items:start;}.gen-v3-logo-edit-card{display:grid;gap:12px;}.gen-v3-logo-edit-card .gen-v3-logo-canvas{width:210px;height:170px;border-radius:26px;}
  .gen-v3-modal-fields{display:grid;gap:14px;}.gen-v3-fields-two{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .gen-v3-field{display:grid;gap:8px;color:#0f172a;}.gen-v3-field>span{font-size:12px;font-weight:950;color:#334155;}.gen-v3-field em{font-style:normal;font-size:12px;color:#64748b;font-weight:750;line-height:1.35;}.gen-v3-field input,.gen-v3-field select,.gen-v3-field textarea{width:100%;border:1px solid #e2e8f0;border-radius:16px;background:#fff;min-height:48px;padding:0 14px;font-size:14px;font-weight:800;color:#0f172a;outline:none;}.gen-v3-field textarea{padding:14px;line-height:1.55;resize:vertical;}.gen-v3-field input:focus,.gen-v3-field select:focus,.gen-v3-field textarea:focus{border-color:rgba(0,103,95,.42);box-shadow:0 0 0 4px rgba(0,103,95,.08)}
  .gen-v3-visibility{width:max-content;border:1px solid #e2e8f0;border-radius:999px;background:#fff;padding:7px 13px 7px 8px;display:flex;align-items:center;gap:9px;cursor:pointer;white-space:nowrap;}.gen-v3-visibility .track{width:46px;height:28px;border-radius:999px;background:#cbd5e1;position:relative;}.gen-v3-visibility .track i{position:absolute;top:3px;left:3px;width:22px;height:22px;border-radius:999px;background:#fff;box-shadow:0 4px 12px rgba(15,23,42,.16);transition:160ms ease;}.gen-v3-visibility.on{border-color:rgba(0,103,95,.22);background:rgba(0,103,95,.055);}.gen-v3-visibility.on .track{background:linear-gradient(135deg,#00675f,#269690);}.gen-v3-visibility.on .track i{left:21px;}.gen-v3-visibility strong{font-size:13px;font-weight:950;color:#0f172a;}
  .gen-v3-url-card{border:1px solid rgba(0,103,95,.14);border-radius:20px;background:rgba(0,103,95,.045);padding:14px;display:grid;gap:12px;}.gen-v3-url-card span{display:block;color:#00675f;text-transform:uppercase;font-size:11px;letter-spacing:.1em;font-weight:950;}.gen-v3-url-card strong{display:block;margin-top:5px;font-size:14px;overflow-wrap:anywhere;color:#00675f;}.gen-v3-url-card em{display:block;margin-top:4px;font-size:12px;color:#64748b;font-weight:750;font-style:normal;}.gen-v3-url-card>div:last-child{display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:8px;}.gen-v3-url-card button{height:42px;border-radius:14px;border:1px solid rgba(0,103,95,.20);background:#fff;color:#00675f;padding:0 12px;font-weight:950;cursor:pointer;}
  .gen-phone-combo{position:relative;width:100%;display:grid;grid-template-columns:126px minmax(0,1fr);border:1px solid #e2e8f0;border-radius:16px;background:#fff;min-height:48px;}.gen-phone-country-button{border:0;border-right:1px solid #e2e8f0;background:#f8fafc;border-radius:16px 0 0 16px;padding:0 10px;display:flex;align-items:center;justify-content:center;gap:7px;cursor:pointer;color:#0f172a;font-size:13px;}.gen-phone-local{border:0!important;background:transparent!important;min-height:46px!important;padding:0 14px!important;font-size:14px!important;font-weight:850!important;outline:none!important;}.gen-phone-country-menu{position:absolute;left:0;top:54px;z-index:5;width:240px;border:1px solid #e2e8f0;background:#fff;border-radius:18px;box-shadow:0 18px 44px rgba(15,23,42,.16);padding:8px;display:grid;gap:4px;}.gen-phone-country-menu button{border:0;border-radius:14px;background:transparent;padding:9px;display:flex;align-items:center;gap:10px;text-align:left;cursor:pointer;}.gen-phone-country-menu button.active,.gen-phone-country-menu button:hover{background:rgba(0,103,95,.07);}.gen-phone-country-menu strong{display:block;font-size:13px;}.gen-phone-country-menu em{display:block;font-style:normal;font-size:11px;color:#64748b;font-weight:800;}
  .gen-css-flag{width:20px;height:14px;border-radius:3px;display:inline-block;position:relative;overflow:hidden;box-shadow:inset 0 0 0 1px rgba(15,23,42,.15);background:#fff;}.gen-css-flag-pa{background:linear-gradient(90deg,#fff 0 50%,#d21f3c 50% 100%);}.gen-css-flag-pa:after{content:"";position:absolute;left:0;bottom:0;width:50%;height:50%;background:#2456a6;}.gen-css-flag .flag-star{display:none}.gen-css-flag-us{background:repeating-linear-gradient(to bottom,#b22234 0 1.4px,#fff 1.4px 2.8px);}.gen-css-flag-us:before{content:"";position:absolute;left:0;top:0;width:45%;height:55%;background:#3c3b6e}.gen-css-flag-co{background:linear-gradient(to bottom,#fcd116 0 50%,#003893 50% 75%,#ce1126 75%)}.gen-css-flag-cr{background:linear-gradient(to bottom,#002b7f 0 18%,#fff 18% 32%,#ce1126 32% 68%,#fff 68% 82%,#002b7f 82%)}.gen-css-flag-mx{background:linear-gradient(90deg,#006847 0 33%,#fff 33% 66%,#ce1126 66%)}.gen-css-flag-br{background:#009b3a}.gen-css-flag-br:before{content:"";position:absolute;inset:3px 5px;background:#ffdf00;transform:rotate(45deg)}.gen-css-flag-es{background:linear-gradient(to bottom,#aa151b 0 25%,#f1bf00 25% 75%,#aa151b 75%)}
  .gen-v3-mode-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}.gen-v3-mode-card{min-height:104px;border:1px solid #e2e8f0;border-radius:20px;background:#fff;padding:14px;text-align:left;display:grid;gap:6px;cursor:pointer;}.gen-v3-mode-card.active{border-color:rgba(0,103,95,.42);background:rgba(0,103,95,.055);box-shadow:0 0 0 4px rgba(0,103,95,.06);}.gen-v3-mode-card span{font-size:22px}.gen-v3-mode-card strong{font-size:14px;color:#0f172a;font-weight:950}.gen-v3-mode-card em{font-style:normal;font-size:12px;color:#64748b;font-weight:750;line-height:1.35}
  .gen-v3-location-picker-row{border:1px solid rgba(226,232,240,.9);border-radius:20px;background:#f8fafc;padding:14px;display:flex;align-items:center;justify-content:space-between;gap:12px;}.gen-v3-location-picker-row strong{display:block;color:#0f172a;font-size:14px;font-weight:950}.gen-v3-location-picker-row span{display:block;margin-top:3px;color:#64748b;font-size:12px;font-weight:750}.gen-v3-service-areas{border:1px solid rgba(226,232,240,.9);border-radius:20px;overflow:hidden;background:#fff;}
  .gen-logo-modal-preview{width:100%;aspect-ratio:16/9;border-radius:22px;border:0;background:var(--gen-logo-bg,#f7efe6);display:grid;place-items:center;overflow:hidden;}.gen-logo-modal-preview .smart-logo-frame{width:100%!important;height:100%!important;background:var(--gen-logo-bg,#f7efe6)!important;border:0!important;box-shadow:none!important}.gen-logo-modal-preview .smart-logo-inner{padding:0!important}.gen-logo-modal-preview .smart-logo-wash,.gen-logo-modal-preview .smart-logo-blur{display:none!important}.gen-v3-logo-actions{display:grid;grid-template-columns:1fr 1fr;gap:10px}.gen-v3-secondary-btn:disabled{opacity:.45;cursor:not-allowed}.gen-logo-color-panel{border:1px solid #edf2f7;border-radius:22px;padding:14px;background:#f8fafc;}.gen-logo-color-panel label{display:flex;align-items:center;justify-content:space-between;gap:12px;color:#0f172a;font-size:13px;font-weight:900}.gen-logo-color-panel input[type="color"]{width:46px;height:34px;border:0;padding:0;background:transparent;cursor:pointer}.gen-logo-swatches{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}.gen-logo-swatches button{width:30px;height:30px;border-radius:999px;border:2px solid rgba(203,213,225,.8);cursor:pointer;box-shadow:0 5px 14px rgba(15,23,42,.08)}.gen-logo-swatches button.active{border-color:#00675f;box-shadow:0 0 0 4px rgba(0,103,95,.10)}
  @media(max-width:1120px){.gen-v3-hero-card{grid-template-columns:150px minmax(0,1fr);}.gen-v3-public-card{grid-column:1/-1;}.gen-v3-logo-wrap{width:150px;height:150px}.gen-v3-two-col{grid-template-columns:1fr;}}
  @media(max-width:720px){.gen-v3-hero-card{grid-template-columns:1fr;padding:20px;border-radius:24px;}.gen-v3-logo-wrap{width:150px;height:150px}.gen-v3-card{padding:20px;border-radius:24px}.gen-v3-presentation-box{padding:16px;}.gen-v3-spa-art{display:none}.gen-v3-info-list>div{grid-template-columns:38px 110px minmax(0,1fr) 20px}.gen-v3-modal-grid,.gen-v3-fields-two,.gen-v3-mode-grid{grid-template-columns:1fr}.gen-v3-logo-edit-card .gen-v3-logo-canvas{width:100%;height:190px}.gen-v3-url-card>div:last-child{grid-template-columns:1fr}.gen-v3-sheet{border-radius:24px}.gen-v3-sheet-head,.gen-v3-sheet-body,.gen-v3-sheet-footer{padding-left:18px;padding-right:18px}.gen-phone-combo{grid-template-columns:118px minmax(0,1fr)}}

  /* LOOP D2.9.4K — GeneralSection exact visual polish */
  .loop-settings-content .gen-v3-head{display:none!important;}
  .gen-v3-page{gap:18px!important;}
  .gen-v3-hero-card{padding:28px 32px!important;grid-template-columns:184px minmax(0,1fr) 330px!important;align-items:center!important;min-height:246px!important;}
  .gen-v3-logo-wrap{width:170px!important;height:170px!important;border-radius:30px!important;}
  .gen-v3-logo-canvas{border-radius:30px!important;}
  .gen-v3-title-row{display:flex;align-items:center;gap:10px;min-width:0;}
  .gen-v3-title-row h2{margin:0!important;}
  .gen-v3-title-edit{width:34px;height:34px;border:0;border-radius:999px;background:rgba(0,103,95,.08);color:#00675f;display:inline-grid;place-items:center;cursor:pointer;box-shadow:none;flex:0 0 auto;}
  .gen-v3-title-edit .gen-v3-icon{width:18px;height:18px;background:transparent!important;color:inherit!important;}
  .gen-v3-title-edit:hover{background:rgba(0,103,95,.14);}
  .gen-v3-url-line{margin-top:16px!important;gap:9px!important;}
  .gen-v3-url-line>.gen-v3-icon{width:22px!important;height:22px!important;background:transparent!important;color:#00675f!important;}
  .gen-v3-url-line strong{color:#00675f!important;font-size:15.5px!important;}
  .gen-v3-hero-actions{gap:12px!important;margin-top:15px!important;}
  .gen-v3-hero-actions button,.gen-v3-secondary-btn{height:43px!important;border-radius:15px!important;border:1px solid rgba(203,213,225,.78)!important;background:#fff!important;color:#0f172a!important;padding:0 17px!important;font-size:14px!important;font-weight:900!important;box-shadow:0 8px 20px rgba(15,23,42,.035)!important;}
  .gen-v3-hero-actions button .gen-v3-icon,.gen-v3-secondary-btn .gen-v3-icon{width:28px!important;height:28px!important;}
  .gen-v3-hero-actions button:hover,.gen-v3-secondary-btn:hover{border-color:rgba(0,103,95,.24)!important;color:#00675f!important;}
  .gen-v3-public-card{padding:28px!important;border-radius:24px!important;background:linear-gradient(135deg,rgba(0,103,95,.055),rgba(255,255,255,.94))!important;}
  .gen-v3-public-card>div .gen-v3-icon{width:30px!important;height:30px!important;}
  .gen-v3-public-card button{border-radius:15px!important;height:45px!important;}
  .gen-v3-two-col{gap:18px!important;}
  .gen-v3-card{border-radius:28px!important;padding:25px 28px!important;box-shadow:0 18px 55px rgba(15,23,42,.055)!important;}
  .gen-v3-card-head h3{font-size:20px!important;}
  .gen-v3-info-list{margin-top:18px!important;margin-bottom:20px!important;}
  .gen-v3-info-list>div{grid-template-columns:42px 138px minmax(0,1fr) 22px!important;min-height:58px!important;}
  .gen-v3-info-list .gen-v3-icon{width:32px!important;height:32px!important;}
  .gen-v3-info-list strong{font-size:14.5px!important;}
  .gen-v3-info-list span{font-size:14px!important;color:#475569!important;}
  .gen-v3-info-list button{font-size:22px!important;color:#64748b!important;}
  .gen-v3-icon{font-size:0!important;}
  .gen-v3-icon svg{width:16px;height:16px;display:block;}
  .gen-v3-icon-whatsapp{background:#dcfce7!important;color:#22c55e!important;}
  .gen-v3-icon-phone{background:rgba(0,103,95,.10)!important;color:#007467!important;}
  .gen-v3-icon-instagram{background:radial-gradient(circle at 30% 110%,#fdf497 0 18%,#fd5949 42%,#d6249f 68%,#285AEB 100%)!important;color:#fff!important;}
  .gen-v3-icon-web{background:rgba(0,103,95,.10)!important;color:#00675f!important;}
  .gen-v3-icon-copy,.gen-v3-icon-share,.gen-v3-icon-edit,.gen-v3-icon-map,.gen-v3-icon-eye{background:rgba(0,103,95,.08)!important;color:#00675f!important;}
  .gen-v3-location-top .gen-v3-icon{width:42px!important;height:42px!important;}
  .gen-v3-map-preview{height:142px!important;border-radius:18px!important;}
  .gen-v3-row-actions{gap:11px!important;}
  .gen-v3-presentation-summary{padding-bottom:22px!important;}
  .gen-v3-presentation-box{min-height:100px!important;border-radius:18px!important;}
  @media(max-width:1120px){.gen-v3-hero-card{grid-template-columns:160px minmax(0,1fr)!important;}.gen-v3-public-card{grid-column:1/-1!important}.gen-v3-logo-wrap{width:156px!important;height:156px!important;}}
  @media(max-width:720px){.gen-v3-hero-card{grid-template-columns:1fr!important;padding:20px!important}.gen-v3-logo-wrap{width:150px!important;height:150px!important}.gen-v3-info-list>div{grid-template-columns:38px 110px minmax(0,1fr) 20px!important;}}



  /* LOOP D2.9.4K.2 — visible contact icon + harmony modal override */
  .gen-v3-contact-summary .gen-v3-info-list{margin-top:18px!important;}
  .gen-v3-contact-summary .gen-v3-info-list>div{grid-template-columns:48px 132px minmax(0,1fr) 22px!important;min-height:62px!important;}
  .gen-v3-contact-summary .gen-v3-info-list .gen-v3-icon{
    width:36px!important;height:36px!important;border-radius:13px!important;
    display:inline-grid!important;place-items:center!important;
    box-shadow:0 10px 22px rgba(15,23,42,.075)!important;
    background:#edf8f5!important;color:#007467!important;
  }
  .gen-v3-contact-summary .gen-v3-info-list .gen-v3-icon svg{display:none!important;}
  .gen-v3-contact-summary .gen-v3-info-list .gen-v3-icon:before{
    content:"";width:19px;height:19px;display:block;background:currentColor;
    -webkit-mask:center/contain no-repeat;mask:center/contain no-repeat;
  }
  .gen-v3-contact-summary .gen-v3-icon-whatsapp{
    background:#25D366!important;color:#fff!important;border-radius:12px!important;
  }
  .gen-v3-contact-summary .gen-v3-icon-whatsapp:before{
    width:20px!important;height:20px!important;
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20.52 3.48A11.86 11.86 0 0 0 12.07 0C5.5 0 .16 5.34.16 11.91c0 2.1.55 4.16 1.6 5.97L.06 24l6.27-1.64a11.9 11.9 0 0 0 5.74 1.46h.01c6.57 0 11.91-5.34 11.92-11.9 0-3.19-1.24-6.18-3.48-8.44ZM12.08 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.72.97.99-3.63-.24-.37a9.86 9.86 0 0 1-1.52-5.27c0-5.45 4.44-9.89 9.9-9.89a9.82 9.82 0 0 1 6.99 2.9 9.83 9.83 0 0 1 2.9 7c0 5.45-4.44 9.88-9.89 9.88Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.09 3.19 5.07 4.47.71.31 1.26.49 1.69.63.71.23 1.36.2 1.88.12.57-.09 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20.52 3.48A11.86 11.86 0 0 0 12.07 0C5.5 0 .16 5.34.16 11.91c0 2.1.55 4.16 1.6 5.97L.06 24l6.27-1.64a11.9 11.9 0 0 0 5.74 1.46h.01c6.57 0 11.91-5.34 11.92-11.9 0-3.19-1.24-6.18-3.48-8.44ZM12.08 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.72.97.99-3.63-.24-.37a9.86 9.86 0 0 1-1.52-5.27c0-5.45 4.44-9.89 9.9-9.89a9.82 9.82 0 0 1 6.99 2.9 9.83 9.83 0 0 1 2.9 7c0 5.45-4.44 9.88-9.89 9.88Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.09 3.19 5.07 4.47.71.31 1.26.49 1.69.63.71.23 1.36.2 1.88.12.57-.09 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z'/%3E%3C/svg%3E");
  }
  .gen-v3-contact-summary .gen-v3-icon-phone:before{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.58.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.58 1 1 0 0 1-.24 1.01l-2.2 2.2Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.58.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.58 1 1 0 0 1-.24 1.01l-2.2 2.2Z'/%3E%3C/svg%3E");
  }
  .gen-v3-contact-summary .gen-v3-icon-instagram{
    background:radial-gradient(circle at 30% 107%,#fdf497 0 16%,#fd5949 38%,#d6249f 62%,#285AEB 100%)!important;color:#fff!important;border-radius:12px!important;
  }
  .gen-v3-contact-summary .gen-v3-icon-instagram:before{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.8 2h8.4A5.8 5.8 0 0 1 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8A5.8 5.8 0 0 1 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2Zm0 2A3.8 3.8 0 0 0 4 7.8v8.4A3.8 3.8 0 0 0 7.8 20h8.4a3.8 3.8 0 0 0 3.8-3.8V7.8A3.8 3.8 0 0 0 16.2 4H7.8Zm4.2 3.3a4.7 4.7 0 1 1 0 9.4 4.7 4.7 0 0 1 0-9.4Zm0 2a2.7 2.7 0 1 0 0 5.4 2.7 2.7 0 0 0 0-5.4Zm5.2-2.6a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.8 2h8.4A5.8 5.8 0 0 1 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8A5.8 5.8 0 0 1 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2Zm0 2A3.8 3.8 0 0 0 4 7.8v8.4A3.8 3.8 0 0 0 7.8 20h8.4a3.8 3.8 0 0 0 3.8-3.8V7.8A3.8 3.8 0 0 0 16.2 4H7.8Zm4.2 3.3a4.7 4.7 0 1 1 0 9.4 4.7 4.7 0 0 1 0-9.4Zm0 2a2.7 2.7 0 1 0 0 5.4 2.7 2.7 0 0 0 0-5.4Zm5.2-2.6a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2Z'/%3E%3C/svg%3E");
  }
  .gen-v3-contact-summary .gen-v3-icon-web:before{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 2c.95 1.15 1.74 2.67 2.18 4H9.82C10.26 6.67 11.05 5.15 12 4ZM4.26 14A8.1 8.1 0 0 1 4 12c0-.69.09-1.36.26-2h3.28A15.9 15.9 0 0 0 7.4 12c0 .68.05 1.35.14 2H4.26Zm1.16 2h2.55c.27 1.01.68 1.94 1.2 2.72A8.04 8.04 0 0 1 5.42 16Zm2.55-8H5.42a8.04 8.04 0 0 1 3.75-2.72A11.9 11.9 0 0 0 7.97 8Zm4.03 12c-.95-1.15-1.74-2.67-2.18-4h4.36c-.44 1.33-1.23 2.85-2.18 4Zm2.72-6H9.28a13.6 13.6 0 0 1-.15-2c0-.7.05-1.37.15-2h5.44c.1.63.15 1.3.15 2 0 .7-.05 1.37-.15 2Zm.11 4.72c.52-.78.93-1.71 1.2-2.72h2.55a8.04 8.04 0 0 1-3.75 2.72ZM16.46 14c.09-.65.14-1.32.14-2 0-.68-.05-1.35-.14-2h3.28c.17.64.26 1.31.26 2 0 .69-.09 1.36-.26 2h-3.28Zm-.43-6a11.9 11.9 0 0 0-1.2-2.72A8.04 8.04 0 0 1 18.58 8h-2.55Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 2c.95 1.15 1.74 2.67 2.18 4H9.82C10.26 6.67 11.05 5.15 12 4ZM4.26 14A8.1 8.1 0 0 1 4 12c0-.69.09-1.36.26-2h3.28A15.9 15.9 0 0 0 7.4 12c0 .68.05 1.35.14 2H4.26Zm1.16 2h2.55c.27 1.01.68 1.94 1.2 2.72A8.04 8.04 0 0 1 5.42 16Zm2.55-8H5.42a8.04 8.04 0 0 1 3.75-2.72A11.9 11.9 0 0 0 7.97 8Zm4.03 12c-.95-1.15-1.74-2.67-2.18-4h4.36c-.44 1.33-1.23 2.85-2.18 4Zm2.72-6H9.28a13.6 13.6 0 0 1-.15-2c0-.7.05-1.37.15-2h5.44c.1.63.15 1.3.15 2 0 .7-.05 1.37-.15 2Zm.11 4.72c.52-.78.93-1.71 1.2-2.72h2.55a8.04 8.04 0 0 1-3.75 2.72ZM16.46 14c.09-.65.14-1.32.14-2 0-.68-.05-1.35-.14-2h3.28c.17.64.26 1.31.26 2 0 .69-.09 1.36-.26 2h-3.28Zm-.43-6a11.9 11.9 0 0 0-1.2-2.72A8.04 8.04 0 0 1 18.58 8h-2.55Z'/%3E%3C/svg%3E");
  }

  .gen-v3-modal{
    background:rgba(251,247,239,.34)!important;
    backdrop-filter:blur(4px) saturate(1.02)!important;
    -webkit-backdrop-filter:blur(4px) saturate(1.02)!important;
    padding:24px!important;
  }
  .gen-v3-modal:before{
    content:""!important;position:absolute!important;inset:0!important;
    background:radial-gradient(circle at 50% 45%,rgba(255,255,255,.40),rgba(255,255,255,.16) 44%,rgba(0,103,95,.06) 100%)!important;
    pointer-events:none!important;
  }
  .gen-v3-sheet{
    border-radius:30px!important;background:rgba(255,255,255,.985)!important;
    border:1px solid rgba(226,232,240,.78)!important;
    box-shadow:0 24px 70px rgba(15,23,42,.16),0 1px 0 rgba(255,255,255,.9) inset!important;
    transform-origin:center!important;
  }
  .gen-v3-sheet-head,.gen-v3-sheet-body,.gen-v3-sheet-footer{background:rgba(255,255,255,.985)!important;}
  .gen-v3-sheet-head{border-bottom:1px solid rgba(226,232,240,.58)!important;}
  .gen-v3-sheet-footer{border-top:1px solid rgba(226,232,240,.58)!important;}

  /* LOOP GeneralSection modal portal fix — render over the full app, not only the General canvas */
  .gen-v3-modal.gen-v3-modal-workspace{
    position:fixed!important;
    inset:0!important;
    z-index:var(--loop-z-modal-backdrop,4000)!important;
    width:100vw!important;
    height:100dvh!important;
    min-height:100dvh!important;
    background:
      radial-gradient(circle at 50% 0%, rgba(255,255,255,.18), transparent 36%),
      linear-gradient(180deg,rgba(0,103,95,.10),rgba(15,23,42,.43)),
      rgba(15,23,42,.42)!important;
    backdrop-filter:blur(18px) saturate(.95)!important;
    -webkit-backdrop-filter:blur(18px) saturate(.95)!important;
    display:flex!important;
    align-items:flex-start!important;
    justify-content:center!important;
    padding:clamp(32px,5vh,56px) clamp(18px,3.4vw,64px)!important;
    overflow-y:auto!important;
    overscroll-behavior:contain!important;
    isolation:isolate!important;
  }
  .gen-v3-sheet.gen-v3-sheet-workspace{
    position:relative!important;
    width:min(1120px,calc(100vw - 84px))!important;
    max-height:calc(100dvh - clamp(64px,10vh,112px))!important;
    min-height:min(620px,calc(100dvh - 112px))!important;
    overflow:hidden!important;
    display:flex!important;
    flex-direction:column!important;
    background:rgba(255,255,255,.985)!important;
    border:1px solid rgba(255,255,255,.78)!important;
    border-radius:34px!important;
    box-shadow:0 42px 125px rgba(15,23,42,.34),0 0 0 1px rgba(226,232,240,.65)!important;
  }
  .gen-v3-sheet.gen-v3-sheet-workspace .gen-v3-sheet-head{
    flex:0 0 auto!important;
    background:rgba(255,255,255,.985)!important;
    backdrop-filter:blur(12px)!important;
    -webkit-backdrop-filter:blur(12px)!important;
  }
  .gen-v3-sheet.gen-v3-sheet-workspace .gen-v3-sheet-body{
    flex:1 1 auto!important;
    min-height:0!important;
    overflow:auto!important;
    padding:28px 34px!important;
  }
  .gen-v3-sheet.gen-v3-sheet-workspace .gen-v3-sheet-footer{
    flex:0 0 auto!important;
    background:linear-gradient(180deg,rgba(255,255,255,.82),#fff)!important;
    backdrop-filter:blur(12px)!important;
    -webkit-backdrop-filter:blur(12px)!important;
  }
  @media(max-width:860px){
    .gen-v3-modal.gen-v3-modal-workspace{padding:14px!important;align-items:flex-start!important;}
    .gen-v3-sheet.gen-v3-sheet-workspace{
      width:100%!important;
      min-height:calc(100dvh - 28px)!important;
      max-height:calc(100dvh - 28px)!important;
      border-radius:26px!important;
    }
  }



  /* LOOP D2.9.4K.5 — professional inline SVG contact icons (no CSS masks) */
  .gen-v3-contact-summary .gen-v3-info-list>div{
    grid-template-columns:48px 132px minmax(0,1fr) 22px!important;
    min-height:60px!important;
  }
  .gen-contact-icon{
    width:32px!important;
    height:32px!important;
    border-radius:10px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    flex:0 0 32px!important;
    box-shadow:none!important;
    transform:translateZ(0);
  }
  .gen-contact-icon svg{
    width:21px!important;
    height:21px!important;
    display:block!important;
    overflow:visible!important;
  }
  .gen-contact-icon-whatsapp{
    background:#25D366!important;
    color:#fff!important;
  }
  .gen-contact-icon-whatsapp svg{width:22px!important;height:22px!important;}
  .gen-contact-icon-phone{
    background:transparent!important;
    color:#007467!important;
  }
  .gen-contact-icon-instagram{
    background:radial-gradient(circle at 30% 105%,#fdf497 0 16%,#fd5949 39%,#d6249f 65%,#285AEB 100%)!important;
    color:#fff!important;
  }
  .gen-contact-icon-web{
    background:transparent!important;
    color:#00675f!important;
  }
  .gen-v3-contact-summary .gen-v3-info-list .gen-contact-icon + strong{
    font-size:14.5px!important;
    letter-spacing:-.01em!important;
  }

  /* LOOP GENERAL MOBILE PROFILE REVAMP — feed-first profile editor */
  .gen-v3-page{
    --gen-mobile-safe-bottom:calc(92px + env(safe-area-inset-bottom,0px));
  }

  @media (max-width: 960px){
    .gen-v3-page{
      gap:14px!important;
      width:100%!important;
      overflow-x:hidden!important;
    }
    .gen-v3-head{
      margin:0!important;
      padding:0 2px!important;
    }
    .gen-v3-head h1{
      font-size:24px!important;
      letter-spacing:-.05em!important;
    }
    .gen-v3-head p{
      margin-top:6px!important;
      font-size:13px!important;
      line-height:1.35!important;
      max-width:520px!important;
    }

    .gen-v3-hero-card,
    .gen-v3-card{
      border-radius:22px!important;
      box-shadow:0 10px 28px rgba(15,23,42,.045)!important;
      border-color:rgba(226,232,240,.88)!important;
      background:rgba(255,255,255,.92)!important;
    }

    .gen-v3-hero-card{
      grid-template-columns:76px minmax(0,1fr) auto!important;
      min-height:0!important;
      gap:12px!important;
      padding:14px!important;
      align-items:center!important;
      overflow:visible!important;
    }
    .gen-v3-logo-wrap{
      width:76px!important;
      height:76px!important;
      border-radius:18px!important;
      box-shadow:0 8px 20px rgba(15,23,42,.07)!important;
    }
    .gen-v3-logo-canvas,
    .gen-v3-logo-canvas .smart-logo-frame{
      border-radius:18px!important;
    }
    .gen-v3-floating-edit{
      right:-6px!important;
      bottom:-6px!important;
      width:30px!important;
      height:30px!important;
      box-shadow:0 8px 16px rgba(15,23,42,.16)!important;
    }
    .gen-v3-floating-edit svg{
      width:14px!important;
      height:14px!important;
    }
    .gen-v3-hero-info{
      min-width:0!important;
      align-content:center!important;
    }
    .gen-v3-title-row{
      display:flex!important;
      align-items:center!important;
      gap:8px!important;
      min-width:0!important;
      width:100%!important;
    }
    .gen-v3-hero-info h2{
      font-size:21px!important;
      line-height:1.05!important;
      letter-spacing:-.055em!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
      max-width:100%!important;
    }
    .gen-v3-title-edit{
      width:30px!important;
      height:30px!important;
      min-width:30px!important;
      border:1px solid rgba(226,232,240,.92)!important;
      border-radius:999px!important;
      background:#fff!important;
      color:#00675f!important;
      display:inline-grid!important;
      place-items:center!important;
      padding:0!important;
      box-shadow:0 6px 16px rgba(15,23,42,.055)!important;
    }
    .gen-v3-title-edit svg{width:14px!important;height:14px!important;}
    .gen-v3-hero-info>p{
      margin-top:3px!important;
      font-size:13px!important;
      font-weight:850!important;
      color:#64748b!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }
    .gen-v3-pill{
      height:23px!important;
      margin-top:6px!important;
      padding:0 9px!important;
      font-size:11.5px!important;
    }
    .gen-v3-url-line{
      margin-top:8px!important;
      gap:6px!important;
      color:#64748b!important;
    }
    .gen-v3-url-line svg{width:14px!important;height:14px!important;}
    .gen-v3-url-line strong{
      font-size:12px!important;
      font-weight:850!important;
      color:#64748b!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }
    .gen-v3-hero-actions{
      grid-column:1 / -1!important;
      margin-top:8px!important;
      gap:8px!important;
      display:grid!important;
      grid-template-columns:1fr 1fr!important;
      width:100%!important;
    }
    .gen-v3-hero-actions button{
      height:38px!important;
      border-radius:13px!important;
      padding:0 10px!important;
      font-size:12.5px!important;
      justify-content:center!important;
      box-shadow:none!important;
    }
    .gen-v3-public-card{
      grid-column:auto!important;
      min-height:0!important;
      padding:0!important;
      border:0!important;
      background:transparent!important;
      box-shadow:none!important;
      display:flex!important;
      justify-content:flex-end!important;
      align-items:center!important;
      gap:0!important;
    }
    .gen-v3-public-card div,
    .gen-v3-public-card p{
      display:none!important;
    }
    .gen-v3-public-card button{
      width:42px!important;
      height:42px!important;
      min-width:42px!important;
      border-radius:14px!important;
      padding:0!important;
      font-size:0!important;
      display:grid!important;
      place-items:center!important;
      box-shadow:0 10px 22px rgba(0,103,95,.13)!important;
    }
    .gen-v3-public-card button:before{
      content:'↗';
      font-size:18px;
      font-weight:950;
      line-height:1;
    }

    .gen-v3-two-col{
      grid-template-columns:1fr!important;
      gap:12px!important;
    }
    .gen-v3-card{
      padding:16px!important;
    }
    .gen-v3-card-head{
      margin-bottom:12px!important;
      gap:10px!important;
    }
    .gen-v3-card-head h3{
      font-size:17px!important;
    }
    .gen-v3-card-head p{
      font-size:12.5px!important;
    }

    .gen-v3-info-list{
      margin-bottom:12px!important;
      gap:8px!important;
    }
    .gen-v3-info-list>div,
    .gen-v3-contact-summary .gen-v3-info-list>div{
      grid-template-columns:34px minmax(0,1fr) 26px!important;
      min-height:50px!important;
      border:1px solid rgba(226,232,240,.76)!important;
      border-radius:16px!important;
      padding:8px 10px!important;
      background:rgba(248,250,252,.72)!important;
      gap:10px!important;
    }
    .gen-v3-info-list>div>strong{
      display:none!important;
    }
    .gen-v3-info-list>div>span{
      grid-column:2!important;
      font-size:13.5px!important;
      font-weight:820!important;
      color:#334155!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }
    .gen-v3-info-list>div>button{
      grid-column:3!important;
      width:26px!important;
      height:26px!important;
      font-size:22px!important;
      color:#94a3b8!important;
    }
    .gen-contact-icon{
      width:30px!important;
      height:30px!important;
      border-radius:10px!important;
      flex-basis:30px!important;
    }
    .gen-contact-icon svg{
      width:19px!important;
      height:19px!important;
    }
    .gen-v3-contact-summary .gen-v3-secondary-btn,
    .gen-v3-location-summary .gen-v3-secondary-btn,
    .gen-v3-presentation-summary>.gen-v3-secondary-btn{
      width:100%!important;
      justify-content:center!important;
      height:42px!important;
      border-radius:14px!important;
      box-shadow:none!important;
    }

    .gen-v3-location-top{
      margin-bottom:12px!important;
      gap:10px!important;
    }
    .gen-v3-location-top strong{
      font-size:14px!important;
    }
    .gen-v3-location-top span{
      font-size:12.5px!important;
    }
    .gen-v3-map-preview{
      height:116px!important;
      border-radius:16px!important;
      margin-bottom:12px!important;
    }
    .gen-v3-row-actions{
      display:grid!important;
      grid-template-columns:1fr!important;
      gap:8px!important;
    }

    .gen-v3-presentation-box{
      min-height:0!important;
      padding:14px!important;
      border-radius:16px!important;
      align-items:flex-start!important;
    }
    .gen-v3-presentation-box p{
      font-size:13.5px!important;
      line-height:1.55!important;
      display:-webkit-box!important;
      -webkit-line-clamp:4!important;
      -webkit-box-orient:vertical!important;
      overflow:hidden!important;
    }
    .gen-v3-spa-art{display:none!important;}
  }

  @media (max-width: 720px){
    .gen-v3-head{
      display:none!important;
    }
    .gen-v3-page{
      gap:12px!important;
      padding-bottom:var(--gen-mobile-safe-bottom)!important;
    }
    .gen-v3-hero-card{
      grid-template-columns:64px minmax(0,1fr) 40px!important;
      padding:12px!important;
      gap:10px!important;
      border-radius:20px!important;
    }
    .gen-v3-logo-wrap{
      width:64px!important;
      height:64px!important;
      border-radius:16px!important;
    }
    .gen-v3-logo-canvas,
    .gen-v3-logo-canvas .smart-logo-frame{
      border-radius:16px!important;
    }
    .gen-v3-floating-edit{
      width:27px!important;
      height:27px!important;
      right:-5px!important;
      bottom:-5px!important;
    }
    .gen-v3-hero-info h2{
      font-size:19px!important;
    }
    .gen-v3-url-line{
      max-width:100%!important;
    }
    .gen-v3-hero-actions{
      grid-template-columns:1fr 1fr!important;
    }
    .gen-v3-hero-actions button svg{
      width:14px!important;
      height:14px!important;
    }

    .gen-v3-card{
      padding:14px!important;
      border-radius:20px!important;
    }
    .gen-v3-card-head{
      margin-bottom:10px!important;
    }
    .gen-v3-card-head h3{
      font-size:16px!important;
      letter-spacing:-.03em!important;
    }
    .gen-v3-card-head p{
      display:none!important;
    }

    .gen-v3-modal.gen-v3-modal-workspace{
      padding:0!important;
      align-items:flex-end!important;
      justify-content:center!important;
      background:rgba(15,23,42,.42)!important;
    }
    .gen-v3-sheet.gen-v3-sheet-workspace{
      width:100vw!important;
      max-width:100vw!important;
      min-height:auto!important;
      height:auto!important;
      max-height:88dvh!important;
      border-radius:26px 26px 0 0!important;
      border-left:0!important;
      border-right:0!important;
      border-bottom:0!important;
      box-shadow:0 -24px 70px rgba(15,23,42,.28)!important;
    }
    .gen-v3-sheet.gen-v3-sheet-workspace:before{
      content:'';
      width:42px;
      height:5px;
      border-radius:999px;
      background:#cbd5e1;
      position:absolute;
      top:9px;
      left:50%;
      transform:translateX(-50%);
      z-index:2;
    }
    .gen-v3-sheet.gen-v3-sheet-workspace .gen-v3-sheet-head{
      padding:24px 16px 13px!important;
    }
    .gen-v3-sheet-head h3{
      font-size:20px!important;
      letter-spacing:-.045em!important;
    }
    .gen-v3-sheet-head p{
      font-size:12.5px!important;
    }
    .gen-v3-sheet-head>button{
      width:36px!important;
      height:36px!important;
      font-size:24px!important;
    }
    .gen-v3-sheet.gen-v3-sheet-workspace .gen-v3-sheet-body{
      padding:16px!important;
      gap:12px!important;
      max-height:calc(88dvh - 150px)!important;
    }
    .gen-v3-sheet.gen-v3-sheet-workspace .gen-v3-sheet-footer{
      padding:12px 16px calc(14px + env(safe-area-inset-bottom,0px))!important;
      display:grid!important;
      grid-template-columns:1fr 1fr!important;
      gap:10px!important;
    }
    .gen-v3-sheet-footer button,
    .gen-v3-primary-btn,
    .gen-v3-secondary-btn{
      min-width:0!important;
    }
    .gen-v3-sheet-footer .gen-v3-primary-btn,
    .gen-v3-sheet-footer .gen-v3-secondary-btn{
      width:100%!important;
      justify-content:center!important;
    }
    .gen-v3-modal-grid,
    .gen-v3-fields-two,
    .gen-v3-mode-grid{
      grid-template-columns:1fr!important;
      gap:12px!important;
    }
    .gen-v3-logo-edit-card .gen-v3-logo-canvas{
      width:100%!important;
      height:142px!important;
      border-radius:20px!important;
    }
    .gen-v3-url-card{
      padding:12px!important;
      border-radius:16px!important;
    }
    .gen-v3-url-card>div:last-child{
      grid-template-columns:1fr!important;
      gap:8px!important;
    }
    .gen-v3-url-card button{
      width:100%!important;
    }
    .gen-phone-combo{
      grid-template-columns:116px minmax(0,1fr)!important;
    }
    .gen-phone-country-menu{
      width:min(260px,calc(100vw - 32px))!important;
      z-index:20!important;
    }
    .gen-v3-location-picker-row{
      grid-template-columns:1fr!important;
      gap:10px!important;
    }
    .gen-v3-mode-card{
      min-height:86px!important;
      padding:14px!important;
      border-radius:18px!important;
    }
  }

  @media (max-width: 390px){
    .gen-v3-hero-card{
      grid-template-columns:58px minmax(0,1fr) 38px!important;
      padding:11px!important;
    }
    .gen-v3-logo-wrap{width:58px!important;height:58px!important;}
    .gen-v3-hero-info h2{font-size:18px!important;}
    .gen-v3-hero-actions{grid-template-columns:1fr!important;}
    .gen-v3-info-list>div,
    .gen-v3-contact-summary .gen-v3-info-list>div{
      grid-template-columns:32px minmax(0,1fr) 24px!important;
      padding:8px!important;
    }
    .gen-phone-combo{grid-template-columns:108px minmax(0,1fr)!important;}
    .gen-phone-country-button strong{font-size:12px!important;}
  }


  /* LOOP D3 — General contact public compact fix
     Makes Contacto público much smaller on mobile without touching data logic. */
  @media (max-width: 720px){
    .gen-v3-contact-summary{
      padding:12px!important;
      border-radius:18px!important;
    }
    .gen-v3-contact-summary .gen-v3-card-head{
      margin-bottom:8px!important;
      display:flex!important;
      align-items:center!important;
      justify-content:space-between!important;
    }
    .gen-v3-contact-summary .gen-v3-card-head h3{
      font-size:15px!important;
      letter-spacing:-.025em!important;
    }
    .gen-v3-contact-summary .gen-v3-card-head p{
      display:none!important;
    }
    .gen-v3-contact-summary .gen-v3-info-list{
      display:grid!important;
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:7px!important;
      margin:0 0 9px!important;
    }
    .gen-v3-contact-summary .gen-v3-info-list>div{
      min-height:42px!important;
      grid-template-columns:26px minmax(0,1fr)!important;
      padding:7px 8px!important;
      gap:7px!important;
      border-radius:13px!important;
      background:rgba(248,250,252,.74)!important;
      border:1px solid rgba(226,232,240,.72)!important;
    }
    .gen-v3-contact-summary .gen-v3-info-list>div>strong,
    .gen-v3-contact-summary .gen-v3-info-list>div>button{
      display:none!important;
    }
    .gen-v3-contact-summary .gen-v3-info-list>div>span{
      grid-column:2!important;
      font-size:12.25px!important;
      line-height:1.15!important;
      font-weight:850!important;
      color:#334155!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }
    .gen-v3-contact-summary .gen-v3-info-list .gen-contact-icon{
      width:25px!important;
      height:25px!important;
      min-width:25px!important;
      border-radius:9px!important;
      flex-basis:25px!important;
    }
    .gen-v3-contact-summary .gen-v3-info-list .gen-contact-icon svg{
      width:15.5px!important;
      height:15.5px!important;
    }
    .gen-v3-contact-summary .gen-v3-secondary-btn{
      height:36px!important;
      width:100%!important;
      border-radius:12px!important;
      font-size:12.5px!important;
      padding:0 12px!important;
      box-shadow:none!important;
      justify-content:center!important;
    }
  }

  @media (max-width: 390px){
    .gen-v3-contact-summary .gen-v3-info-list{
      grid-template-columns:1fr!important;
    }
    .gen-v3-contact-summary .gen-v3-info-list>div{
      min-height:38px!important;
    }
  }



  /* LOOP GENERAL — PUBLIC LOGO PREVIEW 16:9 + NO LOAD JUMP
     Matches the public card/business page proportions instead of a square avatar.
     Also removes SmartLogoFrame auto-fit layout shifts while the image loads. */
  .gen-v3-hero-card{
    grid-template-columns:190px minmax(0,1fr) minmax(280px,360px)!important;
    gap:22px!important;
    align-items:center!important;
  }

  .gen-v3-logo-wrap{
    width:190px!important;
    height:auto!important;
    aspect-ratio:16/9!important;
    border-radius:24px!important;
    overflow:visible!important;
    background:var(--gen-logo-bg,#f7efe6)!important;
    box-shadow:0 14px 34px rgba(15,23,42,.075)!important;
    flex:0 0 auto!important;
  }

  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas .smart-logo-frame,
  .gen-v3-logo-canvas .smart-logo-inner{
    width:100%!important;
    height:100%!important;
    min-width:0!important;
    min-height:0!important;
    max-width:100%!important;
    max-height:100%!important;
    aspect-ratio:16/9!important;
    border-radius:24px!important;
    overflow:hidden!important;
    background:var(--gen-logo-bg,#f7efe6)!important;
    box-shadow:none!important;
    border:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
  }

  .gen-v3-logo-canvas .smart-logo-frame::before,
  .gen-v3-logo-canvas .smart-logo-frame::after,
  .gen-v3-logo-canvas .smart-logo-inner::before,
  .gen-v3-logo-canvas .smart-logo-inner::after,
  .gen-v3-logo-canvas .smart-logo-wash,
  .gen-v3-logo-canvas .smart-logo-blur{
    display:none!important;
    opacity:0!important;
    visibility:hidden!important;
    content:none!important;
  }

  .gen-v3-logo-canvas .smart-logo-inner{
    padding:0!important;
    box-sizing:border-box!important;
  }

  .gen-v3-logo-canvas .smart-logo-img,
  .gen-v3-logo-canvas img{
    width:100%!important;
    height:100%!important;
    min-width:0!important;
    min-height:0!important;
    max-width:100%!important;
    max-height:100%!important;
    object-fit:cover!important;
    object-position:center center!important;
    display:block!important;
    transform:none!important;
    transition:none!important;
    background:transparent!important;
    border:0!important;
    box-shadow:none!important;
  }

  .gen-v3-floating-edit{
    right:-10px!important;
    bottom:-10px!important;
    width:34px!important;
    height:34px!important;
    border-radius:999px!important;
  }

  .gen-v3-logo-edit-card .gen-v3-logo-canvas,
  .gen-logo-modal-preview{
    aspect-ratio:16/9!important;
  }

  @media(max-width:1120px){
    .gen-v3-hero-card{
      grid-template-columns:168px minmax(0,1fr)!important;
      gap:18px!important;
    }
    .gen-v3-logo-wrap{
      width:168px!important;
      aspect-ratio:16/9!important;
      border-radius:22px!important;
    }
    .gen-v3-logo-canvas,
    .gen-v3-logo-canvas .smart-logo-frame,
    .gen-v3-logo-canvas .smart-logo-inner{
      border-radius:22px!important;
    }
  }

  @media(max-width:720px){
    .gen-v3-hero-card{
      grid-template-columns:116px minmax(0,1fr) 34px!important;
      gap:10px!important;
      align-items:center!important;
    }
    .gen-v3-logo-wrap{
      width:116px!important;
      aspect-ratio:16/9!important;
      border-radius:16px!important;
    }
    .gen-v3-logo-canvas,
    .gen-v3-logo-canvas .smart-logo-frame,
    .gen-v3-logo-canvas .smart-logo-inner{
      border-radius:16px!important;
    }
    .gen-v3-floating-edit{
      width:28px!important;
      height:28px!important;
      right:-6px!important;
      bottom:-6px!important;
    }
  }

  @media(max-width:390px){
    .gen-v3-hero-card{
      grid-template-columns:104px minmax(0,1fr) 32px!important;
    }
    .gen-v3-logo-wrap{
      width:104px!important;
      aspect-ratio:16/9!important;
      border-radius:15px!important;
    }
  }


/* LOOP GENERAL SECTION — RESPONSIVE HERO GUARD FIX
   Fixes broken tablet/sidebar transitions, logo jumping, URL/actions overflow and random profile CTA placement. */
.gen-v3-page{
  width:100%!important;
  max-width:100%!important;
  min-width:0!important;
  overflow-x:hidden!important;
}

.gen-v3-hero-card,
.gen-v3-card,
.gen-v3-two-col{
  min-width:0!important;
  max-width:100%!important;
}

.gen-v3-hero-card{
  display:grid!important;
  grid-template-columns:minmax(132px,164px) minmax(0,1fr) minmax(240px,300px)!important;
  gap:22px!important;
  align-items:center!important;
  overflow:hidden!important;
}

.gen-v3-logo-wrap{
  width:160px!important;
  height:108px!important;
  min-width:160px!important;
  max-width:160px!important;
  aspect-ratio:40/27!important;
  border-radius:24px!important;
  align-self:center!important;
}

.gen-v3-logo-canvas{
  width:100%!important;
  height:100%!important;
  min-height:0!important;
  border-radius:24px!important;
  overflow:hidden!important;
}

.gen-v3-logo-canvas > *,
.gen-v3-logo-canvas img,
.gen-v3-logo-canvas picture,
.gen-v3-logo-canvas canvas,
.gen-v3-logo-canvas svg{
  width:100%!important;
  height:100%!important;
  max-width:100%!important;
  max-height:100%!important;
  object-fit:cover!important;
  display:block!important;
}

.gen-v3-hero-info{
  min-width:0!important;
  max-width:100%!important;
  overflow:hidden!important;
}

.gen-v3-title-row{
  min-width:0!important;
  max-width:100%!important;
}

.gen-v3-title-row h2{
  min-width:0!important;
  max-width:100%!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}

.gen-v3-url-line{
  min-width:0!important;
  max-width:100%!important;
  display:grid!important;
  grid-template-columns:auto minmax(0,1fr)!important;
}

.gen-v3-url-line strong{
  min-width:0!important;
  max-width:100%!important;
  display:block!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
}

.gen-v3-hero-actions{
  display:flex!important;
  flex-wrap:wrap!important;
  gap:10px!important;
  min-width:0!important;
  max-width:100%!important;
}

.gen-v3-hero-actions button{
  min-width:132px!important;
  flex:0 1 auto!important;
  white-space:nowrap!important;
}

.gen-v3-public-card{
  position:static!important;
  transform:none!important;
  inset:auto!important;
  grid-column:auto!important;
  align-self:stretch!important;
  min-width:0!important;
  max-width:100%!important;
  display:grid!important;
  align-content:center!important;
  gap:12px!important;
}

.gen-v3-public-card button{
  position:static!important;
  transform:none!important;
  inset:auto!important;
  width:100%!important;
  min-width:0!important;
  max-width:100%!important;
}

@media (max-width:1120px){
  .gen-v3-hero-card{
    grid-template-columns:150px minmax(0,1fr)!important;
    gap:18px!important;
    padding:22px!important;
    min-height:0!important;
  }

  .gen-v3-logo-wrap{
    width:150px!important;
    height:102px!important;
    min-width:150px!important;
    max-width:150px!important;
  }

  .gen-v3-public-card{
    grid-column:1/-1!important;
    width:100%!important;
    min-height:0!important;
    padding:18px!important;
  }

  .gen-v3-public-card button{
    height:44px!important;
  }
}

@media (max-width:760px){
  .gen-v3-page{
    gap:10px!important;
  }

  .gen-v3-head{
    display:none!important;
  }

  .gen-v3-hero-card{
    grid-template-columns:96px minmax(0,1fr)!important;
    gap:12px!important;
    padding:14px!important;
    border-radius:22px!important;
    align-items:center!important;
  }

  .gen-v3-logo-wrap{
    width:96px!important;
    height:68px!important;
    min-width:96px!important;
    max-width:96px!important;
    border-radius:18px!important;
  }

  .gen-v3-logo-canvas{
    border-radius:18px!important;
  }

  .gen-v3-floating-edit{
    width:30px!important;
    height:30px!important;
    right:-8px!important;
    bottom:-8px!important;
  }

  .gen-v3-title-row{
    gap:7px!important;
  }

  .gen-v3-title-row h2{
    font-size:22px!important;
    line-height:1!important;
    white-space:nowrap!important;
  }

  .gen-v3-title-edit{
    width:30px!important;
    height:30px!important;
    flex:0 0 30px!important;
  }

  .gen-v3-hero-info > p{
    font-size:13px!important;
    margin-top:5px!important;
  }

  .gen-v3-pill{
    margin-top:7px!important;
    height:26px!important;
    font-size:11px!important;
  }

  .gen-v3-url-line{
    margin-top:10px!important;
    gap:7px!important;
  }

  .gen-v3-url-line strong{
    font-size:12px!important;
  }

  .gen-v3-hero-actions{
    grid-column:1/-1!important;
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
    width:100%!important;
    margin-top:10px!important;
  }

  .gen-v3-hero-actions button{
    width:100%!important;
    min-width:0!important;
    height:40px!important;
    border-radius:13px!important;
    padding:0 8px!important;
    font-size:12px!important;
    white-space:normal!important;
    line-height:1.05!important;
  }

  .gen-v3-hero-actions button .gen-v3-icon{
    width:26px!important;
    height:26px!important;
    flex:0 0 26px!important;
  }

  .gen-v3-public-card{
    grid-column:1/-1!important;
    padding:12px!important;
    border-radius:18px!important;
    gap:8px!important;
    align-content:start!important;
  }

  .gen-v3-public-card div,
  .gen-v3-public-card p{
    display:none!important;
  }

  .gen-v3-public-card button{
    height:40px!important;
    min-height:40px!important;
    border-radius:14px!important;
    font-size:13px!important;
  }
}

@media (max-width:420px){
  .gen-v3-hero-card{
    grid-template-columns:84px minmax(0,1fr)!important;
    gap:10px!important;
    padding:12px!important;
  }

  .gen-v3-logo-wrap{
    width:84px!important;
    height:60px!important;
    min-width:84px!important;
    max-width:84px!important;
    border-radius:16px!important;
  }

  .gen-v3-title-row h2{
    font-size:20px!important;
  }

  .gen-v3-url-line strong{
    font-size:11.5px!important;
  }

  .gen-v3-hero-actions button{
    font-size:11.5px!important;
    padding:0 6px!important;
  }
}



/* =========================================================
   LOOP GENERAL SECTION — TIGHTER DESKTOP CONTENT
   Moves General section cards up/left naturally by reducing internal hero/card
   whitespace and letting the section use the available BusinessEdit width.
   ========================================================= */
@media (min-width: 1101px) {
  .gen-v3-page {
    gap: 16px !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .gen-v3-head {
    margin-bottom: 0 !important;
  }

  .gen-v3-hero-card {
    padding: 26px 34px !important;
    min-height: 226px !important;
    grid-template-columns: minmax(132px, 170px) minmax(0, 1fr) minmax(250px, 320px) !important;
    gap: 28px !important;
    align-items: center !important;
  }

  .gen-v3-logo-wrap {
    width: 150px !important;
    height: 104px !important;
    min-width: 150px !important;
    max-width: 150px !important;
  }

  .gen-v3-title-row h2 {
    font-size: clamp(30px, 2.2vw, 40px) !important;
    line-height: .95 !important;
  }

  .gen-v3-hero-info > p {
    margin-top: 6px !important;
  }

  .gen-v3-url-line {
    margin-top: 14px !important;
  }

  .gen-v3-hero-actions {
    margin-top: 14px !important;
  }

  .gen-v3-public-card {
    padding: 24px !important;
    min-height: 156px !important;
    border-radius: 24px !important;
  }

  .gen-v3-grid-2 {
    gap: 18px !important;
  }

  .gen-v3-card {
    border-radius: 24px !important;
    padding: 26px !important;
  }
}

@media (min-width: 1101px) and (max-width: 1500px) {
  .gen-v3-hero-card {
    padding: 24px 28px !important;
    gap: 24px !important;
    grid-template-columns: 150px minmax(0, 1fr) 286px !important;
  }

  .gen-v3-card {
    padding: 24px !important;
  }
}



/* =========================================================
   LOOP GENERAL SECTION — DESKTOP DENSE ALIGNMENT FINAL
   Reduces card height and inner whitespace so General starts closer to title
   and uses the newly widened BusinessEdit main column.
   ========================================================= */
@media (min-width: 1101px) {
  .gen-page.gen-v3-page,
  .gen-v3-page {
    gap: 12px !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .gen-v3-hero-card {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 214px !important;
    padding: 22px 28px !important;
    border-radius: 24px !important;
    grid-template-columns: 138px minmax(0, 1fr) 268px !important;
    gap: 22px !important;
    align-items: center !important;
  }

  .gen-v3-logo-wrap {
    width: 132px !important;
    height: 94px !important;
    min-width: 132px !important;
    max-width: 132px !important;
    border-radius: 20px !important;
  }

  .gen-v3-title-row {
    gap: 9px !important;
  }

  .gen-v3-title-row h2 {
    font-size: clamp(28px, 2.1vw, 37px) !important;
    line-height: .95 !important;
  }

  .gen-v3-title-edit {
    width: 34px !important;
    height: 34px !important;
  }

  .gen-v3-hero-info > p {
    margin-top: 4px !important;
    font-size: 14px !important;
  }

  .gen-v3-pill {
    margin-top: 8px !important;
  }

  .gen-v3-url-line {
    margin-top: 10px !important;
  }

  .gen-v3-hero-actions {
    margin-top: 10px !important;
    gap: 8px !important;
  }

  .gen-v3-hero-actions button {
    height: 42px !important;
    border-radius: 14px !important;
  }

  .gen-v3-public-card {
    min-height: 142px !important;
    padding: 20px !important;
    border-radius: 22px !important;
    gap: 10px !important;
  }

  .gen-v3-public-card button {
    height: 44px !important;
    border-radius: 14px !important;
  }

  .gen-v3-grid-2 {
    gap: 14px !important;
  }

  .gen-v3-card {
    padding: 22px !important;
    border-radius: 22px !important;
  }

  .gen-v3-contact-list {
    margin-top: 18px !important;
  }
}

@media (min-width: 1101px) and (max-width: 1320px) {
  .gen-v3-hero-card {
    grid-template-columns: 126px minmax(0, 1fr) 244px !important;
    gap: 18px !important;
    padding: 20px 24px !important;
  }

  .gen-v3-logo-wrap {
    width: 122px !important;
    height: 88px !important;
    min-width: 122px !important;
    max-width: 122px !important;
  }

  .gen-v3-public-card {
    padding: 18px !important;
  }
}



/* =========================================================
   LOOP LOGO STUDIO — premium upload/reframe modal
   ========================================================= */
.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio){
  width:min(1080px,calc(100vw - 40px))!important;
  max-height:min(760px,calc(100dvh - 44px))!important;
  border-radius:28px!important;
  overflow:hidden!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-head{
  padding:22px 28px 18px!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-body{
  padding:22px 28px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfaf7 100%)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-footer{
  padding:18px 28px 22px!important;
  display:flex!important;
  justify-content:flex-end!important;
  gap:10px!important;
  border-top:1px solid rgba(226,232,240,.86)!important;
}

.gen-logo-studio{
  display:grid;
  grid-template-columns:minmax(0,1fr) 330px;
  gap:20px;
  align-items:stretch;
}

.gen-logo-studio-preview-panel{
  border:1px solid rgba(226,232,240,.92);
  border-radius:26px;
  background:
    radial-gradient(circle at 14% 4%, rgba(0,103,95,.10), transparent 34%),
    linear-gradient(135deg,#fff 0%,#f8fffd 100%);
  padding:18px;
  min-height:430px;
  display:grid;
  grid-template-rows:auto minmax(0,1fr) auto;
  gap:14px;
  box-shadow:0 22px 52px rgba(15,23,42,.06);
}

.gen-logo-studio-preview-top{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}

.gen-logo-studio-preview-top span,
.gen-logo-studio-kicker{
  color:#00675f;
  font-size:11px;
  font-weight:950;
  letter-spacing:.14em;
  text-transform:uppercase;
}

.gen-logo-studio-preview-top strong{
  height:28px;
  border-radius:999px;
  border:1px solid rgba(0,103,95,.16);
  background:#f0fdfa;
  color:#00675f;
  display:inline-flex;
  align-items:center;
  padding:0 10px;
  font-size:11px;
  font-weight:950;
}

.gen-logo-studio-main-preview{
  width:100%;
  height:100%;
  min-height:285px;
  border:1px solid rgba(226,232,240,.9);
  border-radius:24px;
  background:var(--gen-logo-bg,#f7efe6);
  overflow:hidden;
  cursor:pointer;
  display:grid;
  place-items:center;
  padding:0;
  box-shadow:inset 0 0 0 1px rgba(255,255,255,.5);
}

.gen-logo-studio-main-preview .smart-logo-frame,
.gen-logo-studio-main-preview .smart-logo-inner{
  width:100%!important;
  height:100%!important;
  border:0!important;
  box-shadow:none!important;
  background:var(--gen-logo-bg,#f7efe6)!important;
}

.gen-logo-studio-main-preview .smart-logo-wash,
.gen-logo-studio-main-preview .smart-logo-blur{
  display:none!important;
}

.gen-logo-studio-empty{
  min-height:210px;
  display:grid;
  place-items:center;
  align-content:center;
  gap:8px;
  color:#00675f;
  text-align:center;
}

.gen-logo-studio-empty b{
  width:58px;
  height:58px;
  border-radius:20px;
  background:#e6f5f2;
  display:grid;
  place-items:center;
  font-size:30px;
}

.gen-logo-studio-empty strong{
  color:#101828;
  font-size:20px;
  font-weight:950;
  letter-spacing:-.04em;
}

.gen-logo-studio-empty span{
  color:#667085;
  font-size:12px;
  font-weight:850;
}

.gen-logo-studio-mini-row{
  display:grid;
  grid-template-columns:1.2fr .8fr;
  gap:12px;
}

.gen-logo-mini-card,
.gen-logo-mini-rail{
  min-height:74px;
  border:1px solid rgba(226,232,240,.92);
  background:rgba(255,255,255,.82);
  border-radius:18px;
  display:flex;
  align-items:center;
  gap:12px;
  padding:10px;
}

.gen-logo-mini-thumb{
  width:52px;
  height:52px;
  border-radius:14px;
  overflow:hidden;
  background:var(--gen-logo-bg,#f7efe6);
  flex:0 0 52px;
  display:grid;
  place-items:center;
  color:#00675f;
  font-weight:950;
}

.gen-logo-mini-thumb .smart-logo-frame,
.gen-logo-mini-thumb .smart-logo-inner{
  width:100%!important;
  height:100%!important;
}

.gen-logo-mini-card strong,
.gen-logo-mini-rail strong{
  display:block;
  color:#101828;
  font-size:13px;
  font-weight:950;
  line-height:1.05;
}

.gen-logo-mini-card span,
.gen-logo-mini-rail span{
  display:block;
  margin-top:3px;
  color:#667085;
  font-size:11px;
  font-weight:800;
}

.gen-logo-mini-dot{
  width:42px;
  height:42px;
  border-radius:999px;
  background:linear-gradient(135deg,#dff5f1,#fff);
  border:1px solid rgba(0,103,95,.12);
  position:relative;
  flex:0 0 42px;
}

.gen-logo-mini-dot::after{
  content:"";
  position:absolute;
  inset:13px;
  border-radius:999px;
  background:#00675f;
}

.gen-logo-studio-controls{
  display:grid;
  gap:14px;
  align-content:start;
}

.gen-logo-control-card{
  border:1px solid rgba(226,232,240,.92);
  border-radius:22px;
  background:rgba(255,255,255,.9);
  padding:16px;
  box-shadow:0 14px 34px rgba(15,23,42,.045);
}

.gen-logo-control-card-primary{
  background:linear-gradient(180deg,#ffffff 0%,#f8fffd 100%);
}

.gen-logo-control-card h4{
  margin:7px 0 0;
  color:#101828;
  font-size:20px;
  line-height:.98;
  letter-spacing:-.055em;
  font-weight:950;
}

.gen-logo-control-card p{
  margin:8px 0 0;
  color:#667085;
  font-size:12.5px;
  line-height:1.38;
  font-weight:780;
}

.gen-logo-control-actions{
  margin-top:14px;
  display:grid;
  gap:9px;
}

.gen-logo-primary-action,
.gen-logo-secondary-action{
  height:44px;
  border-radius:14px;
  font-weight:950;
  cursor:pointer;
}

.gen-logo-primary-action{
  border:0;
  background:linear-gradient(135deg,#00675f,#00897f);
  color:#fff;
  box-shadow:0 14px 28px rgba(0,103,95,.18);
}

.gen-logo-secondary-action{
  border:1px solid rgba(203,213,225,.95);
  background:#fff;
  color:#334155;
}

.gen-logo-secondary-action:disabled{
  opacity:.46;
  cursor:not-allowed;
}

.gen-logo-upload-status{
  margin-top:10px;
  border-radius:13px;
  background:#f0fdfa;
  border:1px solid rgba(0,103,95,.12);
  color:#00675f;
  padding:9px 10px;
  font-size:11.5px;
  font-weight:900;
}

.gen-logo-color-head{
  display:flex;
  align-items:start;
  justify-content:space-between;
  gap:12px;
}

.gen-logo-color-head input[type="color"]{
  width:44px;
  height:34px;
  border:0;
  padding:0;
  background:transparent;
  cursor:pointer;
}

.gen-logo-studio-swatches{
  margin-top:14px;
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:9px;
}

.gen-logo-studio-swatches button{
  height:38px;
  border-radius:999px;
  border:1px solid rgba(203,213,225,.86);
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:0 8px;
  box-shadow:inset 0 0 0 999px rgba(255,255,255,.18);
}

.gen-logo-studio-swatches button.active{
  border-color:#00675f;
  box-shadow:0 0 0 4px rgba(0,103,95,.10), inset 0 0 0 999px rgba(255,255,255,.18);
}

.gen-logo-studio-swatches button span{
  max-width:100%;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:#101828;
  font-size:10.5px;
  font-weight:950;
  text-shadow:0 1px 0 rgba(255,255,255,.62);
}

.gen-logo-studio-tip{
  border:1px solid rgba(0,103,95,.14);
  border-radius:20px;
  background:#f0fdfa;
  padding:14px;
  display:grid;
  gap:4px;
}

.gen-logo-studio-tip strong{
  color:#00675f;
  font-size:12px;
  font-weight:950;
}

.gen-logo-studio-tip span{
  color:#475467;
  font-size:11.7px;
  line-height:1.36;
  font-weight:820;
}

@media(max-width:860px){
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio){
    width:min(100vw - 20px,560px)!important;
    max-height:calc(100dvh - 20px)!important;
    border-radius:24px!important;
  }

  .gen-logo-studio{
    grid-template-columns:1fr;
  }

  .gen-logo-studio-preview-panel{
    min-height:360px;
    padding:14px;
    border-radius:22px;
  }

  .gen-logo-studio-main-preview{
    min-height:230px;
    border-radius:20px;
  }

  .gen-logo-studio-mini-row{
    grid-template-columns:1fr;
  }

  .gen-logo-studio-swatches{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }

  .gen-logo-studio-footer-secondary{
    display:none!important;
  }
}



/* =========================================================
   LOOP LOGO STUDIO — FINAL PROFESSIONAL FIX
   1) Modal above fixed global header
   2) Preview uses BusinessPage/MainMenu 16:9 proportions
   3) Background color fills all non-image canvas
   ========================================================= */

.gen-v3-modal.gen-v3-modal-workspace:has(.gen-logo-studio),
.gen-v3-modal-workspace:has(.gen-logo-studio){
  position:fixed!important;
  inset:0!important;
  z-index:var(--loop-z-modal-backdrop,4000)!important;
  padding:18px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  background:rgba(15,23,42,.58)!important;
  backdrop-filter:blur(14px)!important;
  -webkit-backdrop-filter:blur(14px)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio){
  width:min(1040px,calc(100vw - 36px))!important;
  max-height:calc(100dvh - 36px)!important;
  margin:0!important;
  border-radius:30px!important;
  overflow:hidden!important;
  display:flex!important;
  flex-direction:column!important;
  background:#fff!important;
  box-shadow:0 34px 90px rgba(15,23,42,.32)!important;
  border:1px solid rgba(255,255,255,.72)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-head{
  flex:0 0 auto!important;
  padding:22px 28px 18px!important;
  background:#fff!important;
  border-bottom:1px solid rgba(226,232,240,.86)!important;
  z-index:3!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-body{
  flex:1 1 auto!important;
  min-height:0!important;
  overflow:auto!important;
  padding:22px 28px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfaf7 100%)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-footer{
  flex:0 0 auto!important;
  padding:16px 28px 20px!important;
  background:rgba(255,255,255,.96)!important;
  border-top:1px solid rgba(226,232,240,.86)!important;
  display:flex!important;
  justify-content:flex-end!important;
  gap:10px!important;
  z-index:4!important;
  box-shadow:0 -12px 30px rgba(15,23,42,.045)!important;
}

.gen-logo-studio{
  grid-template-columns:minmax(0,1fr) 330px!important;
  gap:20px!important;
  align-items:start!important;
}

.gen-logo-studio-preview-panel{
  min-height:0!important;
  align-content:start!important;
  grid-template-rows:auto auto auto!important;
  padding:18px!important;
  gap:14px!important;
}

.gen-logo-studio-main-preview{
  width:min(100%,430px)!important;
  aspect-ratio:16/9!important;
  height:auto!important;
  min-height:0!important;
  max-height:none!important;
  justify-self:center!important;
  border-radius:26px!important;
  background:var(--gen-logo-bg,#f7efe6)!important;
  border:1px solid rgba(108,88,61,.12)!important;
  box-shadow:0 20px 58px rgba(54,41,25,.08), inset 0 0 0 1px rgba(255,255,255,.48)!important;
  overflow:hidden!important;
  padding:0!important;
}

.gen-logo-studio-main-preview .smart-logo-frame,
.gen-logo-studio-main-preview .smart-logo-inner{
  width:100%!important;
  height:100%!important;
  min-width:0!important;
  min-height:0!important;
  border-radius:26px!important;
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
  overflow:hidden!important;
}

.gen-logo-studio-main-preview .smart-logo-inner{
  padding:18px!important;
  box-sizing:border-box!important;
}

.gen-logo-studio-main-preview .smart-logo-fit-cover .smart-logo-inner{
  padding:18px!important;
}

.gen-logo-studio-main-preview .smart-logo-img,
.gen-logo-studio-main-preview img{
  width:100%!important;
  height:100%!important;
  max-width:100%!important;
  max-height:100%!important;
  object-fit:contain!important;
  object-position:center!important;
  background:transparent!important;
  border:0!important;
  box-shadow:none!important;
}

.gen-logo-studio-main-preview .smart-logo-wash,
.gen-logo-studio-main-preview .smart-logo-blur{
  display:none!important;
  opacity:0!important;
  visibility:hidden!important;
}

.gen-logo-studio-empty{
  aspect-ratio:16/9!important;
  min-height:0!important;
  width:100%!important;
}

.gen-logo-studio-mini-row{
  width:min(100%,430px)!important;
  justify-self:center!important;
}

.gen-logo-mini-thumb .smart-logo-inner{
  background:var(--gen-logo-bg,#f7efe6)!important;
  padding:6px!important;
}

.gen-logo-mini-thumb .smart-logo-img,
.gen-logo-mini-thumb img{
  object-fit:contain!important;
}

.gen-logo-color-head input[type="color"]{
  border-radius:8px!important;
  overflow:hidden!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#fff!important;
}

.gen-logo-studio-swatches button{
  position:relative!important;
  overflow:hidden!important;
}

.gen-logo-studio-swatches button::before{
  content:""!important;
  position:absolute!important;
  inset:0!important;
  background:inherit!important;
  z-index:0!important;
}

.gen-logo-studio-swatches button span{
  position:relative!important;
  z-index:1!important;
}

@media(max-width:860px){
  .gen-v3-modal.gen-v3-modal-workspace:has(.gen-logo-studio),
  .gen-v3-modal-workspace:has(.gen-logo-studio){
    padding:10px!important;
    align-items:center!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio){
    width:calc(100vw - 20px)!important;
    max-height:calc(100dvh - 20px)!important;
    border-radius:24px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-head{
    padding:18px 18px 14px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-body{
    padding:16px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-logo-studio) .gen-v3-sheet-footer{
    padding:14px 16px 16px!important;
  }

  .gen-logo-studio{
    grid-template-columns:1fr!important;
  }

  .gen-logo-studio-main-preview{
    width:100%!important;
    border-radius:22px!important;
  }

  .gen-logo-studio-main-preview .smart-logo-frame,
  .gen-logo-studio-main-preview .smart-logo-inner{
    border-radius:22px!important;
  }

  .gen-logo-studio-main-preview .smart-logo-inner{
    padding:14px!important;
  }

  .gen-logo-studio-mini-row{
    width:100%!important;
  }
}



/* =========================================================
   LOOP LOGO STUDIO — NO TOP/BOTTOM GUTTER + LIVE COLOR SYNC
   - Main preview matches the real 16:9 logo frame: image fills available height.
   - Background color fills every non-image area.
   - General hero logo uses the same selected background immediately.
   ========================================================= */

.gen-v3-logo-wrap,
.gen-v3-logo-canvas,
.gen-v3-logo-canvas .smart-logo-frame,
.gen-v3-logo-canvas .smart-logo-inner{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}

.gen-v3-logo-canvas .smart-logo-wash,
.gen-v3-logo-canvas .smart-logo-blur{
  display:none!important;
  opacity:0!important;
  visibility:hidden!important;
}

.gen-v3-logo-canvas .smart-logo-inner{
  padding:0!important;
}

.gen-v3-logo-canvas .smart-logo-img,
.gen-v3-logo-canvas img{
  object-fit:contain!important;
  object-position:center!important;
  background:transparent!important;
}

.gen-logo-studio-main-preview{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}

.gen-logo-studio-main-preview .smart-logo-frame,
.gen-logo-studio-main-preview .smart-logo-inner{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}

.gen-logo-studio-main-preview .smart-logo-inner,
.gen-logo-studio-main-preview .smart-logo-fit-contain .smart-logo-inner,
.gen-logo-studio-main-preview .smart-logo-fit-cover .smart-logo-inner{
  padding:0!important;
}

.gen-logo-studio-main-preview .smart-logo-img,
.gen-logo-studio-main-preview img{
  width:100%!important;
  height:100%!important;
  max-width:100%!important;
  max-height:100%!important;
  object-fit:contain!important;
  object-position:center center!important;
  background:transparent!important;
}

.gen-logo-studio-mini-thumb,
.gen-logo-studio-mini-thumb .smart-logo-frame,
.gen-logo-studio-mini-thumb .smart-logo-inner{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}

.gen-logo-studio-mini-thumb .smart-logo-inner{
  padding:0!important;
}

.gen-logo-mini-thumb,
.gen-logo-mini-thumb .smart-logo-frame,
.gen-logo-mini-thumb .smart-logo-inner{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}

.gen-logo-mini-thumb .smart-logo-inner{
  padding:0!important;
}

.gen-logo-mini-thumb .smart-logo-img,
.gen-logo-mini-thumb img{
  object-fit:contain!important;
  object-position:center!important;
}

@media(max-width:860px){
  .gen-logo-studio-main-preview .smart-logo-inner,
  .gen-logo-studio-main-preview .smart-logo-fit-contain .smart-logo-inner,
  .gen-logo-studio-main-preview .smart-logo-fit-cover .smart-logo-inner{
    padding:0!important;
  }
}



/* =========================================================
   LOOP LOGO STUDIO — DIRECT BACKGROUND COLOR SOURCE OF TRUTH
   The selected swatch must paint the visible preview immediately.
   Inline style provides --gen-logo-bg, this CSS prevents any gradient/card wash
   from visually hiding it.
   ========================================================= */
.gen-logo-studio-main-preview{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
  box-shadow:0 20px 58px rgba(54,41,25,.08), inset 0 0 0 1px rgba(255,255,255,.12)!important;
}

.gen-logo-studio-main-preview::before,
.gen-logo-studio-main-preview::after{
  display:none!important;
  content:none!important;
}

.gen-logo-studio-main-preview .smart-logo-frame,
.gen-logo-studio-main-preview .smart-logo-inner,
.gen-logo-studio-main-preview .smart-logo-fit-contain .smart-logo-inner,
.gen-logo-studio-main-preview .smart-logo-fit-cover .smart-logo-inner{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}

.gen-logo-studio-main-preview .smart-logo-inner{
  padding:0!important;
}

.gen-logo-studio-main-preview .smart-logo-wash,
.gen-logo-studio-main-preview .smart-logo-blur{
  display:none!important;
}

.gen-logo-studio-main-preview .smart-logo-img,
.gen-logo-studio-main-preview img{
  object-fit:contain!important;
  object-position:center!important;
  background:transparent!important;
}

/* mini preview should also reflect selected color */
.gen-logo-mini-thumb,
.gen-logo-mini-thumb .smart-logo-frame,
.gen-logo-mini-thumb .smart-logo-inner,
.gen-logo-studio-mini-row .smart-logo-frame,
.gen-logo-studio-mini-row .smart-logo-inner{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}



/* =========================================================
   LOOP LOGO PREVIEW — TRUE SELECTED BACKGROUND
   Treat the uploaded image as the logo asset. Everything outside the image
   is painted by the selected background color, not by SmartLogoFrame layers.
   ========================================================= */
.gen-logo-bg-preview{
  width:100%;
  height:100%;
  min-width:0;
  min-height:0;
  overflow:hidden;
  display:flex;
  align-items:center;
  justify-content:center;
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
  box-sizing:border-box;
}

.gen-logo-bg-preview img{
  width:100%;
  height:100%;
  max-width:100%;
  max-height:100%;
  object-fit:contain;
  object-position:center center;
  display:block;
  background:transparent!important;
  border:0!important;
  box-shadow:none!important;
}

.gen-logo-bg-preview span{
  color:#00675f;
  font-size:18px;
  font-weight:950;
}

.gen-logo-studio-main-preview > .gen-logo-bg-preview,
.gen-v3-logo-canvas > .gen-logo-bg-preview,
.gen-logo-mini-thumb > .gen-logo-bg-preview{
  width:100%!important;
  height:100%!important;
}

.gen-logo-bg-preview.is-studio-main,
.gen-logo-bg-preview.is-mini{
  border-radius:inherit!important;
}

.gen-logo-studio-main-preview,
.gen-v3-logo-canvas,
.gen-logo-mini-thumb{
  background:var(--gen-logo-bg,#f7efe6)!important;
  background-color:var(--gen-logo-bg,#f7efe6)!important;
  background-image:none!important;
}



/* =========================================================
   LOOP GENERAL HERO LOGO — BIGGER + TIGHTER PREVIEW
   Only affects the outside General section preview.
   - Larger visual logo card.
   - Crops the previously baked 16:9 canvas slightly so the "real" image feels larger.
   - Removes the visual top/bottom dead zones in the compact General card.
   ========================================================= */
@media (min-width: 901px){
  .gen-v3-hero-card{
    grid-template-columns: 220px minmax(0,1fr) 330px !important;
    gap: 26px !important;
  }

  .gen-v3-logo-wrap{
    width: 220px !important;
    height: 154px !important;
    border-radius: 28px !important;
    align-self: center !important;
  }

  .gen-v3-logo-canvas{
    border-radius: 28px !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    border-radius: 28px !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    object-fit: cover !important;
    object-position: center center !important;
    transform: scale(1.10);
    transform-origin: center center;
  }

  .gen-v3-floating-edit{
    right: -10px !important;
    bottom: -8px !important;
  }
}

@media (min-width: 901px) and (max-width: 1320px){
  .gen-v3-hero-card{
    grid-template-columns: 198px minmax(0,1fr) 286px !important;
  }

  .gen-v3-logo-wrap{
    width: 198px !important;
    height: 138px !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    transform: scale(1.12);
  }
}

@media (max-width: 900px){
  .gen-v3-logo-wrap{
    width: 132px !important;
    height: 94px !important;
    border-radius: 22px !important;
  }

  .gen-v3-logo-canvas{
    border-radius: 22px !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    border-radius: 22px !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    object-fit: cover !important;
    object-position: center center !important;
    transform: scale(1.10);
    transform-origin: center center;
  }

  .gen-v3-floating-edit{
    right: -8px !important;
    bottom: -8px !important;
  }
}



/* =========================================================
   LOOP GENERAL HERO LOGO — APP-WIDE LOGO PROPORTION
   Uses the same landscape/logo-card logic as the rest of Loop:
   - 16:9 proportional frame
   - selected background visible around the uploaded image
   - no artificial tight crop/vertical-only look in General
   ========================================================= */
@media (min-width: 901px){
  .gen-v3-hero-card{
    grid-template-columns: 248px minmax(0,1fr) 330px !important;
    gap: 28px !important;
  }

  .gen-v3-logo-wrap{
    width: 248px !important;
    height: 140px !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 28px !important;
    align-self: center !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
  }

  .gen-v3-logo-canvas{
    width: 100% !important;
    height: 100% !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 28px !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    width: 100% !important;
    height: 100% !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 28px !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview img,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
    object-position: center center !important;
    transform: none !important;
    background: transparent !important;
  }

  .gen-v3-floating-edit{
    right: -10px !important;
    bottom: -10px !important;
  }
}

@media (min-width: 901px) and (max-width: 1320px){
  .gen-v3-hero-card{
    grid-template-columns: 224px minmax(0,1fr) 286px !important;
    gap: 24px !important;
  }

  .gen-v3-logo-wrap{
    width: 224px !important;
    height: 126px !important;
  }
}

@media (max-width: 900px){
  .gen-v3-logo-wrap{
    width: 148px !important;
    height: 84px !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 22px !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
  }

  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    width: 100% !important;
    height: 100% !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 22px !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview img,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
    object-position: center center !important;
    transform: none !important;
    background: transparent !important;
  }

  .gen-v3-floating-edit{
    right: -8px !important;
    bottom: -8px !important;
  }
}



/* =========================================================
   LOOP GENERAL LOGO — EXACT SAME PROPORTION AS LOGO STUDIO
   Final override for the outside General hero logo.
   The container, button and preview all share the same 16:9 size so it
   matches the Logo Studio / ImageCropper visual proportion.
   ========================================================= */

/* Desktop */
@media (min-width: 901px){
  .gen-v3-hero-card{
    grid-template-columns: 430px minmax(0,1fr) 330px !important;
    column-gap: 28px !important;
  }

  .gen-v3-logo-wrap{
    width: 430px !important;
    height: 242px !important;
    min-width: 430px !important;
    max-width: 430px !important;
    min-height: 242px !important;
    max-height: 242px !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 28px !important;
    display: block !important;
    align-self: center !important;
    justify-self: start !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
    overflow: visible !important;
  }

  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    width: 430px !important;
    height: 242px !important;
    min-width: 430px !important;
    max-width: 430px !important;
    min-height: 242px !important;
    max-height: 242px !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 28px !important;
    display: block !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
    overflow: hidden !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview img,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: cover !important;
    object-position: center center !important;
    transform: none !important;
    display: block !important;
    background: transparent !important;
  }

  .gen-v3-floating-edit{
    right: -12px !important;
    bottom: -12px !important;
    z-index: 5 !important;
  }
}

/* Medium desktop/tablet landscape */
@media (min-width: 901px) and (max-width: 1500px){
  .gen-v3-hero-card{
    grid-template-columns: 300px minmax(0,1fr) 286px !important;
    column-gap: 24px !important;
  }

  .gen-v3-logo-wrap,
  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    width: 300px !important;
    height: 169px !important;
    min-width: 300px !important;
    max-width: 300px !important;
    min-height: 169px !important;
    max-height: 169px !important;
  }
}

/* Narrow desktop */
@media (min-width: 901px) and (max-width: 1180px){
  .gen-v3-hero-card{
    grid-template-columns: 248px minmax(0,1fr) !important;
    column-gap: 22px !important;
  }

  .gen-v3-public-card{
    grid-column: 1 / -1 !important;
  }

  .gen-v3-logo-wrap,
  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    width: 248px !important;
    height: 140px !important;
    min-width: 248px !important;
    max-width: 248px !important;
    min-height: 140px !important;
    max-height: 140px !important;
  }
}

/* Mobile */
@media (max-width: 900px){
  .gen-v3-logo-wrap,
  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    width: 160px !important;
    height: 90px !important;
    min-width: 160px !important;
    max-width: 160px !important;
    min-height: 90px !important;
    max-height: 90px !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 22px !important;
    background: var(--gen-logo-bg,#f7efe6) !important;
    background-color: var(--gen-logo-bg,#f7efe6) !important;
    background-image: none !important;
    overflow: hidden !important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview img,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    object-position: center center !important;
    transform: none !important;
  }
}



/* =========================================================
   LOOP IDENTITY MODAL — PREMIUM PROFILE EDITOR
   Removes logo editing from the business identity popup. Logo editing now lives
   only in Logo Studio, while this modal focuses on name, visibility, category
   and public URL.
   ========================================================= */
.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio){
  width:min(980px,calc(100vw - 38px))!important;
  max-height:calc(100dvh - 38px)!important;
  border-radius:30px!important;
  overflow:hidden!important;
  display:flex!important;
  flex-direction:column!important;
  background:#fff!important;
  box-shadow:0 34px 90px rgba(15,23,42,.30)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio) .gen-v3-sheet-head{
  flex:0 0 auto!important;
  padding:24px 28px 18px!important;
  border-bottom:1px solid rgba(226,232,240,.86)!important;
  background:#fff!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio) .gen-v3-sheet-body{
  flex:1 1 auto!important;
  min-height:0!important;
  overflow:auto!important;
  padding:22px 28px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfaf7 100%)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio) .gen-v3-sheet-footer{
  flex:0 0 auto!important;
  padding:16px 28px 22px!important;
  border-top:1px solid rgba(226,232,240,.86)!important;
  background:rgba(255,255,255,.96)!important;
  display:flex!important;
  justify-content:flex-end!important;
  gap:10px!important;
}

.gen-identity-studio{
  display:grid;
  grid-template-columns:280px minmax(0,1fr);
  gap:18px;
  align-items:start;
}

.gen-identity-preview-card,
.gen-identity-form-card{
  border:1px solid rgba(226,232,240,.92);
  background:rgba(255,255,255,.92);
  border-radius:26px;
  box-shadow:0 18px 48px rgba(15,23,42,.055);
}

.gen-identity-preview-card{
  padding:20px;
  position:sticky;
  top:0;
  overflow:hidden;
  background:
    radial-gradient(circle at 10% 0%, rgba(0,103,95,.12), transparent 34%),
    linear-gradient(180deg,#ffffff 0%,#f8fffd 100%);
}

.gen-identity-preview-card::after{
  content:"";
  position:absolute;
  right:-42px;
  bottom:-42px;
  width:130px;
  height:130px;
  border-radius:999px;
  background:rgba(0,103,95,.07);
  pointer-events:none;
}

.gen-identity-kicker,
.gen-identity-section-head span{
  color:#00675f;
  font-size:11px;
  font-weight:950;
  letter-spacing:.14em;
  text-transform:uppercase;
}

.gen-identity-preview-card h4{
  margin:12px 0 0;
  color:#101828;
  font-size:28px;
  line-height:.96;
  letter-spacing:-.07em;
  font-weight:950;
}

.gen-identity-preview-card p{
  margin:8px 0 0;
  color:#475467;
  font-size:13px;
  line-height:1.35;
  font-weight:850;
}

.gen-identity-preview-url{
  margin-top:18px;
  border:1px solid rgba(0,103,95,.13);
  background:#f0fdfa;
  color:#00675f;
  border-radius:18px;
  padding:12px;
  display:flex;
  gap:9px;
  align-items:center;
  min-width:0;
}

.gen-identity-preview-url strong{
  font-size:12.5px;
  font-weight:950;
  overflow-wrap:anywhere;
}

.gen-identity-preview-status{
  margin-top:14px;
  border:1px solid rgba(226,232,240,.92);
  background:#fff;
  border-radius:18px;
  padding:12px;
  display:flex;
  align-items:center;
  gap:12px;
}

.gen-identity-preview-status > span{
  width:12px;
  height:12px;
  border-radius:999px;
  flex:0 0 12px;
  box-shadow:0 0 0 5px rgba(148,163,184,.12);
  background:#94a3b8;
}

.gen-identity-preview-status > span.is-live{
  background:#00897f;
  box-shadow:0 0 0 5px rgba(0,137,127,.13);
}

.gen-identity-preview-status strong{
  display:block;
  color:#101828;
  font-size:12.5px;
  font-weight:950;
}

.gen-identity-preview-status em{
  display:block;
  margin-top:2px;
  color:#667085;
  font-size:11px;
  line-height:1.35;
  font-style:normal;
  font-weight:800;
}

.gen-identity-form-card{
  padding:20px;
}

.gen-identity-section-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:14px;
  margin-bottom:16px;
}

.gen-identity-section-head.compact{
  margin-bottom:12px;
}

.gen-identity-section-head h4{
  margin:6px 0 0;
  color:#101828;
  font-size:22px;
  line-height:.98;
  letter-spacing:-.055em;
  font-weight:950;
}

.gen-identity-section-head.compact h4{
  font-size:18px;
}

.gen-identity-section-head > button{
  height:36px;
  border-radius:999px;
  border:1px solid rgba(203,213,225,.95);
  background:#fff;
  color:#00675f;
  padding:0 13px;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
}

.gen-identity-fields{
  display:grid;
  gap:14px;
}

.gen-identity-two-fields{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}

.gen-identity-studio .gen-v3-field{
  margin:0!important;
}

.gen-identity-studio .gen-v3-field > span{
  margin-bottom:8px!important;
  color:#475467!important;
  font-size:11.5px!important;
  font-weight:950!important;
  letter-spacing:.06em!important;
  text-transform:uppercase!important;
}

.gen-identity-studio input,
.gen-identity-studio select{
  height:48px!important;
  border-radius:15px!important;
  border:1px solid rgba(203,213,225,.92)!important;
  background:#fff!important;
  color:#101828!important;
  font-size:14px!important;
  font-weight:850!important;
  padding:0 14px!important;
  box-shadow:none!important;
}

.gen-identity-studio input:focus,
.gen-identity-studio select:focus{
  outline:none!important;
  border-color:rgba(0,103,95,.45)!important;
  box-shadow:0 0 0 4px rgba(0,103,95,.08)!important;
}

.gen-identity-url-card{
  margin-top:18px;
  border:1px solid rgba(0,103,95,.13);
  background:linear-gradient(180deg,#f8fffd 0%,#ffffff 100%);
  border-radius:24px;
  padding:16px;
}

.gen-identity-url-preview{
  min-height:46px;
  border-radius:16px;
  border:1px solid rgba(0,103,95,.12);
  background:#f0fdfa;
  display:flex;
  align-items:center;
  flex-wrap:wrap;
  gap:0;
  padding:10px 12px;
  color:#00675f;
  font-size:13px;
  font-weight:900;
  overflow-wrap:anywhere;
}

.gen-identity-url-preview span{
  color:#667085;
  font-weight:850;
}

.gen-identity-url-preview strong{
  color:#00675f;
  font-weight:950;
}

.gen-identity-url-actions{
  margin-top:12px;
  display:grid;
  grid-template-columns:minmax(0,1fr) auto auto;
  gap:9px;
}

.gen-identity-url-actions button{
  height:46px;
  border-radius:14px;
  border:1px solid rgba(203,213,225,.95);
  background:#fff;
  color:#334155;
  padding:0 14px;
  font-size:13px;
  font-weight:950;
  cursor:pointer;
}

.gen-identity-url-actions button:last-child{
  background:#00675f;
  border-color:#00675f;
  color:#fff;
  box-shadow:0 12px 24px rgba(0,103,95,.14);
}

.gen-identity-url-status{
  margin:10px 0 0;
  color:#00675f;
  font-size:12px;
  line-height:1.35;
  font-weight:850;
}

.gen-identity-studio .gen-v3-visibility{
  min-width:126px!important;
  height:42px!important;
  border-radius:999px!important;
}

@media(max-width:860px){
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio){
    width:calc(100vw - 20px)!important;
    max-height:calc(100dvh - 20px)!important;
    border-radius:24px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio) .gen-v3-sheet-head{
    padding:18px 18px 14px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio) .gen-v3-sheet-body{
    padding:16px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio) .gen-v3-sheet-footer{
    padding:14px 16px 16px!important;
  }

  .gen-identity-studio{
    grid-template-columns:1fr;
    gap:12px;
  }

  .gen-identity-preview-card{
    position:relative;
    top:auto;
    padding:16px;
    border-radius:22px;
  }

  .gen-identity-preview-card h4{
    font-size:24px;
  }

  .gen-identity-form-card{
    padding:16px;
    border-radius:22px;
  }

  .gen-identity-section-head{
    align-items:flex-start;
  }

  .gen-identity-two-fields{
    grid-template-columns:1fr;
  }

  .gen-identity-url-actions{
    grid-template-columns:1fr;
  }

  .gen-identity-url-actions button{
    width:100%;
  }
}



/* =========================================================
   LOOP IDENTITY MODAL — SIMPLE VERSION + HEADER SAFE
   - Modal always appears above the global fixed header.
   - Removes the left public identity card.
   - Keeps only the information card centered and premium.
   - Adds a compact visible/hidden pill next to the outside business name.
   ========================================================= */
.gen-v3-modal.gen-v3-modal-workspace,
.gen-v3-modal-workspace{
  position:fixed!important;
  inset:0!important;
  z-index:var(--loop-z-modal-backdrop,4000)!important;
  padding:18px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  background:rgba(15,23,42,.58)!important;
  backdrop-filter:blur(14px)!important;
  -webkit-backdrop-filter:blur(14px)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace{
  margin:0!important;
  max-height:calc(100dvh - 36px)!important;
  box-shadow:0 34px 90px rgba(15,23,42,.30)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple){
  width:min(760px,calc(100vw - 38px))!important;
  border-radius:30px!important;
  overflow:hidden!important;
  display:flex!important;
  flex-direction:column!important;
  background:#fff!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple) .gen-v3-sheet-head{
  flex:0 0 auto!important;
  padding:24px 28px 18px!important;
  border-bottom:1px solid rgba(226,232,240,.86)!important;
  background:#fff!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple) .gen-v3-sheet-body{
  flex:1 1 auto!important;
  min-height:0!important;
  overflow:auto!important;
  padding:22px 28px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfaf7 100%)!important;
}

.gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple) .gen-v3-sheet-footer{
  flex:0 0 auto!important;
  padding:16px 28px 22px!important;
  border-top:1px solid rgba(226,232,240,.86)!important;
  background:rgba(255,255,255,.97)!important;
  display:flex!important;
  justify-content:flex-end!important;
  gap:10px!important;
}

.gen-identity-studio-simple{
  display:block!important;
}

.gen-identity-studio-simple .gen-identity-form-card{
  width:100%!important;
  max-width:100%!important;
  padding:22px!important;
  border-radius:26px!important;
  border:1px solid rgba(226,232,240,.92)!important;
  background:rgba(255,255,255,.94)!important;
  box-shadow:0 18px 48px rgba(15,23,42,.055)!important;
}

.gen-identity-studio-simple .gen-identity-section-head{
  margin-bottom:16px!important;
}

.gen-identity-studio-simple .gen-identity-url-card{
  margin-top:18px!important;
}

/* Hide any legacy public identity card if older CSS still creates space */
.gen-identity-studio-simple .gen-identity-preview-card{
  display:none!important;
}

/* Compact status beside the outside business name */
.gen-v3-title-row{
  display:flex!important;
  align-items:center!important;
  gap:10px!important;
  flex-wrap:wrap!important;
}

.gen-v3-title-status{
  height:30px;
  border-radius:999px;
  padding:0 11px;
  display:inline-flex;
  align-items:center;
  gap:6px;
  border:1px solid rgba(203,213,225,.86);
  background:#fff;
  color:#64748b;
  font-size:11.5px;
  font-weight:950;
  line-height:1;
  white-space:nowrap;
}

.gen-v3-title-status::before{
  content:"";
  width:7px;
  height:7px;
  border-radius:999px;
  background:#94a3b8;
  box-shadow:0 0 0 4px rgba(148,163,184,.12);
}

.gen-v3-title-status.is-live{
  color:#00675f;
  border-color:rgba(0,103,95,.18);
  background:#f0fdfa;
}

.gen-v3-title-status.is-live::before{
  background:#00897f;
  box-shadow:0 0 0 4px rgba(0,137,127,.13);
}

.gen-v3-title-status.is-hidden{
  color:#64748b;
  background:#f8fafc;
}

@media(max-width:860px){
  .gen-v3-modal.gen-v3-modal-workspace,
  .gen-v3-modal-workspace{
    padding:10px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple){
    width:calc(100vw - 20px)!important;
    max-height:calc(100dvh - 20px)!important;
    border-radius:24px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple) .gen-v3-sheet-head{
    padding:18px 18px 14px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple) .gen-v3-sheet-body{
    padding:16px!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-identity-studio-simple) .gen-v3-sheet-footer{
    padding:14px 16px 16px!important;
  }

  .gen-identity-studio-simple .gen-identity-form-card{
    padding:16px!important;
    border-radius:22px!important;
  }

  .gen-v3-title-status{
    height:26px;
    font-size:10.5px;
    padding:0 9px;
  }
}



/* =========================================================
   LOOP CONTACT PUBLIC — PROFESSIONAL ICON SYSTEM + COMPACT CARD
   Matches the cleaner reference:
   - WhatsApp: official-style green rounded icon with white glyph
   - Phone/Web: premium teal line icons without heavy bubbles
   - Instagram: compact gradient rounded icon with white glyph
   - Slightly denser contact/location cards on desktop and mobile
   ========================================================= */
.gen-v3-contact-summary,
.gen-v3-location-summary{
  padding:24px 26px!important;
}

.gen-v3-contact-summary .gen-v3-card-head,
.gen-v3-location-summary .gen-v3-card-head{
  margin-bottom:18px!important;
}

.gen-v3-info-list{
  margin-bottom:18px!important;
}

.gen-v3-info-list > div{
  grid-template-columns:36px minmax(105px,132px) minmax(0,1fr) 22px!important;
  min-height:50px!important;
  gap:10px!important;
}

.gen-v3-info-list strong{
  font-size:13.5px!important;
}

.gen-v3-info-list span{
  font-size:13.5px!important;
}

.gen-v3-info-list button{
  font-size:21px!important;
  color:#667085!important;
}

.gen-v3-contact-summary > .gen-v3-secondary-btn{
  height:40px!important;
  border-radius:13px!important;
  padding:0 14px!important;
}

/* Contact icons */
.gen-contact-icon{
  width:28px!important;
  height:28px!important;
  min-width:28px!important;
  border-radius:9px!important;
  display:inline-grid!important;
  place-items:center!important;
  background:transparent!important;
  color:#08786f!important;
  box-shadow:none!important;
  overflow:hidden!important;
}

.gen-contact-icon svg{
  width:21px!important;
  height:21px!important;
  display:block!important;
}

/* WhatsApp professional app-style */
.gen-contact-icon-whatsapp{
  background:#25D366!important;
  color:#fff!important;
  border-radius:9px!important;
  box-shadow:0 7px 16px rgba(37,211,102,.18)!important;
}

.gen-contact-icon-whatsapp svg{
  width:20px!important;
  height:20px!important;
}

/* Phone: simple teal stroke, no bubble */
.gen-contact-icon-phone{
  color:#08786f!important;
  background:transparent!important;
  border-radius:0!important;
}

.gen-contact-icon-phone svg{
  width:23px!important;
  height:23px!important;
}

/* Instagram: real gradient tile */
.gen-contact-icon-instagram{
  color:#fff!important;
  border-radius:9px!important;
  background:
    radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 62%, #285AEB 90%)!important;
  box-shadow:0 7px 16px rgba(214,36,159,.16)!important;
}

.gen-contact-icon-instagram svg{
  width:19px!important;
  height:19px!important;
}

/* Web: simple teal globe, no bubble */
.gen-contact-icon-web{
  color:#08786f!important;
  background:transparent!important;
  border-radius:0!important;
}

.gen-contact-icon-web svg{
  width:23px!important;
  height:23px!important;
}

/* Make generic icon used inside "Editar contacto" smaller and softer */
.gen-v3-contact-summary .gen-v3-secondary-btn .gen-v3-icon{
  width:28px!important;
  height:28px!important;
  background:rgba(0,103,95,.09)!important;
}

/* Location card: slight compression to align better with Contact */
.gen-v3-location-top{
  margin-bottom:13px!important;
}

.gen-v3-map-preview{
  height:142px!important;
  margin-bottom:14px!important;
  border-radius:17px!important;
}

.gen-v3-row-actions .gen-v3-secondary-btn{
  height:40px!important;
  border-radius:13px!important;
  padding:0 14px!important;
}

@media(max-width:900px){
  .gen-v3-contact-summary,
  .gen-v3-location-summary{
    padding:20px!important;
  }

  .gen-v3-info-list > div{
    grid-template-columns:34px minmax(92px,118px) minmax(0,1fr) 18px!important;
    min-height:48px!important;
  }

  .gen-contact-icon{
    width:26px!important;
    height:26px!important;
    min-width:26px!important;
    border-radius:8px!important;
  }

  .gen-contact-icon svg{
    width:19px!important;
    height:19px!important;
  }

  .gen-contact-icon-phone svg,
  .gen-contact-icon-web svg{
    width:21px!important;
    height:21px!important;
  }

  .gen-v3-map-preview{
    height:132px!important;
  }
}



/* =========================================================
   LOOP CONTACT PUBLIC — SVG ASSET ICONS FINAL
   Uses real SVG assets instead of simulated CSS icons.
   Keeps the contact card clean, compact and professional.
   ========================================================= */
.gen-v3-contact-summary,
.gen-v3-location-summary{
  padding:24px 26px!important;
}

.gen-v3-contact-summary .gen-v3-card-head,
.gen-v3-location-summary .gen-v3-card-head{
  margin-bottom:18px!important;
}

.gen-v3-info-list{
  margin-bottom:18px!important;
}

.gen-v3-info-list > div{
  grid-template-columns:38px minmax(106px,136px) minmax(0,1fr) 22px!important;
  min-height:50px!important;
  gap:10px!important;
}

.gen-contact-icon{
  width:28px!important;
  height:28px!important;
  min-width:28px!important;
  border-radius:0!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  background:transparent!important;
  box-shadow:none!important;
  overflow:visible!important;
}

.gen-contact-icon img{
  width:28px!important;
  height:28px!important;
  display:block!important;
  object-fit:contain!important;
  image-rendering:auto!important;
  user-select:none!important;
  pointer-events:none!important;
}

.gen-contact-icon-whatsapp img,
.gen-contact-icon-instagram img{
  width:26px!important;
  height:26px!important;
}

.gen-contact-icon-phone img,
.gen-contact-icon-web img{
  width:24px!important;
  height:24px!important;
}

.gen-v3-info-list strong{
  font-size:13.5px!important;
  font-weight:900!important;
}

.gen-v3-info-list span{
  font-size:13.5px!important;
  font-weight:750!important;
}

.gen-v3-info-list button{
  color:#667085!important;
  font-size:21px!important;
}

.gen-v3-contact-summary > .gen-v3-secondary-btn{
  height:40px!important;
  border-radius:13px!important;
  padding:0 14px!important;
}

.gen-v3-location-top{
  margin-bottom:13px!important;
}

.gen-v3-map-preview{
  height:142px!important;
  margin-bottom:14px!important;
  border-radius:17px!important;
}

.gen-v3-row-actions .gen-v3-secondary-btn{
  height:40px!important;
  border-radius:13px!important;
  padding:0 14px!important;
}

@media(max-width:900px){
  .gen-v3-contact-summary,
  .gen-v3-location-summary{
    padding:20px!important;
  }

  .gen-v3-info-list > div{
    grid-template-columns:36px minmax(94px,118px) minmax(0,1fr) 18px!important;
    min-height:48px!important;
  }

  .gen-contact-icon{
    width:26px!important;
    height:26px!important;
    min-width:26px!important;
  }

  .gen-contact-icon img{
    width:26px!important;
    height:26px!important;
  }

  .gen-contact-icon-phone img,
  .gen-contact-icon-web img{
    width:23px!important;
    height:23px!important;
  }

  .gen-v3-map-preview{
    height:132px!important;
  }
}



/* =========================================================
   LOOP LOCATION PICKER — TOP PORTAL FIX
   The Panama location picker is opened from inside another modal. It must
   render in its own portal layer above:
   - global fixed header
   - GeneralSection modal/backdrop
   - BusinessEdit sidebar/header
   ========================================================= */

/* Keep General modals above the app header, but below nested location picker */
.gen-v3-modal.gen-v3-modal-workspace,
.gen-v3-modal-workspace{
  z-index:var(--loop-z-modal-backdrop,4000)!important;
}

/* Dedicated highest layer for PanamaLocationPickerModal */
.gen-location-picker-portal-layer{
  position:fixed!important;
  inset:0!important;
  z-index:var(--loop-z-nested-modal-backdrop,5000)!important;
  isolation:isolate!important;
  pointer-events:auto!important;
}

/* Force any overlay/dialog rendered by the picker to stay inside this top layer */
.gen-location-picker-portal-layer > *{
  z-index:1!important;
}
.gen-location-picker-portal-layer [role="dialog"],
.gen-location-picker-portal-layer [aria-modal="true"]{
  z-index:2!important;
}

/* If the location picker uses its own fixed backdrop, keep it above everything */
.gen-location-picker-portal-layer [class*="modal"],
.gen-location-picker-portal-layer [class*="Modal"],
.gen-location-picker-portal-layer [class*="overlay"],
.gen-location-picker-portal-layer [class*="Overlay"],
.gen-location-picker-portal-layer [class*="backdrop"],
.gen-location-picker-portal-layer [class*="Backdrop"]{
  z-index:1!important;
}

/* Prevent background header/sidebar from visually competing while picker is open */
.gen-location-picker-portal-layer{
  background:transparent!important;
}

/* Mobile safety */
@media(max-width:860px){
  .gen-location-picker-portal-layer{
    padding:0!important;
  }
}



/* =========================================================
   LOOP GENERAL SECTION — MOBILE / TABLET FIT FIX
   Prevents the General hero from stacking elements on top of each other.
   Mobile becomes a clean vertical layout:
   logo -> business info -> actions -> public profile CTA.
   ========================================================= */

@media (max-width: 900px){
  .gen-v3-page{
    gap:14px!important;
    overflow-x:hidden!important;
    width:100%!important;
  }

  .gen-v3-head{
    margin:0!important;
    padding:0 2px!important;
  }

  .gen-v3-head h1{
    font-size:28px!important;
    line-height:.98!important;
    letter-spacing:-.06em!important;
  }

  .gen-v3-head p{
    font-size:12.5px!important;
    line-height:1.32!important;
    margin-top:6px!important;
    max-width:100%!important;
  }

  .gen-v3-hero-card{
    width:100%!important;
    min-width:0!important;
    min-height:0!important;
    padding:16px!important;
    border-radius:24px!important;
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:14px!important;
    align-items:start!important;
    overflow:hidden!important;
  }

  .gen-v3-logo-wrap{
    width:100%!important;
    height:auto!important;
    min-width:0!important;
    max-width:100%!important;
    min-height:0!important;
    max-height:none!important;
    aspect-ratio:16/9!important;
    border-radius:22px!important;
    justify-self:stretch!important;
    align-self:auto!important;
    overflow:visible!important;
  }

  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    width:100%!important;
    height:100%!important;
    min-width:0!important;
    max-width:100%!important;
    min-height:0!important;
    max-height:none!important;
    aspect-ratio:16/9!important;
    border-radius:22px!important;
    overflow:hidden!important;
  }

  .gen-v3-logo-canvas > .gen-logo-bg-preview img,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight img{
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    object-position:center center!important;
    transform:none!important;
  }

  .gen-v3-floating-edit{
    width:40px!important;
    height:40px!important;
    right:10px!important;
    bottom:10px!important;
    z-index:6!important;
  }

  .gen-v3-hero-info{
    width:100%!important;
    min-width:0!important;
    display:grid!important;
    justify-items:start!important;
    align-content:start!important;
    gap:0!important;
  }

  .gen-v3-title-row{
    width:100%!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto auto!important;
    align-items:center!important;
    gap:8px!important;
  }

  .gen-v3-hero-info h2{
    min-width:0!important;
    max-width:100%!important;
    font-size:26px!important;
    line-height:1!important;
    letter-spacing:-.065em!important;
    white-space:normal!important;
    overflow-wrap:anywhere!important;
  }

  .gen-v3-title-status{
    height:26px!important;
    padding:0 9px!important;
    font-size:10px!important;
    white-space:nowrap!important;
  }

  .gen-v3-title-edit{
    width:34px!important;
    height:34px!important;
    min-width:34px!important;
    padding:0!important;
  }

  .gen-v3-hero-info > p{
    margin:6px 0 0!important;
    font-size:13.5px!important;
    line-height:1.2!important;
  }

  .gen-v3-pill{
    height:24px!important;
    padding:0 10px!important;
    font-size:11.5px!important;
    margin-top:8px!important;
  }

  .gen-v3-url-line{
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    margin-top:12px!important;
    gap:8px!important;
  }

  .gen-v3-url-line strong{
    min-width:0!important;
    max-width:100%!important;
    display:block!important;
    font-size:12.5px!important;
    line-height:1.25!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
  }

  .gen-v3-hero-actions{
    width:100%!important;
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
    margin-top:12px!important;
  }

  .gen-v3-hero-actions button{
    width:100%!important;
    min-width:0!important;
    height:42px!important;
    border-radius:14px!important;
    justify-content:center!important;
    padding:0 10px!important;
    font-size:12.5px!important;
    white-space:nowrap!important;
  }

  .gen-v3-public-card{
    width:100%!important;
    min-height:0!important;
    border-radius:20px!important;
    padding:14px!important;
    display:grid!important;
    gap:10px!important;
    align-content:start!important;
  }

  .gen-v3-public-card div{
    gap:9px!important;
  }

  .gen-v3-public-card strong{
    font-size:13.5px!important;
  }

  .gen-v3-public-card p{
    font-size:12px!important;
    line-height:1.35!important;
  }

  .gen-v3-public-card button{
    width:100%!important;
    height:42px!important;
    border-radius:14px!important;
    font-size:13px!important;
  }

  .gen-v3-two-col{
    grid-template-columns:1fr!important;
    gap:14px!important;
  }

  .gen-v3-card{
    border-radius:22px!important;
    padding:18px!important;
    overflow:hidden!important;
  }

  .gen-v3-card-head{
    margin-bottom:14px!important;
  }

  .gen-v3-card-head h3{
    font-size:17px!important;
  }

  .gen-v3-card-head p{
    font-size:12px!important;
  }

  .gen-v3-info-list{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
    margin-bottom:12px!important;
  }

  .gen-v3-info-list > div{
    min-width:0!important;
    min-height:64px!important;
    border:1px solid rgba(226,232,240,.86)!important;
    border-radius:14px!important;
    background:#fff!important;
    padding:10px!important;
    display:grid!important;
    grid-template-columns:28px minmax(0,1fr) 16px!important;
    grid-template-rows:auto auto!important;
    gap:5px 8px!important;
    align-items:center!important;
  }

  .gen-v3-info-list > div > .gen-contact-icon{
    grid-row:1 / 3!important;
  }

  .gen-v3-info-list > div strong{
    grid-column:2!important;
    grid-row:1!important;
    font-size:11.5px!important;
    line-height:1.15!important;
  }

  .gen-v3-info-list > div span{
    grid-column:2!important;
    grid-row:2!important;
    font-size:11.5px!important;
    line-height:1.15!important;
    max-width:100%!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .gen-v3-info-list > div button{
    grid-column:3!important;
    grid-row:1 / 3!important;
    font-size:20px!important;
    align-self:center!important;
  }

  .gen-v3-contact-summary > .gen-v3-secondary-btn,
  .gen-v3-row-actions .gen-v3-secondary-btn{
    width:100%!important;
    justify-content:center!important;
  }

  .gen-v3-location-top{
    gap:10px!important;
    margin-bottom:12px!important;
  }

  .gen-v3-location-top strong{
    font-size:13px!important;
    line-height:1.22!important;
  }

  .gen-v3-location-top span{
    font-size:11.5px!important;
  }

  .gen-v3-map-preview{
    height:130px!important;
    border-radius:16px!important;
    margin-bottom:12px!important;
  }

  .gen-v3-row-actions{
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:8px!important;
  }

  .gen-v3-presentation-box{
    min-height:0!important;
    padding:14px!important;
  }

  .gen-v3-presentation-box p{
    font-size:13px!important;
    line-height:1.45!important;
  }

  .gen-v3-spa-art{
    display:none!important;
  }
}

@media (max-width: 430px){
  .gen-v3-hero-card{
    padding:14px!important;
    border-radius:22px!important;
  }

  .gen-v3-hero-info h2{
    font-size:24px!important;
  }

  .gen-v3-title-row{
    grid-template-columns:minmax(0,1fr) auto!important;
  }

  .gen-v3-title-status{
    grid-column:1 / 2!important;
    justify-self:start!important;
    margin-top:2px!important;
  }

  .gen-v3-title-edit{
    grid-column:2 / 3!important;
    grid-row:1 / 3!important;
  }

  .gen-v3-hero-actions{
    grid-template-columns:1fr!important;
  }

  .gen-v3-info-list{
    grid-template-columns:1fr!important;
  }

  .gen-v3-logo-wrap{
    border-radius:20px!important;
  }

  .gen-v3-logo-canvas,
  .gen-v3-logo-canvas > .gen-logo-bg-preview,
  .gen-v3-logo-canvas > .gen-logo-bg-preview.is-hero-tight{
    border-radius:20px!important;
  }
}



/* =========================================================
   LOOP GENERAL — MOBILE FIT FINAL POLISH
   Fixes:
   - hero/logo gets breathing room below mobile section selector
   - contact icons no longer overlap text on small widths
   - "Ver mi perfil" CTA becomes a normal full-width button on mobile
   ========================================================= */
@media (max-width: 900px){
  .gen-v3-page,
  .gen-page{
    padding-top:0!important;
    gap:16px!important;
  }

  .gen-v3-hero-card{
    margin-top:0!important;
    padding:16px!important;
    gap:16px!important;
    overflow:visible!important;
  }

  .gen-v3-public-card{
    position:relative!important;
    width:100%!important;
    min-height:0!important;
    margin:0!important;
    padding:14px!important;
    border-radius:20px!important;
    display:grid!important;
    gap:10px!important;
    align-content:start!important;
    overflow:hidden!important;
  }

  .gen-v3-public-card button,
  .gen-v3-public-card .gen-v3-primary-btn{
    position:static!important;
    inset:auto!important;
    transform:none!important;
    width:100%!important;
    height:42px!important;
    min-height:42px!important;
    border-radius:14px!important;
    margin:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    white-space:nowrap!important;
    font-size:13px!important;
    line-height:1!important;
  }

  .gen-v3-two-col{
    gap:14px!important;
  }

  .gen-v3-contact-summary{
    padding:18px!important;
  }

  .gen-v3-contact-summary .gen-v3-card-head{
    margin-bottom:14px!important;
  }

  .gen-v3-info-list{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:8px!important;
    margin:0 0 12px!important;
  }

  .gen-v3-info-list > div{
    min-width:0!important;
    min-height:72px!important;
    height:auto!important;
    padding:10px 8px!important;
    border:1px solid rgba(226,232,240,.92)!important;
    border-radius:14px!important;
    background:#fff!important;
    display:grid!important;
    grid-template-columns:1fr!important;
    grid-template-rows:28px auto!important;
    justify-items:center!important;
    align-items:center!important;
    gap:6px!important;
    position:relative!important;
    overflow:hidden!important;
  }

  .gen-v3-info-list > div > .gen-contact-icon{
    grid-column:1!important;
    grid-row:1!important;
    width:26px!important;
    height:26px!important;
    min-width:26px!important;
    margin:0!important;
    align-self:end!important;
    justify-self:center!important;
    position:static!important;
  }

  .gen-v3-info-list > div > .gen-contact-icon img{
    width:26px!important;
    height:26px!important;
    object-fit:contain!important;
    display:block!important;
  }

  .gen-v3-info-list > div strong{
    display:none!important;
  }

  .gen-v3-info-list > div span{
    grid-column:1!important;
    grid-row:2!important;
    min-width:0!important;
    max-width:100%!important;
    display:block!important;
    color:#475467!important;
    font-size:12px!important;
    line-height:1.15!important;
    font-weight:850!important;
    text-align:center!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    padding:0 2px!important;
  }

  .gen-v3-info-list > div button{
    display:none!important;
  }

  .gen-v3-contact-summary > .gen-v3-secondary-btn{
    width:100%!important;
    height:40px!important;
    min-height:40px!important;
    justify-content:center!important;
    border-radius:14px!important;
  }
}

@media (max-width: 390px){
  .gen-v3-info-list{
    grid-template-columns:1fr!important;
  }

  .gen-v3-info-list > div{
    min-height:62px!important;
    grid-template-columns:30px minmax(0,1fr)!important;
    grid-template-rows:1fr!important;
    justify-items:start!important;
    gap:8px!important;
  }

  .gen-v3-info-list > div > .gen-contact-icon{
    grid-column:1!important;
    grid-row:1!important;
    justify-self:center!important;
    align-self:center!important;
  }

  .gen-v3-info-list > div span{
    grid-column:2!important;
    grid-row:1!important;
    text-align:left!important;
  }
}



/* =========================================================
   LOOP GENERAL — MOBILE CONTACT CENTERING + HERO SAFE FINAL
   - Contact public values/icons are perfectly centered in their tiles.
   - Hero card has a safe top position and does not tuck under the mobile selector.
   ========================================================= */
@media (max-width: 900px){
  .gen-v3-page,
  .gen-page{
    padding-top:0!important;
    margin-top:0!important;
    gap:16px!important;
    overflow:visible!important;
  }

  .gen-v3-hero-card{
    margin-top:0!important;
    padding:16px!important;
    gap:16px!important;
    overflow:visible!important;
    position:relative!important;
    z-index:1!important;
  }

  .gen-v3-contact-summary{
    padding:18px 14px!important;
  }

  .gen-v3-contact-summary .gen-v3-card-head{
    margin-bottom:14px!important;
    text-align:left!important;
  }

  .gen-v3-info-list{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:8px!important;
    width:100%!important;
    margin:0 0 12px!important;
    align-items:stretch!important;
    justify-items:stretch!important;
  }

  .gen-v3-info-list > div{
    min-width:0!important;
    width:100%!important;
    min-height:64px!important;
    height:64px!important;
    padding:9px 8px!important;
    border:1px solid rgba(226,232,240,.92)!important;
    border-radius:14px!important;
    background:#fff!important;
    display:flex!important;
    flex-direction:column!important;
    justify-content:center!important;
    align-items:center!important;
    gap:5px!important;
    position:relative!important;
    overflow:hidden!important;
    text-align:center!important;
  }

  .gen-v3-info-list > div > .gen-contact-icon{
    width:22px!important;
    height:22px!important;
    min-width:22px!important;
    max-width:22px!important;
    min-height:22px!important;
    max-height:22px!important;
    margin:0!important;
    padding:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    position:static!important;
    flex:0 0 auto!important;
  }

  .gen-v3-info-list > div > .gen-contact-icon img{
    width:22px!important;
    height:22px!important;
    max-width:22px!important;
    max-height:22px!important;
    object-fit:contain!important;
    display:block!important;
    margin:0!important;
  }

  .gen-v3-info-list > div strong{
    display:none!important;
  }

  .gen-v3-info-list > div span{
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    display:block!important;
    color:#475467!important;
    font-size:12px!important;
    line-height:1.12!important;
    font-weight:850!important;
    text-align:center!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    padding:0 2px!important;
    margin:0!important;
    flex:0 1 auto!important;
  }

  .gen-v3-info-list > div button{
    display:none!important;
  }

  .gen-v3-contact-summary > .gen-v3-secondary-btn{
    width:100%!important;
    height:40px!important;
    min-height:40px!important;
    justify-content:center!important;
    border-radius:14px!important;
    margin-top:2px!important;
  }
}

@media (max-width: 390px){
  .gen-v3-info-list{
    grid-template-columns:1fr!important;
  }

  .gen-v3-info-list > div{
    height:58px!important;
    min-height:58px!important;
    flex-direction:row!important;
    justify-content:center!important;
    align-items:center!important;
    gap:10px!important;
  }

  .gen-v3-info-list > div span{
    width:auto!important;
    max-width:calc(100% - 42px)!important;
    text-align:left!important;
  }
}


  /* =========================================================
     LOOP GENERAL CONTACT MODAL V4.3 — smaller no-empty-space modal
     ========================================================= */
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio){
    width:min(520px,calc(100vw - 36px))!important;
    max-height:calc(100dvh - 76px)!important;
    min-height:0!important;
    height:auto!important;
    border-radius:26px!important;
    overflow:hidden!important;
    display:block!important;
  }
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-head{
    padding:16px 20px 12px!important;
    min-height:0!important;
  }
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-head h3{
    font-size:21px!important;
    line-height:1!important;
    letter-spacing:-.055em!important;
  }
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-head p{
    margin-top:5px!important;
    font-size:12px!important;
    line-height:1.25!important;
  }
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-head>button{
    width:34px!important;
    height:34px!important;
    font-size:22px!important;
  }
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-body{
    padding:13px 18px 15px!important;
    overflow:visible!important;
    display:block!important;
    flex:0 0 auto!important;
    min-height:0!important;
  }
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-footer{
    padding:12px 18px 16px!important;
    min-height:0!important;
  }
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-footer button,
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-footer .gen-v3-primary-btn,
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio) .gen-v3-sheet-footer .gen-v3-secondary-btn{
    height:40px!important;
    min-height:40px!important;
    border-radius:13px!important;
    padding:0 16px!important;
    font-size:12px!important;
  }
  .gen-contact-studio{
    display:grid!important;
    gap:12px!important;
    align-content:start!important;
    min-height:0!important;
  }
  .gen-contact-phone-grid,
  .gen-contact-link-grid{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:10px!important;
    align-items:start!important;
  }
  .gen-contact-phone-kit.loop-phone-input{
    gap:5px!important;
    min-width:0!important;
  }
  .gen-contact-phone-kit .loop-phone-label,
  .gen-contact-pro-field>span{
    color:#334155!important;
    font-size:11px!important;
    line-height:1.1!important;
    font-weight:950!important;
    letter-spacing:-.01em!important;
  }
  .gen-contact-phone-kit .loop-phone-control{
    min-height:42px!important;
    border-radius:15px!important;
    box-shadow:none!important;
  }
  .gen-contact-phone-kit .loop-phone-country{
    height:40px!important;
    min-width:86px!important;
    border-radius:14px 0 0 14px!important;
    gap:6px!important;
    padding:0 9px!important;
  }
  .gen-contact-phone-kit .loop-phone-flag{
    width:19px!important;
    height:14px!important;
  }
  .gen-contact-phone-kit .loop-phone-selected-dial{
    font-size:12px!important;
  }
  .gen-contact-phone-kit .loop-phone-control input{
    height:40px!important;
    font-size:13px!important;
    font-weight:850!important;
    padding:0 11px!important;
  }
  .gen-contact-phone-kit .loop-phone-hint{
    display:none!important;
  }
  .gen-contact-pro-field{
    display:grid!important;
    gap:5px!important;
    min-width:0!important;
  }
  .gen-contact-prefixed-input{
    height:42px!important;
    border:1px solid #dbe5ee!important;
    background:#fff!important;
    border-radius:15px!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr)!important;
    align-items:center!important;
    overflow:hidden!important;
    box-shadow:none!important;
  }
  .gen-contact-prefixed-input b{
    height:40px!important;
    min-width:40px!important;
    padding:0 10px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:#f8fafc!important;
    border-right:1px solid #e2e8f0!important;
    color:#00675f!important;
    font-size:12px!important;
    font-weight:950!important;
    font-style:normal!important;
  }
  .gen-contact-prefixed-input.is-web b{color:#475569!important;}
  .gen-contact-prefixed-input input{
    height:40px!important;
    border:0!important;
    background:#fff!important;
    border-radius:0!important;
    padding:0 11px!important;
    color:#0f172a!important;
    font-size:13px!important;
    font-weight:850!important;
    outline:0!important;
    box-shadow:none!important;
    min-width:0!important;
  }
  .gen-contact-prefixed-input input::placeholder{
    color:#94a3b8!important;
    font-weight:760!important;
  }
  .gen-contact-pro-field em{display:none!important;}
  .gen-contact-status{
    border-radius:14px!important;
    padding:9px 10px!important;
    font-size:11.5px!important;
    line-height:1.25!important;
    font-weight:880!important;
  }
  .gen-contact-status.is-ok{border:1px solid #99f6e4;background:#f0fdfa;color:#0f766e;}
  .gen-contact-status.is-error{border:1px solid #fecaca;background:#fff1f2;color:#be123c;}
  @media(max-width:720px){
    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio){
      width:min(100%,calc(100vw - 18px))!important;
      max-height:88vh!important;
      border-radius:24px!important;
    }
    .gen-contact-phone-grid,.gen-contact-link-grid{grid-template-columns:1fr!important;}
    .gen-contact-phone-kit .loop-phone-country{min-width:86px!important;}
  }


  /* =========================================================
     LOOP GENERAL — PROFILE HEADER ACTIONS V5
     Name first, then Visible / Ver perfil / edit on one row.
     ========================================================= */
  .gen-v3-profile-head{
    width:100%;
    display:grid;
    gap:9px;
    min-width:0;
    align-content:start;
    justify-items:start;
  }

  .gen-v3-profile-head h2{
    margin:0!important;
    max-width:100%;
    min-width:0;
    overflow:hidden;
    text-overflow:ellipsis;
  }

  .gen-v3-profile-actions-row{
    display:flex;
    align-items:center;
    flex-wrap:wrap;
    gap:8px;
    min-width:0;
  }

  .gen-v3-profile-preview-btn{
    height:28px!important;
    min-height:28px!important;
    border-radius:999px!important;
    border:1px solid rgba(0,103,95,.16)!important;
    background:rgba(0,103,95,.075)!important;
    color:#00675f!important;
    box-shadow:none!important;
    padding:0 11px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    font-size:11px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:-.01em!important;
    white-space:nowrap!important;
    cursor:pointer!important;
  }

  .gen-v3-profile-preview-btn:hover{
    background:rgba(0,103,95,.11)!important;
    border-color:rgba(0,103,95,.24)!important;
  }

  @media(max-width:760px){
    .gen-v3-profile-head{
      gap:8px!important;
    }

    .gen-v3-profile-actions-row{
      width:100%!important;
      gap:7px!important;
      margin-top:0!important;
    }

    .gen-v3-profile-actions-row .gen-v3-title-status{
      height:28px!important;
      min-height:28px!important;
      padding:0 10px!important;
      font-size:11px!important;
    }

    .gen-v3-profile-actions-row .gen-v3-title-edit{
      width:30px!important;
      height:30px!important;
      min-width:30px!important;
      min-height:30px!important;
      margin-left:0!important;
      position:static!important;
      transform:none!important;
    }

    .gen-v3-profile-actions-row .gen-v3-title-edit .gen-v3-icon,
    .gen-v3-profile-actions-row .gen-v3-title-edit svg{
      width:14px!important;
      height:14px!important;
    }

    .gen-v3-public-card,
    .gen-v3-public-card.gen-v3-public-card-desktop{
      display:none!important;
    }

    .gen-v3-title-row{
      padding-right:0!important;
    }
  }

  @media(min-width:761px){
    .gen-v3-profile-actions-row .gen-v3-profile-preview-btn{
      display:none!important;
    }
  }


  /* =========================================================
     LOOP GENERAL — CONTACT PUBLIC MOBILE V2
     Compact 2x2 contact dashboard with visible labels and top edit action.
     ========================================================= */
  .gen-v3-contact-summary-v2{
    padding:20px!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-head-v2{
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:12px!important;
    margin-bottom:14px!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-head-v2 h3{
    margin:0!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-head-v2 p{
    margin-top:3px!important;
  }

  .gen-v3-contact-edit-mini{
    flex:0 0 auto!important;
    height:32px!important;
    min-height:32px!important;
    border-radius:999px!important;
    border:1px solid rgba(0,103,95,.15)!important;
    background:rgba(0,103,95,.065)!important;
    color:#00675f!important;
    box-shadow:none!important;
    padding:0 11px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:6px!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:900!important;
    letter-spacing:-.01em!important;
    white-space:nowrap!important;
    cursor:pointer!important;
  }

  .gen-v3-contact-edit-mini .gen-v3-icon{
    width:22px!important;
    height:22px!important;
    min-width:22px!important;
    background:rgba(0,103,95,.09)!important;
  }

  .gen-v3-contact-edit-mini svg{
    width:12px!important;
    height:12px!important;
  }

  .gen-v3-contact-edit-mini:hover{
    background:rgba(0,103,95,.1)!important;
    border-color:rgba(0,103,95,.24)!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2,
  .gen-v3-contact-grid-v2{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:10px!important;
    width:100%!important;
    margin:0!important;
    padding:0!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2 > .gen-v3-contact-tile,
  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile{
    min-width:0!important;
    width:100%!important;
    min-height:78px!important;
    height:auto!important;
    padding:12px!important;
    border:1px solid rgba(226,232,240,.95)!important;
    border-radius:18px!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdfd 100%)!important;
    box-shadow:0 8px 22px rgba(15,23,42,.035)!important;
    display:grid!important;
    grid-template-columns:34px minmax(0,1fr)!important;
    grid-template-rows:auto auto!important;
    gap:4px 10px!important;
    align-items:center!important;
    justify-items:start!important;
    text-align:left!important;
    cursor:pointer!important;
    outline:0!important;
    overflow:hidden!important;
    transition:transform 120ms ease, border-color 120ms ease, background 120ms ease!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile:hover,
  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile:focus-visible{
    border-color:rgba(0,103,95,.24)!important;
    background:linear-gradient(180deg,#ffffff 0%,#f7fffd 100%)!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile:active{
    transform:scale(.985)!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon{
    grid-column:1!important;
    grid-row:1 / 3!important;
    width:30px!important;
    height:30px!important;
    min-width:30px!important;
    max-width:30px!important;
    align-self:center!important;
    justify-self:center!important;
    margin:0!important;
    padding:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    position:static!important;
    flex:none!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon img{
    width:28px!important;
    height:28px!important;
    max-width:28px!important;
    max-height:28px!important;
    object-fit:contain!important;
    display:block!important;
    margin:0!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > strong{
    grid-column:2!important;
    grid-row:1!important;
    display:block!important;
    width:100%!important;
    min-width:0!important;
    color:#0f172a!important;
    font-size:12.5px!important;
    line-height:1.05!important;
    font-weight:900!important;
    letter-spacing:-.015em!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    margin:0!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span{
    grid-column:2!important;
    grid-row:2!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    display:block!important;
    color:#475467!important;
    font-size:12px!important;
    line-height:1.1!important;
    font-weight:800!important;
    text-align:left!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    padding:0!important;
    margin:0!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span.is-pending{
    color:#98a2b3!important;
    font-weight:850!important;
  }

  .gen-v3-contact-summary-v2 > .gen-v3-secondary-btn{
    display:none!important;
  }

  @media(max-width:900px){
    .gen-v3-contact-summary-v2{
      padding:18px 14px!important;
    }

    .gen-v3-contact-summary-v2 .gen-v3-contact-head-v2{
      margin-bottom:12px!important;
      align-items:center!important;
    }

    .gen-v3-contact-summary-v2 .gen-v3-contact-head-v2 p{
      display:none!important;
    }

    .gen-v3-contact-edit-mini{
      height:30px!important;
      min-height:30px!important;
      padding:0 10px!important;
      font-size:11.5px!important;
    }

    .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2,
    .gen-v3-contact-grid-v2{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:8px!important;
      margin:0!important;
    }

    .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2 > .gen-v3-contact-tile,
    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile{
      min-height:74px!important;
      height:auto!important;
      padding:10px!important;
      border-radius:16px!important;
      grid-template-columns:30px minmax(0,1fr)!important;
      gap:4px 8px!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon,
    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon img{
      width:26px!important;
      height:26px!important;
      min-width:26px!important;
      max-width:26px!important;
      max-height:26px!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > strong{
      display:block!important;
      font-size:11.5px!important;
      text-align:left!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span{
      display:block!important;
      font-size:11.5px!important;
      text-align:left!important;
      max-width:100%!important;
    }
  }

  @media(max-width:390px){
    .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2,
    .gen-v3-contact-grid-v2{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:7px!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile{
      min-height:70px!important;
      padding:9px 8px!important;
      grid-template-columns:26px minmax(0,1fr)!important;
      gap:3px 7px!important;
      flex-direction:initial!important;
      justify-content:initial!important;
      align-items:center!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon,
    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon img{
      width:24px!important;
      height:24px!important;
      min-width:24px!important;
      max-width:24px!important;
      max-height:24px!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > strong,
    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span{
      font-size:10.8px!important;
      width:100%!important;
      max-width:100%!important;
      text-align:left!important;
    }
  }


  /* =========================================================
     LOOP GENERAL — CONTACT PUBLIC MOBILE V3 COMPACT ROW
     Smaller bubbles + value centered beside the icon.
     ========================================================= */
  .gen-v3-contact-summary-v2{
    padding:17px 16px!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-head-v2{
    margin-bottom:10px!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2,
  .gen-v3-contact-grid-v2{
    gap:8px!important;
  }

  .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2 > .gen-v3-contact-tile,
  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile{
    min-height:58px!important;
    height:58px!important;
    padding:8px 10px!important;
    border-radius:15px!important;
    display:flex!important;
    flex-direction:row!important;
    align-items:center!important;
    justify-content:center!important;
    gap:9px!important;
    text-align:left!important;
    grid-template-columns:none!important;
    grid-template-rows:none!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon{
    grid-column:auto!important;
    grid-row:auto!important;
    width:25px!important;
    height:25px!important;
    min-width:25px!important;
    max-width:25px!important;
    flex:0 0 25px!important;
    align-self:center!important;
    justify-self:auto!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon img{
    width:25px!important;
    height:25px!important;
    min-width:25px!important;
    max-width:25px!important;
    max-height:25px!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > strong{
    display:none!important;
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span{
    grid-column:auto!important;
    grid-row:auto!important;
    flex:0 1 auto!important;
    width:auto!important;
    max-width:calc(100% - 34px)!important;
    min-width:0!important;
    display:block!important;
    align-self:center!important;
    text-align:left!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:900!important;
    letter-spacing:-.015em!important;
    margin:0!important;
    transform:translateY(.5px);
  }

  .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span.is-pending{
    color:#98a2b3!important;
    font-weight:900!important;
  }

  @media(max-width:900px){
    .gen-v3-contact-summary-v2{
      padding:16px 12px!important;
    }

    .gen-v3-contact-edit-mini{
      height:29px!important;
      min-height:29px!important;
      padding:0 9px!important;
    }

    .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2,
    .gen-v3-contact-grid-v2{
      gap:7px!important;
    }

    .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2 > .gen-v3-contact-tile,
    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile{
      min-height:54px!important;
      height:54px!important;
      padding:7px 8px!important;
      border-radius:14px!important;
      gap:7px!important;
      display:flex!important;
      align-items:center!important;
      justify-content:center!important;
      grid-template-columns:none!important;
      grid-template-rows:none!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon,
    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon img{
      width:23px!important;
      height:23px!important;
      min-width:23px!important;
      max-width:23px!important;
      max-height:23px!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span{
      font-size:11.5px!important;
      line-height:1!important;
      max-width:calc(100% - 30px)!important;
      text-align:left!important;
    }
  }

  @media(max-width:390px){
    .gen-v3-contact-summary-v2{
      padding:15px 11px!important;
    }

    .gen-v3-contact-summary-v2 .gen-v3-contact-grid-v2,
    .gen-v3-contact-grid-v2{
      gap:7px!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile{
      min-height:50px!important;
      height:50px!important;
      padding:7px 7px!important;
      border-radius:13px!important;
      gap:6px!important;
      display:flex!important;
      align-items:center!important;
      justify-content:center!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon,
    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > .gen-contact-icon img{
      width:21px!important;
      height:21px!important;
      min-width:21px!important;
      max-width:21px!important;
      max-height:21px!important;
    }

    .gen-v3-contact-grid-v2 > .gen-v3-contact-tile > span{
      font-size:10.7px!important;
      max-width:calc(100% - 27px)!important;
      text-align:left!important;
    }
  }


  /* =========================================================
     LOOP GENERAL CONTACT PUBLIC POLISH V4
     Desktop/tablet: compact 2x2 channel cards.
     Mobile: one-column touch-friendly contact list.
     Contact and location cards keep their own natural heights.
     ========================================================= */
  .gen-v3-two-col{
    align-items:start!important;
  }

  .gen-v3-contact-summary-v4{
    align-self:start!important;
    height:auto!important;
    min-height:0!important;
    padding:18px!important;
    overflow:visible!important;
  }

  .gen-v3-contact-summary-v4 .gen-v3-contact-head-v4{
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:14px!important;
    margin:0 0 12px!important;
  }

  .gen-v3-contact-summary-v4 .gen-v3-contact-head-v4 > div{
    min-width:0!important;
  }

  .gen-v3-contact-summary-v4 .gen-v3-contact-head-v4 h3{
    margin:0!important;
  }

  .gen-v3-contact-summary-v4 .gen-v3-contact-head-v4 p{
    margin:4px 0 0!important;
  }

  .gen-v3-contact-edit-v4{
    flex:0 0 auto!important;
    min-width:86px!important;
    height:38px!important;
    min-height:38px!important;
    padding:0 12px!important;
    border:1px solid rgba(0,103,95,.16)!important;
    border-radius:999px!important;
    background:rgba(0,103,95,.06)!important;
    color:#00675f!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:6px!important;
    font:inherit!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:900!important;
    cursor:pointer!important;
    box-shadow:none!important;
  }

  .gen-v3-contact-edit-v4:hover,
  .gen-v3-contact-edit-v4:focus-visible{
    border-color:rgba(0,103,95,.28)!important;
    background:rgba(0,103,95,.10)!important;
    outline:none!important;
  }

  .gen-v3-contact-edit-v4 .gen-v3-icon{
    width:22px!important;
    height:22px!important;
    min-width:22px!important;
    border-radius:999px!important;
    background:rgba(0,103,95,.09)!important;
  }

  .gen-v3-contact-edit-v4 .gen-v3-icon svg{
    width:12px!important;
    height:12px!important;
  }

  .gen-v3-contact-grid-v4{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:10px!important;
    width:100%!important;
    margin:0!important;
    padding:0!important;
  }

  .gen-v3-contact-tile-v4{
    appearance:none!important;
    -webkit-appearance:none!important;
    width:100%!important;
    min-width:0!important;
    min-height:78px!important;
    margin:0!important;
    padding:11px 12px!important;
    border:1px solid rgba(226,232,240,.96)!important;
    border-radius:16px!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdfd 100%)!important;
    box-shadow:0 7px 18px rgba(15,23,42,.035)!important;
    color:#0f172a!important;
    display:grid!important;
    grid-template-columns:36px minmax(0,1fr) 24px!important;
    align-items:center!important;
    gap:10px!important;
    text-align:left!important;
    cursor:pointer!important;
    overflow:hidden!important;
    transition:border-color 120ms ease,background 120ms ease,box-shadow 120ms ease,transform 120ms ease!important;
  }

  .gen-v3-contact-tile-v4:hover,
  .gen-v3-contact-tile-v4:focus-visible{
    border-color:rgba(0,103,95,.25)!important;
    background:linear-gradient(180deg,#ffffff 0%,#f5fffc 100%)!important;
    box-shadow:0 10px 24px rgba(15,23,42,.06)!important;
    outline:none!important;
  }

  .gen-v3-contact-tile-v4:active{
    transform:scale(.988)!important;
  }

  .gen-v3-contact-tile-v4 > .gen-contact-icon{
    position:static!important;
    grid-column:1!important;
    width:32px!important;
    height:32px!important;
    min-width:32px!important;
    max-width:32px!important;
    margin:0!important;
    padding:0!important;
    border-radius:10px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:rgba(0,103,95,.045)!important;
    overflow:hidden!important;
  }

  .gen-v3-contact-tile-v4 > .gen-contact-icon img{
    width:26px!important;
    height:26px!important;
    min-width:26px!important;
    max-width:26px!important;
    max-height:26px!important;
    display:block!important;
    object-fit:contain!important;
    margin:0!important;
  }

  .gen-v3-contact-copy-v4{
    grid-column:2!important;
    min-width:0!important;
    display:grid!important;
    gap:2px!important;
    margin:0!important;
    padding:0!important;
  }

  .gen-v3-contact-copy-v4 > strong{
    min-width:0!important;
    margin:0!important;
    color:#0f172a!important;
    font-size:12.5px!important;
    line-height:1.05!important;
    font-weight:900!important;
    letter-spacing:-.015em!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .gen-v3-contact-copy-v4 > span{
    min-width:0!important;
    width:100%!important;
    max-width:100%!important;
    margin:0!important;
    padding:0!important;
    color:#344054!important;
    font-size:12px!important;
    line-height:1.15!important;
    font-weight:800!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .gen-v3-contact-copy-v4 > small{
    min-width:0!important;
    margin:0!important;
    color:#007c70!important;
    font-size:10.5px!important;
    line-height:1.05!important;
    font-weight:850!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .gen-v3-contact-tile-v4.is-empty{
    background:#fcfcfd!important;
    box-shadow:none!important;
  }

  .gen-v3-contact-tile-v4.is-empty .gen-v3-contact-copy-v4 > span{
    color:#667085!important;
  }

  .gen-v3-contact-tile-v4.is-empty .gen-v3-contact-copy-v4 > small{
    color:#98a2b3!important;
  }

  .gen-v3-contact-action-v4{
    grid-column:3!important;
    width:22px!important;
    height:22px!important;
    min-width:22px!important;
    margin:0!important;
    padding:0!important;
    border-radius:999px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    justify-self:end!important;
    color:#00675f!important;
    background:rgba(0,103,95,.08)!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:950!important;
  }

  .gen-v3-contact-tile-v4.is-empty .gen-v3-contact-action-v4{
    color:#667085!important;
    background:#f2f4f7!important;
    font-size:18px!important;
    font-weight:700!important;
  }

  @media(max-width:900px){
    .gen-v3-contact-summary-v4{
      padding:16px!important;
    }

    .gen-v3-contact-summary-v4 .gen-v3-contact-head-v4{
      margin-bottom:10px!important;
      align-items:center!important;
    }

    .gen-v3-contact-grid-v4{
      gap:8px!important;
    }

    .gen-v3-contact-tile-v4{
      min-height:74px!important;
      padding:10px!important;
      grid-template-columns:34px minmax(0,1fr) 22px!important;
      gap:8px!important;
      border-radius:15px!important;
    }

    .gen-v3-contact-edit-v4{
      height:40px!important;
      min-height:40px!important;
    }
  }

  @media(max-width:720px){
    .gen-v3-contact-summary-v4{
      padding:16px 14px!important;
      border-radius:22px!important;
    }

    .gen-v3-contact-summary-v4 .gen-v3-contact-head-v4{
      gap:10px!important;
      margin-bottom:12px!important;
    }

    .gen-v3-contact-summary-v4 .gen-v3-contact-head-v4 p{
      display:block!important;
      font-size:11.5px!important;
      line-height:1.25!important;
    }

    .gen-v3-contact-edit-v4{
      min-width:78px!important;
      height:44px!important;
      min-height:44px!important;
      padding:0 10px!important;
      font-size:11.5px!important;
    }

    .gen-v3-contact-grid-v4{
      grid-template-columns:1fr!important;
      gap:8px!important;
    }

    .gen-v3-contact-tile-v4{
      min-height:68px!important;
      height:auto!important;
      padding:10px 11px!important;
      grid-template-columns:38px minmax(0,1fr) 28px!important;
      gap:10px!important;
      border-radius:15px!important;
      box-shadow:0 5px 14px rgba(15,23,42,.03)!important;
    }

    .gen-v3-contact-tile-v4 > .gen-contact-icon{
      width:34px!important;
      height:34px!important;
      min-width:34px!important;
      max-width:34px!important;
      border-radius:11px!important;
    }

    .gen-v3-contact-tile-v4 > .gen-contact-icon img{
      width:28px!important;
      height:28px!important;
      min-width:28px!important;
      max-width:28px!important;
      max-height:28px!important;
    }

    .gen-v3-contact-copy-v4 > strong{
      font-size:12.5px!important;
    }

    .gen-v3-contact-copy-v4 > span{
      font-size:12px!important;
    }

    .gen-v3-contact-copy-v4 > small{
      font-size:10.5px!important;
    }

    .gen-v3-contact-action-v4{
      width:26px!important;
      height:26px!important;
      min-width:26px!important;
    }
  }

  @media(max-width:390px){
    .gen-v3-contact-summary-v4{
      padding:14px 12px!important;
    }

    .gen-v3-contact-head-v4 h3{
      font-size:16px!important;
    }

    .gen-v3-contact-head-v4 p{
      max-width:180px!important;
    }

    .gen-v3-contact-edit-v4{
      min-width:72px!important;
      padding:0 9px!important;
    }

    .gen-v3-contact-tile-v4{
      min-height:66px!important;
      padding:9px 10px!important;
      grid-template-columns:34px minmax(0,1fr) 26px!important;
      gap:8px!important;
    }

    .gen-v3-contact-tile-v4 > .gen-contact-icon{
      width:32px!important;
      height:32px!important;
      min-width:32px!important;
      max-width:32px!important;
    }

    .gen-v3-contact-tile-v4 > .gen-contact-icon img{
      width:26px!important;
      height:26px!important;
      min-width:26px!important;
      max-width:26px!important;
      max-height:26px!important;
    }

    .gen-v3-contact-copy-v4 > span{
      font-size:11.5px!important;
    }
  }


  /* =========================================================
     LOOP GENERAL CONTACT MODAL PREMIUM V1
     Grouped contact editor with one responsive contract for
     desktop, tablet and mobile.
     ========================================================= */
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5){
    width:min(620px,calc(100vw - 32px))!important;
    max-height:min(760px,calc(100dvh - 42px))!important;
    border-radius:28px!important;
    border:1px solid rgba(216,226,235,.96)!important;
    background:#ffffff!important;
    box-shadow:0 28px 90px rgba(15,23,42,.22)!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head{
    padding:20px 22px 16px!important;
    border-bottom:1px solid #e8eef3!important;
    background:
      radial-gradient(circle at 4% 0%,rgba(20,184,166,.085),transparent 34%),
      linear-gradient(180deg,#ffffff 0%,#fbfdfd 100%)!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head h3{
    margin:0!important;
    font-size:22px!important;
    line-height:1.05!important;
    letter-spacing:-.045em!important;
    color:#0f172a!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head p{
    max-width:470px!important;
    margin:6px 0 0!important;
    color:#64748b!important;
    font-size:12.5px!important;
    line-height:1.35!important;
    font-weight:720!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head>button{
    width:36px!important;
    height:36px!important;
    min-width:36px!important;
    border-radius:999px!important;
    background:#f8fafc!important;
    border:1px solid #e2e8f0!important;
    color:#334155!important;
    font-size:21px!important;
    line-height:1!important;
    transition:background 140ms ease,border-color 140ms ease,transform 140ms ease!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head>button:hover{
    background:#f1f5f9!important;
    border-color:#cbd5e1!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head>button:active{
    transform:scale(.96)!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-body{
    padding:16px 18px 18px!important;
    overflow:auto!important;
    overscroll-behavior:contain!important;
    -webkit-overflow-scrolling:touch!important;
    background:#ffffff!important;
  }

  .gen-contact-studio-v5{
    display:grid!important;
    gap:12px!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group{
    min-width:0!important;
    display:grid!important;
    gap:13px!important;
    padding:14px!important;
    border:1px solid #e3ebf1!important;
    border-radius:19px!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdfd 100%)!important;
    box-shadow:0 7px 18px rgba(15,23,42,.035)!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group-head{
    min-width:0!important;
    display:flex!important;
    align-items:center!important;
    gap:10px!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group-icon{
    width:34px!important;
    height:34px!important;
    min-width:34px!important;
    border-radius:11px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:#ecfdf8!important;
    color:#00796f!important;
    border:1px solid rgba(20,184,166,.18)!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group-icon .gen-v3-icon{
    width:16px!important;
    height:16px!important;
    min-width:16px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:transparent!important;
    border:0!important;
    box-shadow:none!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group-icon svg{
    width:16px!important;
    height:16px!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group-head>div{
    min-width:0!important;
    display:grid!important;
    gap:2px!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group-head strong{
    color:#0f172a!important;
    font-size:12.5px!important;
    line-height:1.15!important;
    font-weight:950!important;
    letter-spacing:-.018em!important;
  }

  .gen-contact-studio-v5 .gen-contact-editor-group-head small{
    color:#64748b!important;
    font-size:10.5px!important;
    line-height:1.28!important;
    font-weight:700!important;
  }

  .gen-contact-studio-v5 .gen-contact-phone-grid,
  .gen-contact-studio-v5 .gen-contact-link-grid{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) minmax(0,1fr)!important;
    gap:10px!important;
    align-items:start!important;
  }

  .gen-contact-studio-v5 .gen-contact-phone-kit.loop-phone-input,
  .gen-contact-studio-v5 .gen-contact-pro-field{
    min-width:0!important;
    gap:6px!important;
  }

  .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-label,
  .gen-contact-studio-v5 .gen-contact-pro-field>span{
    color:#334155!important;
    font-size:11px!important;
    line-height:1.1!important;
    font-weight:900!important;
    letter-spacing:-.01em!important;
  }

  .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-control,
  .gen-contact-studio-v5 .gen-contact-prefixed-input{
    min-height:46px!important;
    height:46px!important;
    border:1px solid #d9e4eb!important;
    border-radius:14px!important;
    background:#ffffff!important;
    box-shadow:0 1px 0 rgba(15,23,42,.02)!important;
    transition:border-color 140ms ease,box-shadow 140ms ease,background 140ms ease!important;
  }

  .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-control:focus-within,
  .gen-contact-studio-v5 .gen-contact-prefixed-input:focus-within{
    border-color:rgba(0,121,111,.62)!important;
    box-shadow:0 0 0 3px rgba(20,184,166,.10)!important;
    background:#ffffff!important;
  }

  .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-country{
    min-width:92px!important;
    height:44px!important;
    padding:0 10px!important;
    border-radius:13px 0 0 13px!important;
    background:#f8fafc!important;
  }

  .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-control input{
    height:44px!important;
    min-width:0!important;
    padding:0 11px!important;
    color:#0f172a!important;
    font-size:13px!important;
    font-weight:820!important;
  }

  .gen-contact-studio-v5 .gen-contact-prefixed-input{
    display:grid!important;
    grid-template-columns:44px minmax(0,1fr)!important;
    overflow:hidden!important;
  }

  .gen-contact-studio-v5 .gen-contact-prefixed-input b{
    width:44px!important;
    min-width:44px!important;
    height:44px!important;
    padding:0!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-right:1px solid #e2e8f0!important;
    background:#f8fafc!important;
    color:#00796f!important;
    font-size:13px!important;
    font-weight:950!important;
  }

  .gen-contact-studio-v5 .gen-contact-prefixed-input.is-web b{
    color:#475569!important;
  }

  .gen-contact-studio-v5 .gen-contact-prefixed-input input{
    width:100%!important;
    min-width:0!important;
    height:44px!important;
    padding:0 12px!important;
    border:0!important;
    outline:0!important;
    background:transparent!important;
    color:#0f172a!important;
    font-size:13px!important;
    font-weight:820!important;
    box-shadow:none!important;
  }

  .gen-contact-studio-v5 .gen-contact-prefixed-input input::placeholder,
  .gen-contact-studio-v5 .gen-contact-phone-kit input::placeholder{
    color:#94a3b8!important;
    font-weight:680!important;
  }

  .gen-contact-studio-v5 .gen-contact-status{
    margin:0!important;
    padding:10px 12px!important;
    border-radius:14px!important;
    font-size:11.5px!important;
    line-height:1.3!important;
    font-weight:820!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-footer{
    min-height:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:flex-end!important;
    gap:9px!important;
    padding:13px 18px 16px!important;
    border-top:1px solid #e8eef3!important;
    background:#fbfdfd!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-footer .gen-v3-secondary-btn,
  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-footer .gen-v3-primary-btn{
    height:44px!important;
    min-height:44px!important;
    padding:0 17px!important;
    border-radius:13px!important;
    font-size:12px!important;
    font-weight:900!important;
  }

  .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-footer .gen-v3-primary-btn{
    min-width:142px!important;
    box-shadow:0 8px 18px rgba(0,103,95,.15)!important;
  }

  @media(max-width:720px){
    .gen-v3-modal.gen-v3-modal-workspace:has(.gen-contact-studio-v5){
      padding:8px!important;
      align-items:center!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5){
      width:calc(100vw - 16px)!important;
      max-width:none!important;
      max-height:calc(100dvh - 16px)!important;
      border-radius:23px!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head{
      padding:17px 16px 13px!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head h3{
      font-size:20px!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head p{
      max-width:calc(100% - 38px)!important;
      font-size:11.5px!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-body{
      padding:12px!important;
    }

    .gen-contact-studio-v5{
      gap:10px!important;
    }

    .gen-contact-studio-v5 .gen-contact-editor-group{
      padding:12px!important;
      border-radius:17px!important;
      gap:11px!important;
    }

    .gen-contact-studio-v5 .gen-contact-phone-grid,
    .gen-contact-studio-v5 .gen-contact-link-grid{
      grid-template-columns:1fr!important;
      gap:10px!important;
    }

    .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-control,
    .gen-contact-studio-v5 .gen-contact-prefixed-input{
      height:48px!important;
      min-height:48px!important;
    }

    .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-country,
    .gen-contact-studio-v5 .gen-contact-phone-kit .loop-phone-control input,
    .gen-contact-studio-v5 .gen-contact-prefixed-input b,
    .gen-contact-studio-v5 .gen-contact-prefixed-input input{
      height:46px!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-footer{
      position:sticky!important;
      bottom:0!important;
      z-index:4!important;
      display:grid!important;
      grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr)!important;
      gap:8px!important;
      padding:11px 12px calc(11px + env(safe-area-inset-bottom,0px))!important;
      background:rgba(251,253,253,.98)!important;
      backdrop-filter:blur(14px)!important;
      -webkit-backdrop-filter:blur(14px)!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-footer .gen-v3-secondary-btn,
    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-footer .gen-v3-primary-btn{
      width:100%!important;
      min-width:0!important;
      height:46px!important;
      min-height:46px!important;
      padding:0 10px!important;
      font-size:12px!important;
    }
  }

  @media(max-width:390px){
    .gen-contact-studio-v5 .gen-contact-editor-group-head small{
      display:none!important;
    }

    .gen-contact-studio-v5 .gen-contact-editor-group{
      padding:11px!important;
    }

    .gen-v3-modal-workspace .gen-v3-sheet-workspace:has(.gen-contact-studio-v5) .gen-v3-sheet-head p{
      max-width:100%!important;
      padding-right:36px!important;
    }
  }



  /* =========================================================
     LOOP GENERAL CONTACT LOCATION BALANCE V1
     Wide desktop: Contacto publico + Ubicacion share one clean
     visual row. Compact the map instead of inflating Contacto.
     <=1120px remains natural-height stacked responsive layout.
     ========================================================= */
  @media(min-width:1121px){
    .gen-v3-two-col{
      align-items:stretch!important;
    }

    .gen-v3-two-col > .gen-v3-contact-summary-v4,
    .gen-v3-two-col > .gen-v3-location-summary{
      align-self:stretch!important;
      height:auto!important;
      min-height:0!important;
      max-height:none!important;
      box-sizing:border-box!important;
    }

    .gen-v3-two-col > .gen-v3-contact-summary-v4{
      display:flex!important;
      flex-direction:column!important;
      padding:18px!important;
    }

    .gen-v3-two-col > .gen-v3-contact-summary-v4 .gen-v3-contact-grid-v4{
      flex:1 1 auto!important;
      grid-template-rows:repeat(2,minmax(78px,1fr))!important;
      align-content:stretch!important;
    }

    .gen-v3-two-col > .gen-v3-contact-summary-v4 .gen-v3-contact-tile-v4{
      height:100%!important;
      min-height:78px!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary{
      display:flex!important;
      flex-direction:column!important;
      padding:18px!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-card-head{
      margin-bottom:10px!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-location-top{
      margin-bottom:9px!important;
      gap:10px!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-location-top > div{
      min-width:0!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-location-top strong{
      display:-webkit-box!important;
      -webkit-box-orient:vertical!important;
      -webkit-line-clamp:2!important;
      overflow:hidden!important;
      line-height:1.18!important;
      max-height:2.36em!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-location-top span{
      margin-top:3px!important;
      line-height:1.15!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-map-preview{
      flex:0 0 104px!important;
      height:104px!important;
      min-height:104px!important;
      max-height:104px!important;
      margin-bottom:10px!important;
      border-radius:16px!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-row-actions{
      margin-top:auto!important;
      gap:8px!important;
      align-items:center!important;
    }

    .gen-v3-two-col > .gen-v3-location-summary .gen-v3-row-actions .gen-v3-secondary-btn{
      min-height:38px!important;
      height:38px!important;
      padding:0 12px!important;
      border-radius:12px!important;
      white-space:nowrap!important;
    }
  }


`;
